import { fixedUi } from '@/i18n/fixedUi';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { localizeOptions, optionLabel } from '@/i18n/chartText';
import {
  OUTER_SHAPE_KEYS,
  OUTER_TYPE_KEYS,
  PACKED_KEYS,
  PIECE_UNIT_KEYS,
  SENSITIVE_KEYS,
  SKU_CLASS_KEYS,
  YES_NO_KEYS,
} from '@/i18n/pkgOptionKeys';
import { labeledSiteOptions } from '@/i18n/siteOptions';
import { localizeRegionOptions, localizeCnProvinceOptions } from '@/i18n/catalogDisplayText';
import { useNavigate } from 'react-router-dom';
import RequiredMark from '@/tiktok-platform/components/RequiredMark';
import AttributeField from '@/tiktok-platform/modules/products/components/AttributeField';
import CategoryCascaderSelect from '@/tiktok-platform/components/CategoryCascaderSelect';
import SearchableSelect from '@/tiktok-platform/modules/products/components/SearchableSelect';
import SpecSupplySection from '@/tiktok-platform/modules/products/components/SpecSupplySection';
import TiktokDateTimePicker from '@/tiktok-platform/modules/products/components/TiktokDateTimePicker';
import TiktokOfficialSizeChartPanel, {
  fetchSizeChartClassRequired,
} from '@/tiktok-platform/modules/products/components/TiktokOfficialSizeChartPanel';
import TiktokModelSelectBar from '@/tiktok-platform/modules/products/components/TiktokModelSelectBar';
import TiktokGuideFilePanel from '@/tiktok-platform/modules/products/components/TiktokGuideFilePanel';
import TiktokComplianceRulePanel from '@/tiktok-platform/modules/products/components/TiktokComplianceRulePanel';
import FieldHelpMark from '@/components/FieldHelpMark';
import PublishScrollJumpFab from '@/components/publish/PublishScrollJumpFab';
import {
  buildTiktokModelTryOnSizeOptions,
  officialSizeChartDraftSignature,
} from '@/tiktok-platform/utils/tiktokOfficialSizeChart';
import PackageBatchModal, {
  PACKAGE_TYPE_OPTIONS,
} from '@/tiktok-platform/modules/products/components/PackageBatchModal';
import {
  TIKTOK_OUTER_PACKAGE_SHAPE_OPTIONS,
  TIKTOK_OUTER_PACKAGE_TYPE_OPTIONS,
  coerceOuterPackageFormFields,
} from '@/tiktok-platform/utils/tiktokOuterPackage';
import PublishImageUploadZone from '@/tiktok-platform/modules/products/components/PublishImageUploadZone';
import TitleFieldLoadingShell from '@/tiktok-platform/modules/products/components/TitleFieldLoadingShell';
import TiktokDescriptionRichEditor from '@/tiktok-platform/modules/products/components/TiktokDescriptionRichEditor';
import {
  applyPackageBatchBySize,
  applyPackageBatchToAllSkus,
  firstFilledSkuPackageField,
} from '@/tiktok-platform/utils/tiktokPackageBatch';
import { resolveTiktokProductLevelImages } from '@/tiktok-platform/utils/tiktokProductImage';
import {
  TIKTOK_DIM_UNIT,
  TIKTOK_INDIVIDUALLY_PACKED_OPTIONS,
  TIKTOK_PIECE_UNIT_OPTIONS,
  TIKTOK_SENSITIVE_LIST_OPTIONS,
  TIKTOK_SENSITIVE_YES_NO_OPTIONS,
  TIKTOK_SKU_CLASSIFICATION_OPTIONS,
  TIKTOK_WEIGHT_UNIT,
  sensitiveListFromSlots,
  sensitiveSlotsFromList,
} from '@/tiktok-platform/utils/tiktokSkuPackage';
import SaveAttributeTemplateMetaModal from '@/tiktok-platform/modules/templates/SaveAttributeTemplateMetaModal';
import SaveProductTemplateMetaModal from '@/tiktok-platform/modules/templates/SaveProductTemplateMetaModal';
import { buildTiktokPath, openTiktokPathInNewTab } from '@/tiktok-platform/constants/tiktokRoutes';
import { useTiktokShop } from '@/tiktok-platform/context/TiktokShopContext';
import { TIKTOK_MODE_FULL, TIKTOK_MODE_SEMI, tiktokShopCapabilities } from '@/tiktok-platform/utils/tiktokShopCapabilities';
import { flattenLeafCategories } from '@/tiktok-platform/utils/tiktokCategoryTree';
import {
  calcPublishCompletion,
  parseAttributeTemplate,
  partitionProductAttrsForDisplay,
  attributeDisplayLabel,
  attributeValueDisplayLabel,
  resolveSkcColorAgainstTemplate,
  tiktokSaleAttrAllowsCustomValues,
} from '@/tiktok-platform/utils/tiktokAttributeTemplate';
import { applyShopBrandsToAttrTemplate, shopBrandsToSelectOptions } from '@/tiktok-platform/utils/tiktokShopBrandAttrs';
import { layoutAttributeFields } from '@/tiktok-platform/utils/tiktokAttributeLayout';
import {
  isComplianceProductAttr,
  splitComplianceProductAttrs,
} from '@/tiktok-platform/utils/tiktokComplianceAttrs';
import {
  isEditorAttrRequiredMark,
  isEditorAttrVisible,
  pruneInvisibleProductAttributes,
} from '@/tiktok-platform/utils/tiktokAttrVisibility';
import { parseCustomAttributePermission } from '@/tiktok-platform/utils/tiktokCustomAttribute';
import { filterTiktokSalesWarehouses } from '@/tiktok-platform/utils/tiktokSalesWarehouse';
import {
  applyProductAttributeTemplatePayload,
  countFilledProductAttributes,
  alignAttributesToCategoryTemplate,
  pickFilledProductAttributes,
  remapProductAttributesAcrossCategories,
  remapSizeChartAcrossCategories,
  remapSkcListAcrossCategories,
} from '@/tiktok-platform/utils/tiktokProductAttributeTemplate';
import {
  readProductTemplatePublishHandoff,
  scheduleClearProductTemplatePublishHandoff,
} from '@/tiktok-platform/utils/tiktokProductTemplatePublishHandoff';
import { pickAutoRuleTemplate, pickHighestPriorityTemplate, sortTemplatesByPriority } from '@/tiktok-platform/utils/tiktokTemplateAutoRule';
import { styleNoFromSupplierCode } from '@/tiktok-platform/utils/tiktokStyleNo';
import {
  checkTemplateVersions,
  collectAppliedTemplateRefs,
  conflictFieldKey,
  conflictToastMessage,
  extractAttrTemplatePatch,
  extractSizeChartTemplatePatch,
  isMissingTemplateError,
  mergeProductTemplateIntoForm,
  parseTemplateVersion,
  partitionTemplateVersionConflicts,
  TEMPLATE_VERSION_TYPES,
} from '@/tiktok-platform/utils/tiktokTemplateVersionGuard';
import {
  diffProductAttributeChanges,
  diffSizeChartChanges,
  indexAttributeChangeHints,
} from '@/tiktok-platform/utils/tiktokTemplateChangeDiff';
import TemplateVersionConflictMark from '@/tiktok-platform/modules/products/components/TemplateVersionConflictMark';
import TemplateChangeDiffList from '@/tiktok-platform/modules/products/components/TemplateChangeDiffList';
import {
  customLabelFromKey,
  isCustomSpecKey,
  pruneSizeChartForSubmit,
  skuSizeKey,
} from '@/tiktok-platform/utils/tiktokSizeChart';
import { buildTiktokPublishPayload, samplePublishPayloadCurrency } from '@/tiktok-platform/utils/buildTiktokPublishPayload';
import {
  buildTiktokEditPayload,
  enrichFormWithPlatformSkcIds,
  validateEditPlatformIds,
} from '@/tiktok-platform/utils/buildTiktokEditPayload';
import {
  buildSaveSalePricePayload,
  buildTiktokUpdatePricePayload,
  buildUpdateCostPayload,
  chunkUpdateCostPayload,
  collectSkuPriceChanges,
  interpretPriceUpdateResponse,
  isEmptyPartialEditError,
  validateTiktokPriceChangesAgainstLocks,
} from '@/tiktok-platform/utils/buildTiktokPriceUpdate';
import PriceUpdateConfirmModal from '@/tiktok-platform/modules/products/components/PriceUpdateConfirmModal';
import {
  EMPTY_SKU_PACKAGE_CAPS,
  pickPublishCurrency,
  resolveSkuPackageCapabilities,
} from '@/tiktok-platform/utils/tiktokPublishFillInStandard';
import {
  extractTiktokPublishedSpu,
  extractTiktokPublishFailMessage,
  isTiktokPublishSuccess,
} from '@/tiktok-platform/utils/tiktokPublishResult';
import {
  extractTiktokPublishFieldErrors,
  fieldErrorsToMap,
} from '@/tiktok-platform/utils/mapTiktokPublishFieldErrors';
import { loadShopPublishCurrencies } from '@/tiktok-platform/utils/resolveTiktokPublishCurrency';
import { listEnabledSites, pickSiteListCurrency } from '@/tiktok-platform/utils/tiktokSiteList';
import { validateTiktokPublishForm } from '@/tiktok-platform/utils/validateTiktokPublishForm';
import {
  createTiktokIdempotencyKey,
  parseTiktokStringList,
  resolveTiktokPreSaleRule,
  resolveTiktokPreSaleTypes,
  tiktokMultilineText,
  tiktokStringListChars,
  TIKTOK_DESCRIPTION_MAX_CHARS,
  TIKTOK_EXTERNAL_PRODUCT_ID_MAX,
  TIKTOK_KEY_FEATURES_MAX,
  TIKTOK_KEY_FEATURES_MAX_CHARS,
  TIKTOK_SEARCH_TERMS_MAX,
  TIKTOK_SEARCH_TERMS_MAX_CHARS,
  resolveTiktokTitleLengthBounds,
} from '@/tiktok-platform/utils/tiktokPublishFormLimits';
import { hideTiktokPublishField, isTiktokTtsPublishMode } from '@/tiktok-platform/utils/tiktokPublishUiMode';
import {
  EMPTY_TIKTOK_CATEGORY_RULES,
  normalizeTiktokCategoryRules,
} from '@/tiktok-platform/utils/tiktokCategoryRules';
import { persistTiktokFormImageSlots, sanitizeTiktokFormImageSlots } from '@/tiktok-platform/utils/sanitizeTiktokFormImageSlots';
import { resolveUploadThumbSrc } from '@/tiktok-platform/modules/products/components/publishImageUploadThumbSrc';
import { runTiktokPublishPreflight } from '@/tiktok-platform/utils/tiktokPublishPreflight';
import { runTiktokEditPrerequisitesCheck, runTiktokListingPlatformCheck } from '@/tiktok-platform/utils/tiktokListingCheck';
import {
  generateTiktokPublishAiDescription,
  generateTiktokPublishAiTitle,
  resolveAllSkcMainImageUrls,
  translateTiktokTitleToEnglish,
} from '@/tiktok-platform/utils/tiktokPublishAiTitle';
import {
  assessTiktokPublishPrintInfringement,
  EMPTY_TIKTOK_PRINT_INFRINGEMENT,
  normalizeTiktokPrintInfringement,
  resolveFirstPrintImageUrl,
} from '@/tiktok-platform/utils/tiktokPublishPrintInfringement';
import { buildCreditActionHint } from '@/constants/ipeCredits';
import { confirmIpeCreditAction } from '@/utils/ipeCreditConfirm';
import {
  enrichTiktokFormPrintNames,
  printNamesFromTiktokPrintImages,
} from '@/pages/ipe/workbench/utils/applyListingItemToTiktokPublishForm';
import { tiktokPlatformAPI } from '@/utils/api';
import { showToast } from '@/utils/toast';
import { useUnsavedLeaveGuard } from '@/tiktok-platform/hooks/useUnsavedLeaveGuard';

function FieldErrorText({ message }) {
  if (!message) return null;
  return <div className="tko-field__error" role="alert">{message}</div>;
}

const ORIGIN_REGION_OPTIONS = [
  { value: 'CN', label: '中国' },
  { value: 'US', label: '美国' },
  { value: 'VN', label: '越南' },
  { value: 'BD', label: '孟加拉' },
  { value: 'IN', label: '印度' },
  { value: 'TR', label: '土耳其' },
  { value: 'ID', label: '印尼' },
  { value: 'KH', label: '柬埔寨' },
  { value: 'PK', label: '巴基斯坦' },
  { value: 'GB', label: '英国' },
  { value: 'DE', label: '德国' },
  { value: 'JP', label: '日本' },
  { value: 'KR', label: '韩国' },
  { value: 'OTHER', label: '其他' },
];

/** 预售类型（pre_sale.type）展示名；仅类目规则允许时可选 */
const TIKTOK_PRESALE_TYPE_KEYS = {
  PRE_ORDER: 'preOrder',
  MADE_TO_ORDER: 'madeToOrder',
  CUSTOM: 'custom',
};

/** 承诺发货时效（秒） */
const SHIPMENT_LIMIT_OPTIONS = [
  { value: '86400', labelKey: 'ship1' },
  { value: '172800', labelKey: 'ship2' },
  { value: '259200', labelKey: 'ship3' },
  { value: '432000', labelKey: 'ship5' },
  { value: '604800', labelKey: 'ship7' },
];

/** CN 时 productOrigin.region2Id（Tiktok 官方省份枚举） */
const CN_REGION2_OPTIONS = [
  { value: '43000000000002', label: '北京市' },
  { value: '43000000000003', label: '安徽省' },
  { value: '43000000000004', label: '福建省' },
  { value: '43000000000005', label: '甘肃省' },
  { value: '43000000000006', label: '广东省' },
  { value: '43000000000007', label: '广西壮族自治区' },
  { value: '43000000000008', label: '贵州省' },
  { value: '43000000000009', label: '海南省' },
  { value: '43000000000010', label: '河北省' },
  { value: '43000000000011', label: '河南省' },
  { value: '43000000000012', label: '黑龙江省' },
  { value: '43000000000013', label: '湖北省' },
  { value: '43000000000014', label: '湖南省' },
  { value: '43000000000015', label: '吉林省' },
  { value: '43000000000016', label: '江苏省' },
  { value: '43000000000017', label: '江西省' },
  { value: '43000000000018', label: '辽宁省' },
  { value: '43000000000019', label: '内蒙古自治区' },
  { value: '43000000000020', label: '宁夏回族自治区' },
  { value: '43000000000021', label: '青海省' },
  { value: '43000000000022', label: '山东省' },
  { value: '43000000000023', label: '山西省' },
  { value: '43000000000024', label: '陕西省' },
  { value: '43000000000025', label: '上海市' },
  { value: '43000000000026', label: '四川省' },
  { value: '43000000000027', label: '天津市' },
  { value: '43000000000028', label: '西藏自治区' },
  { value: '43000000000029', label: '新疆维吾尔自治区' },
  { value: '43000000000030', label: '云南省' },
  { value: '43000000000031', label: '浙江省' },
  { value: '43000000000032', label: '重庆市' },
];

/** 半托管常用站点（文档枚举，value=siteId）。编号与 TEMU 不是同一套。 */
const TIKTOK_SEMI_SITES = [
  ['100', '美国站'],
  ['101', '加拿大站'],
  ['102', '英国站'],
  ['103', '澳大利亚站'],
  ['104', '新西兰站'],
  ['105', '德国站'],
  ['106', '法国站'],
  ['107', '意大利站'],
  ['108', '西班牙站'],
  ['109', '日本站'],
  ['112', '墨西哥站'],
  ['118', '巴西站'],
  ['120', '韩国站'],
];

function imageUrlOfSlot(image) {
  if (!image) return '';
  return String(
    image.tiktokUrl
    || image.publicUrl
    || image.previewUrl
    || image.ipeUrl
    || image.url
    || '',
  ).trim();
}

function hasOfficialSizeChartContent(draft) {
  if (!draft || typeof draft !== 'object') return false;
  if (Array.isArray(draft.selectedBusinessIds) && draft.selectedBusinessIds.some((id) => Number(id) > 0)) {
    return true;
  }
  if (String(draft.selectedLibraryId || '').trim()) return true;
  return !isEmptyOfficialSizeChartDraft(draft);
}

function isEmptyOfficialSizeChartDraft(draft) {
  if (!draft || typeof draft !== 'object') return true;
  const records = Array.isArray(draft.records) ? draft.records : [];
  if (records.length === 0) return true;
  // records: [{ values: { [groupId]: 'S', [elementId]: '40' } }]
  const groupId = String(draft.groupList?.[0]?.id ?? '1');
  return !records.some((row) => {
    if (!row || typeof row !== 'object') return false;
    const values = row.values && typeof row.values === 'object' ? row.values : null;
    if (!values) return false;
    return Object.entries(values).some(([key, val]) => {
      if (String(key) === groupId) return false;
      if (key === 'sizeName' || key === 'groupId' || key === 'name') return false;
      return val != null && String(val).trim() !== '';
    });
  });
}

/** 品牌下拉单页条数：品牌库可能有上百条，其余靠远程模糊搜索 */
const BRAND_DROPDOWN_PAGE_SIZE = 100;

const SECTIONS = [
  { id: 'basic', titleKey: 'sectionBasic' },
  { id: 'attrs', titleKey: 'sectionAttrs' },
  { id: 'compliance', titleKey: 'sectionCompliance' },
  { id: 'spec', titleKey: 'sectionSpec' },
  { id: 'size', titleKey: 'sectionSize' },
  { id: 'sales', titleKey: 'salesTitle' },
];

function deriveSelectedSizeIds(skcList = []) {
  const ids = [];
  const seen = new Set();
  skcList.forEach((skc) => {
    (skc.skuRows || []).forEach((sku) => {
      const key = skuSizeKey(sku);
      if (key && !seen.has(key)) {
        seen.add(key);
        ids.push(key);
      }
    });
  });
  return ids;
}

function parseSizeSelectionKey(sizeId) {
  const key = String(sizeId || '');
  if (isCustomSpecKey(key)) {
    const label = customLabelFromKey(key);
    return {
      sizeValueId: '',
      sizeIsCustom: true,
      sizeExtraValue: label,
      sizeLabel: label,
    };
  }
  return {
    sizeValueId: key,
    sizeIsCustom: false,
    sizeExtraValue: '',
    sizeLabel: '',
  };
}

function buildSkuRowsForSizes(existingRows = [], sizeIds = [], sizeAttr, defaults = {}) {
  if (!sizeIds.length) {
    // 编辑回填：已有多行 SKU 时绝不能压成一行（模板过滤尺码后 sizeIds 可能暂时为空）
    if (existingRows.length > 1) {
      return existingRows.map((row) => ({
        ...row,
        sizeAttributeId: sizeAttr?.attributeId || row.sizeAttributeId || '',
      }));
    }
    // 未选尺码时保留一行空 SKU，便于仅按颜色填价库存
    const first = existingRows[0];
    return [first
      ? {
        ...first,
        sizeValueId: '',
        sizeIsCustom: false,
        sizeExtraValue: '',
        sizeLabel: '',
        sizeAttributeId: sizeAttr?.attributeId || first.sizeAttributeId || '',
      }
      : createSkuRow({
        sizeAttributeId: sizeAttr?.attributeId || '',
        price: defaults.price || '',
        stock: defaults.stock || '',
        weight: defaults.weight || '',
        netWeight: defaults.netWeight || '',
        currency: defaults.currency || '',
      })];
  }
  return sizeIds.map((sizeId) => {
    const parsed = parseSizeSelectionKey(sizeId);
    const found = existingRows.find((row) => skuSizeKey(row) === String(sizeId));
    if (found) {
      return {
        ...found,
        sizeAttributeId: sizeAttr?.attributeId || found.sizeAttributeId || '',
        sizeValueId: parsed.sizeValueId,
        sizeIsCustom: parsed.sizeIsCustom,
        sizeExtraValue: parsed.sizeExtraValue,
        sizeLabel: parsed.sizeLabel
          || found.sizeLabel
          || sizeAttr?.values?.find((v) => String(v.id) === parsed.sizeValueId)?.name
          || '',
      };
    }
    const platformName = !parsed.sizeIsCustom
      ? (sizeAttr?.values?.find((v) => String(v.id) === parsed.sizeValueId)?.name || '')
      : parsed.sizeLabel;
    return createSkuRow({
      sizeAttributeId: sizeAttr?.attributeId || '',
      sizeValueId: parsed.sizeValueId,
      sizeIsCustom: parsed.sizeIsCustom,
      sizeExtraValue: parsed.sizeExtraValue,
      sizeLabel: platformName,
      price: defaults.price || '',
      stock: defaults.stock || '',
      weight: defaults.weight || '',
      netWeight: defaults.netWeight || '',
      currency: defaults.currency || '',
    });
  });
}

function createEmptyAccessoryRow() {
  return { vid: '', num: '', unitCode: 1, unitName: '件', thumbUrl: '', label: '' };
}

function createSkuRow(partial = {}) {
  return {
    id: `sku-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    sizeAttributeId: '',
    sizeValueId: '',
    sizeIsCustom: false,
    sizeExtraValue: '',
    sizeLabel: '',
    supplierSku: '',
    price: '',
    specialPrice: '',
    currency: '',
    stock: '',
    weight: '',
    netWeight: '',
    length: '',
    width: '',
    height: '',
    quantity: '1',
    quantityUnit: '1',
    numberOfPieces: '1',
    pieceUnitCode: '1',
    totalContains: '',
    skuClassification: '1',
    individuallyPacked: '',
    packageType: '',
    isSensitive: 0,
    sensitiveList: [],
    suggestedPrice: '',
    suggestedPriceCurrencyType: '',
    accessories: [createEmptyAccessoryRow()],
    mallState: 1,
    skuImage: null,
    skuCode: '',
    siteRrpInfoList: [],
    ...partial,
  };
}

function createSkc(partial = {}) {
  return {
    id: `skc-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    colorAttributeId: '',
    colorValueId: '',
    colorLabel: '',
    colorCode: '',
    colorIsCustom: false,
    colorExtraValue: '',
    supplierCode: '',
    skcName: '',
    mainImage: null,
    showroomSlots: {},
    detailImages: [],
    squareImage: null,
    colorBlockImage: null,
    /** 颜色变体图（ATTRIBUTE_IMAGE / sku_img），在供应信息表维护 */
    skuImage: null,
    skuRows: [createSkuRow()],
    ...partial,
  };
}

function deriveFormCurrency(initial = {}) {
  const top = String(initial.currency || '').trim();
  if (top) return top;
  for (const skc of initial.skcList || []) {
    for (const sku of skc.skuRows || []) {
      const code = String(sku?.currency || '').trim();
      if (code) return code;
    }
  }
  return '';
}

/** 兼容后端旧形态 {attributeValueId,attributeValueName} → 前端 AttributeField 选中值 */
/** 未选仓库时默认第一个发货仓（提交用 warehouseCode，展示只用名称；排除退货仓） */
function withDefaultWarehouse(form, warehouses = []) {
  const next = form && typeof form === 'object' ? { ...form } : {};
  const rows = filterTiktokSalesWarehouses(warehouses);
  if (!rows.length) return next;
  const codes = rows
    .map((item) => String(item?.id || item?.warehouseCode || '').trim())
    .filter(Boolean);
  const current = String(next.warehouseId || '').trim();
  if (current && codes.includes(current)) {
    if (!String(next.warehouseName || '').trim()) {
      const hit = rows.find((item) => String(item.id || item.warehouseCode || '') === current);
      next.warehouseName = hit?.name || hit?.warehouseName || '';
    }
    return next;
  }
  const first = rows[0];
  const id = String(first?.id || first?.warehouseCode || '').trim();
  if (!id) return next;
  next.warehouseId = id;
  next.warehouseName = first?.name || first?.warehouseName || '';
  return next;
}

function normalizeProductAttributes(raw = {}) {
  const out = {};
  Object.entries(raw || {}).forEach(([id, value]) => {
    if (value == null || value === '') return;
    if (Array.isArray(value)) {
      const asComposition = value.some((row) => (
        row && typeof row === 'object' && ('percent' in row || 'attributeExtraValue' in row || 'valueId' in row)
      ));
      if (asComposition) {
        out[id] = value
          .map((row) => {
            if (row != null && (typeof row === 'string' || typeof row === 'number')) {
              return { valueId: String(row), percent: '' };
            }
            if (!row || typeof row !== 'object') return null;
            const valueId = row.valueId ?? row.attributeValueId;
            if (valueId == null || valueId === '') return null;
            return {
              valueId: String(valueId),
              percent: String(row.percent ?? row.attributeExtraValue ?? ''),
            };
          })
          .filter(Boolean);
        return;
      }
      out[id] = value
        .map((row) => {
          if (row && typeof row === 'object') return String(row.valueId ?? row.attributeValueId ?? '');
          return String(row ?? '');
        })
        .filter(Boolean);
      return;
    }
    if (typeof value === 'object') {
      const valueId = value.valueId ?? value.attributeValueId;
      const percent = value.percent ?? value.attributeExtraValue;
      if (valueId != null && valueId !== '') {
        // 有比例（含 0）或显式 percent 字段 → 成分行；否则先保留 valueId，模板加载后再按 mode 转换
        if (percent != null && String(percent).trim() !== '') {
          out[id] = [{ valueId: String(valueId), percent: String(percent) }];
        } else {
          out[id] = String(valueId);
        }
      } else if (value.attributeExtraValue != null && String(value.attributeExtraValue).trim() !== '') {
        out[id] = String(value.attributeExtraValue);
      }
      return;
    }
    out[id] = value;
  });
  return out;
}

function emptyForm(initial = {}) {
  const rawTitle = String(initial.title || initial.productName || '').trim();
  const rawTitleEn = String(initial.titleEn || initial.englishTitle || '').trim();
  const titleLooksEnglishOnly = rawTitle && !/[\u3400-\u9fff]/.test(rawTitle);
  return {
    spuName: initial.spuName || '',
    /** 商品名称 → TTS Create Product title（须英文） */
    title: rawTitle,
    /** 遗留英文字段（TTS 模式 UI 已隐藏） */
    titleEn: rawTitleEn || (titleLooksEnglishOnly ? rawTitle : ''),
    description: initial.description || '',
    categoryId: initial.categoryId || '',
    productTypeId: initial.productTypeId || '',
    catPathIds: Array.isArray(initial.catPathIds) ? initial.catPathIds : [],
    categoryPathLabel: initial.categoryPathLabel || '',
    supplierCode: String(initial.supplierCode || '').trim()
      || (Array.isArray(initial.skcList)
        ? (initial.skcList.map((skc) => String(skc?.supplierCode || '').trim()).find(Boolean) || '')
        : ''),
    brandCode: initial.brandCode || '',
    originCountry: initial.originCountry || '',
    /** Tiktok productOrigin.region1ShortName（二字简码） */
    originRegion: initial.originRegion || initial.originCountry || 'CN',
    /** CN 时省份 region2Id */
    originRegion2Id: initial.originRegion2Id != null ? String(initial.originRegion2Id) : '',
    ipId: initial.ipId || '',
    ipName: initial.ipName || '',
    ipNameCn: initial.ipNameCn || '',
    productAttributes: normalizeProductAttributes(initial.productAttributes || {}),
    skcList: Array.isArray(initial.skcList) ? initial.skcList : [],
    selectedSizeIds: Array.isArray(initial.selectedSizeIds)
      ? initial.selectedSizeIds
      : deriveSelectedSizeIds(initial.skcList),
    ...(() => {
      const imgs = resolveTiktokProductLevelImages(initial);
      return { mainImage: imgs.mainImage, detailImages: imgs.detailImages };
    })(),
    printImages: Array.isArray(initial.printImages) ? initial.printImages : [],
    ipePrintImageNames: Array.isArray(initial.ipePrintImageNames)
      ? initial.ipePrintImageNames
      : printNamesFromTiktokPrintImages(initial.printImages),
    ipePrintInfringement: normalizeTiktokPrintInfringement(
      initial.ipePrintInfringement || EMPTY_TIKTOK_PRINT_INFRINGEMENT,
    ),
    /** Tiktok 素材图（materialImgUrl）；须单独上传 1:1 图，勿用 3:4 主图冒充 */
    materialImage: initial.materialImage || null,
    materialImgUrl: initial.materialImgUrl
      || (initial.materialImage?.tiktokUrl ? String(initial.materialImage.tiktokUrl).trim() : '')
      || '',
    /** Tiktok 主图视频 productCarouseVideoReqList */
    tiktokVideos: Array.isArray(initial.tiktokVideos) ? initial.tiktokVideos : [],
    /** Tiktok 官方尺码表模板 ID */
    tiktokSizeTemplateIds: Array.isArray(initial.tiktokSizeTemplateIds)
      ? initial.tiktokSizeTemplateIds.map(String)
      : [],
    /** Tiktok 商品模特 goodsModelReqs */
    goodsModels: Array.isArray(initial.goodsModels) ? initial.goodsModels : [],
    /** 电子说明书 productGuideFileReqs */
    guideFiles: Array.isArray(initial.guideFiles) ? initial.guideFiles : [],
    /** 产地证明（全托管非 CN 必填） */
    originCertFiles: Array.isArray(initial.originCertFiles) ? initial.originCertFiles : [],
    /** 货品级外包装（平台必填：shape+type+外包装图） */
    ...(() => {
      const coerced = coerceOuterPackageFormFields(initial);
      return {
        packageShape: coerced.packageShape,
        packageTypeOuter: coerced.packageTypeOuter,
        outerPackageImages: Array.isArray(initial.outerPackageImages)
          ? initial.outerPackageImages
          : [],
      };
    })(),
    freightTemplateId: initial.freightTemplateId || '',
    deliveryOptionIds: Array.isArray(initial.deliveryOptionIds) ? initial.deliveryOptionIds.map(String) : [],
    shipmentLimitSecond: initial.shipmentLimitSecond != null
      ? String(initial.shipmentLimitSecond)
      : '172800',
    isSensitive: initial.isSensitive != null ? Number(initial.isSensitive) : 0,
    sizeChart: initial.sizeChart || {},
    officialSizeChart: initial.officialSizeChart && typeof initial.officialSizeChart === 'object'
      ? initial.officialSizeChart
      : null,
    /** 尺码表图片（size_chart.image，use_case=SIZE_CHART_IMAGE） */
    sizeChartImage: initial.sizeChartImage || null,
    sizeSaleAttributeId: initial.sizeSaleAttributeId || '',
    supplyPrice: initial.supplyPrice || '',
    currency: deriveFormCurrency(initial),
    stockQty: initial.stockQty || '',
    // 商品级包裹：优先表单字段；旧草稿只写在 SKU 行时回填
    weightGrams: initial.weightGrams || firstFilledSkuPackageField(initial.skcList, 'weight') || '',
    netWeightGrams: initial.netWeightGrams || '',
    weightUnit: initial.weightUnit || 'g',
    pkgLength: initial.pkgLength || firstFilledSkuPackageField(initial.skcList, 'length') || '10',
    pkgWidth: initial.pkgWidth || firstFilledSkuPackageField(initial.skcList, 'width') || '10',
    pkgHeight: initial.pkgHeight || firstFilledSkuPackageField(initial.skcList, 'height') || '10',
    dimUnit: initial.dimUnit || 'cm',
    warehouseId: initial.warehouseId || '',
    warehouseName: initial.warehouseName || '',
    selectedSites: Array.isArray(initial.selectedSites) ? initial.selectedSites : [],
    mainSite: initial.mainSite || 'tiktok',
    /**
     * 上架方式：'1' = 自动上架（立即），'2' = 预约上架。
     * 与 scheduled_sale 的唯一开关 hopeOnSaleDate 保持一致：旧数据只填了时间没写方式时补 '2'。
     */
    shelfWay: initial.shelfWay === '2' || (!initial.shelfWay && initial.hopeOnSaleDate) ? '2' : '1',
    /** 预约上架（scheduled_sale）：datetime-local，空 = 立即上架 */
    hopeOnSaleDate: initial.hopeOnSaleDate || '',
    /** 搜索词（search_terms）：多行文本，一行一个，≤15 条 / 合计 ≤250 字符 */
    searchTerms: tiktokMultilineText(initial.searchTerms),
    /** 商品卖点（key_product_features）：多行文本，一行一条，≤5 条 / 合计 ≤1500 字符 */
    keyProductFeatures: tiktokMultilineText(initial.keyProductFeatures),
    /** 限购（purchase_order_quantity_limit）：空 = 不限 */
    maxOrderQuantity: initial.maxOrderQuantity != null ? String(initial.maxOrderQuantity) : '',
    cumulativeOrderQuantity: initial.cumulativeOrderQuantity != null
      ? String(initial.cumulativeOrderQuantity)
      : '',
    /** 预售（skus[].pre_sale）：'' = 非预售；仅类目规则允许时展示 */
    preSaleType: initial.preSaleType || '',
    preSaleHandlingDays: initial.preSaleHandlingDays != null ? String(initial.preSaleHandlingDays) : '',
    preSaleReleaseDate: initial.preSaleReleaseDate || '',
    /** Get Category Rules 驱动项（TK-PUB-25） */
    certifications: Array.isArray(initial.certifications) ? initial.certifications : [],
    isCodAllowed: initial.isCodAllowed === true ? true : (initial.isCodAllowed === false ? false : null),
    manufacturerIds: Array.isArray(initial.manufacturerIds) ? initial.manufacturerIds.map(String) : [],
    responsiblePersonIds: Array.isArray(initial.responsiblePersonIds)
      ? initial.responsiblePersonIds.map(String)
      : [],
    pfandAmount: initial.pfandAmount != null ? String(initial.pfandAmount) : '',
    pfandType: initial.pfandType || '',
  };
}

export default function ProductPublishEditor({
  mode = 'create',
  spuName: editSpuName = '',
  draftId: initialDraftId = '',
  recordId: initialRecordId = '',
  productTemplateId: initialProductTemplateId = '',
  fromProductTemplate = false,
  referenceShopId = '',
}) {
  const { t, i18n } = useTranslation('workbench');
  const isEdit = mode === 'edit';
  const navigate = useNavigate();
  const { activeShop } = useTiktokShop();
  const shopId = referenceShopId || activeShop?.id;
  const shopCaps = useMemo(
    () => tiktokShopCapabilities(activeShop?.shopType),
    [activeShop?.shopType],
  );
  const priceMode = shopCaps.canUpdateSalePrice ? 'sale' : (shopCaps.canUpdateCost ? 'cost' : '');
  const isSemiManaged = shopCaps.mode === TIKTOK_MODE_SEMI;
  const isFullManaged = shopCaps.mode === TIKTOK_MODE_FULL;
  const skipSiteWarehouseFreight = Boolean(shopCaps.skipSiteWarehouseFreight);
  const ttsPublishMode = isTiktokTtsPublishMode();
  const hideTitleEn = hideTiktokPublishField('titleEn');
  const hideProductOrigin = hideTiktokPublishField('productOrigin');
  const hideOuterPackage = hideTiktokPublishField('outerPackage');
  const hideGuideFiles = hideTiktokPublishField('guideFiles');
  const hideGoodsModels = hideTiktokPublishField('goodsModels');
  const hideListingSites = hideTiktokPublishField('listingSites');
  const hideShipmentLimit = hideTiktokPublishField('shipmentLimit');
  const hideTemuSkuPackageExtras = hideTiktokPublishField('temuSkuPackageExtras');
  const needsFormBootstrap = Boolean(
    !isEdit && (initialDraftId || initialProductTemplateId || fromProductTemplate),
  );
  const [draftId, setDraftId] = useState(() => (!isEdit && initialDraftId ? String(initialDraftId) : ''));
  const [draftLoading, setDraftLoading] = useState(() => needsFormBootstrap);
  const [draftHydrated, setDraftHydrated] = useState(() => !needsFormBootstrap);
  const [activeSection, setActiveSection] = useState('basic');
  const [form, setForm] = useState(() => emptyForm({}));
  const [meta, setMeta] = useState({ categories: null, sites: null, warehouses: [] });
  const warehouseListRef = useRef(meta.warehouses);
  warehouseListRef.current = meta.warehouses;
  const [metaLoading, setMetaLoading] = useState(false);
  /** 半托管/全托管：发布规范 【currency】；自运营：query-site-list 自营站点币种 */
  const [publishCurrencies, setPublishCurrencies] = useState([]);
  const [publishCurrencyLoading, setPublishCurrencyLoading] = useState(false);
  /** 按末级类目加载的发布规范：门控 SKU 件数 / 包装类型 */
  const [skuPkgCaps, setSkuPkgCaps] = useState(EMPTY_SKU_PACKAGE_CAPS);
  const [skuPkgCapsLoading, setSkuPkgCapsLoading] = useState(false);
  /** 店铺市场约束：region / 标题长度上限（/publish/standard 返回） */
  const [marketMeta, setMarketMeta] = useState({ region: '', titleMaxLength: 0 });
  const titleMaxChars = useMemo(
    () => resolveTiktokTitleLengthBounds(marketMeta.region, marketMeta.titleMaxLength).max,
    [marketMeta.region, marketMeta.titleMaxLength],
  );
  /** 关联属性必填规则：Tiktok 不走 Shein get-associated-attribute-rules */
  const [attrTpl, setAttrTpl] = useState(null);
  const [attrLoading, setAttrLoading] = useState(false);
  const [attrError, setAttrError] = useState('');
  /** 官方尺码表：bg.goods.sizecharts.class.get */
  const [sizeChartRequired, setSizeChartRequired] = useState(false);
  const [sizeChartClass, setSizeChartClass] = useState(null);
  const [sizeChartClassLoading, setSizeChartClassLoading] = useState(false);
  const [sizeChartClassError, setSizeChartClassError] = useState('');
  /** Get Category Rules：认证 / COD / PFAND / EU 合规实体（TK-PUB-25） */
  const [categoryRules, setCategoryRules] = useState(EMPTY_TIKTOK_CATEGORY_RULES);
  const [categoryRulesLoading, setCategoryRulesLoading] = useState(false);
  const [categoryRulesError, setCategoryRulesError] = useState('');
  const officialSizeChartRef = useRef(null);
  const [attrsExpanded, setAttrsExpanded] = useState(false);
  const [attrTemplates, setAttrTemplates] = useState([]);
  const [selectedAttrTemplateId, setSelectedAttrTemplateId] = useState('');
  const [selectedSizeChartTemplateId, setSelectedSizeChartTemplateId] = useState('');
  const [productTemplates, setProductTemplates] = useState([]);
  const [selectedProductTemplateId, setSelectedProductTemplateId] = useState('');
  const [selectedProductTemplateVersion, setSelectedProductTemplateVersion] = useState(null);
  const [selectedAttrTemplateVersion, setSelectedAttrTemplateVersion] = useState(null);
  const [selectedSizeChartTemplateVersion, setSelectedSizeChartTemplateVersion] = useState(null);
  const [versionConflicts, setVersionConflicts] = useState({});
  const [changePreviews, setChangePreviews] = useState({});
  const [reloadingConflictType, setReloadingConflictType] = useState('');
  const [productTemplateBusy, setProductTemplateBusy] = useState(false);
  const [attrTemplateBusy, setAttrTemplateBusy] = useState(false);
  const [attrSaveModalOpen, setAttrSaveModalOpen] = useState(false);
  const [productTplSaveModalOpen, setProductTplSaveModalOpen] = useState(false);
  /** 分类下可自定义属性值的 attributeId 集合 */
  const [customAttrPerm, setCustomAttrPerm] = useState({});
  /** 类目切换后待自动引用的 productTypeId（空=不自动） */
  const pendingAttrAutoForRef = useRef('');
  /** 类目切换后待自动套用产品模板的 productTypeId */
  const pendingProductTplAutoForRef = useRef('');
  /** 切类目时暂存旧类目已填参数，待新 schema 就绪后按相似字段回填 */
  const pendingCategoryPreserveRef = useRef(null);
  const categorySelectSeqRef = useRef(0);
  const attrTemplatesLoadedForRef = useRef('');
  /** 上次属性库自动套用所用的款号（小写）；用于货号变更时重匹配，避免编辑回填误套 */
  const lastAttrAutoStyleKeyRef = useRef(null);
  const attrTplRef = useRef(null);
  const productTemplateHandoffAppliedRef = useRef('');
  const [submitting, setSubmitting] = useState(false);
  const [aiTitleBusy, setAiTitleBusy] = useState(false);
  const [aiDescBusy, setAiDescBusy] = useState(false);
  const [titleTranslateBusy, setTitleTranslateBusy] = useState(false);
  const titleTranslateSeqRef = useRef(0);
  /** 最近一次自动译出的英文；用户改过后与之不同，防抖翻译不再覆盖 */
  const lastAutoTitleEnRef = useRef('');
  const [printInfringementBusy, setPrintInfringementBusy] = useState(false);
  const [submitHint, setSubmitHint] = useState('');
  /** 编辑改价确认：{ submitForm, changes } */
  const [priceConfirm, setPriceConfirm] = useState(null);
  /** 半托运费模板 */
  const [freightOptions, setFreightOptions] = useState([]);
  const [freightLoading, setFreightLoading] = useState(false);
  /** 商品级自定义配送方式（delivery_option_ids）：仅 ID/MX/MY/PH/SG/TH/VN 市场可选 */
  const [deliveryOptionOptions, setDeliveryOptionOptions] = useState([]);
  const [deliveryOptionsSupported, setDeliveryOptionsSupported] = useState(false);
  const [deliveryOptionsLoading, setDeliveryOptionsLoading] = useState(false);
  /** 品牌下拉：来自「品牌库」本地缓存（Get Brands / 自定义品牌），与类目是否已选无关 */
  const [brandSelectOptions, setBrandSelectOptions] = useState([]);
  const [brandOptionsLoading, setBrandOptionsLoading] = useState(false);
  const [brandKeyword, setBrandKeyword] = useState('');
  /** 品牌库为空：给出「去品牌库同步」提示，避免只看到空下拉 */
  const [brandLibraryEmpty, setBrandLibraryEmpty] = useState(false);
  /** 包装清单物品类型（发品：bg.glo.goods.accessories.get） */
  const [accessoryOptions, setAccessoryOptions] = useState([]);
  const [accessoryLoading, setAccessoryLoading] = useState(false);
  const [accessoryLoadError, setAccessoryLoadError] = useState('');
  const [accessoryReloadKey, setAccessoryReloadKey] = useState(0);
  const [originCertBusy, setOriginCertBusy] = useState(false);

  const [savingDraft, setSavingDraft] = useState(false);
  /** 编辑进页即展示加载层，避免先闪空表单 */
  const [loadingSpu, setLoadingSpu] = useState(() => Boolean(mode === 'edit'));
  const editBaselineRef = useRef(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [pkgBatchMode, setPkgBatchMode] = useState(null);

  const formBootLoading = !draftHydrated
    || draftLoading
    || Boolean(isEdit && loadingSpu)
    || metaLoading
    || publishCurrencyLoading;
  const { allowLeave: allowUnsavedLeave, runWithoutUnloadPrompt } = useUnsavedLeaveGuard({
    value: form,
    enabled: Boolean(shopId),
    loading: formBootLoading,
    settleMs: 1600,
  });

  const categoryOptions = useMemo(() => flattenLeafCategories(meta.categories), [meta.categories]);
  const siteOptions = useMemo(() => listEnabledSites(meta.sites), [meta.sites]);
  const siteSelectOptions = useMemo(() => {
    void i18n.language;
    if (isSemiManaged) {
      return labeledSiteOptions('tiktokSite', TIKTOK_SEMI_SITES);
    }
    return siteOptions.map((site) => ({
      value: site.siteAbbr,
      label: site.siteName || site.siteAbbr,
    }));
  }, [isSemiManaged, siteOptions, i18n.language]);

  /** 半托管/全托管走发布规范币种；自运营走站点列表（随已选站点对齐） */
  const platformCurrency = useMemo(() => {
    if (shopCaps.usesPriceInfo) {
      return pickSiteListCurrency(meta.sites, {
        preferred: form.currency,
        selectedSites: form.selectedSites,
      });
    }
    return pickPublishCurrency(publishCurrencies, form.currency);
  }, [form.currency, form.selectedSites, meta.sites, publishCurrencies, shopCaps.usesPriceInfo]);

  const applyPublishCurrency = (code) => {
    const next = String(code || '').trim().toUpperCase();
    if (!next) return;
    setForm((prev) => {
      if (String(prev.currency || '').trim().toUpperCase() === next) return prev;
      return {
        ...prev,
        currency: next,
        skcList: (prev.skcList || []).map((skc) => ({
          ...skc,
          skuRows: (skc.skuRows || []).map((sku) => ({
            ...sku,
            currency: next,
          })),
        })),
      };
    });
  };

  const selectedSitesKey = useMemo(
    () => (Array.isArray(form.selectedSites) ? form.selectedSites.map(String).filter(Boolean).join('|') : ''),
    [form.selectedSites],
  );

  // 币种就绪后回写表单：半托管/全托管按发布规范；自运营按站点列表（已选站点优先）。
  useEffect(() => {
    if (!publishCurrencies.length) return;
    const selectedSites = selectedSitesKey ? selectedSitesKey.split('|') : [];
    const next = shopCaps.usesPriceInfo
      ? pickSiteListCurrency(meta.sites, {
        preferred: form.currency,
        selectedSites,
      })
      : pickPublishCurrency(publishCurrencies, form.currency);
    if (!next) return;
    if (String(form.currency || '').trim().toUpperCase() === String(next).trim().toUpperCase()) return;
    applyPublishCurrency(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 规范/站点币种或回填变化时对齐
  }, [publishCurrencies, form.currency, selectedSitesKey, meta.sites, shopCaps.usesPriceInfo]);

  const warehouseOptions = useMemo(() => {
    // Create Product 库存仓只能选发货仓，排除退货仓（12052532）
    const rows = filterTiktokSalesWarehouses(meta.warehouses);
    const mapped = rows
      .map((item) => {
        const id = String(item.id || item.warehouseCode || '');
        const name = String(item.name || item.warehouseName || '').trim();
        return {
          value: id,
          // 下拉只展示仓库名称；提交仍用 value=warehouseCode
          label: name || id,
          name: name || id,
        };
      })
      .filter((item) => item.value);
    if (form.warehouseId && !mapped.some((item) => item.value === String(form.warehouseId))) {
      // 当前选中若为退货仓/失效仓，不塞回选项，交给下方 effect 改默认发货仓
      const stillSales = filterTiktokSalesWarehouses(meta.warehouses)
        .some((item) => String(item.id || item.warehouseCode || '') === String(form.warehouseId));
      if (stillSales) {
        const fallbackName = String(form.warehouseName || '').trim();
        mapped.unshift({
          value: String(form.warehouseId),
          label: fallbackName || String(form.warehouseId),
          name: fallbackName || String(form.warehouseId),
        });
      }
    }
    return mapped;
  }, [meta.warehouses, form.warehouseId, form.warehouseName]);
  const multiWarehouse = warehouseOptions.length > 1;

  // 库存仓库：默认第一个发货仓。编辑回填可能清空 warehouseId，或误选退货仓时改回发货仓。
  // 全托管/本土跳过站点·仓·运费，不自动写入 warehouseId。
  useEffect(() => {
    if (skipSiteWarehouseFreight) return;
    const rows = filterTiktokSalesWarehouses(meta.warehouses);
    if (!rows.length) return;
    setForm((prev) => {
      const current = String(prev.warehouseId || '').trim();
      const codes = rows
        .map((item) => String(item.id || item.warehouseCode || '').trim())
        .filter(Boolean);
      if (current && codes.includes(current)) {
        // 补全名称（仅有 code 时）
        if (!String(prev.warehouseName || '').trim()) {
          const hit = rows.find((item) => String(item.id || item.warehouseCode || '') === current);
          const name = hit?.name || hit?.warehouseName || '';
          if (name) return { ...prev, warehouseName: name };
        }
        return prev;
      }
      const first = rows[0];
      const id = String(first.id || first.warehouseCode || '').trim();
      if (!id) return prev;
      return {
        ...prev,
        warehouseId: id,
        warehouseName: first.name || first.warehouseName || '',
      };
    });
  }, [meta.warehouses, form.warehouseId, skipSiteWarehouseFreight]);
  const colorAttr = attrTpl?.skcSaleAttrs?.[0] || null;
  const sizeAttr = attrTpl?.skuSaleAttrs?.[0] || null;

  const sizeMeasureAttrsBase = attrTpl?.sizeAttrs;
  const sizeMeasureAttrs = useMemo(
    () => (Array.isArray(sizeMeasureAttrsBase) ? sizeMeasureAttrsBase : []),
    [sizeMeasureAttrsBase],
  );
  const selectedSizeLabels = useMemo(() => {
    const labels = [];
    const seen = new Set();
    const push = (key, label) => {
      const k = String(key || '').trim();
      const name = String(label || '').trim();
      if (!k || seen.has(k) || !name) return;
      seen.add(k);
      labels.push(name);
    };
    (form.skcList || []).forEach((skc) => {
      (skc.skuRows || []).forEach((sku) => {
        const key = skuSizeKey(sku);
        const fromOpt = sizeAttr?.values?.find((v) => String(v.id) === String(sku.sizeValueId));
        push(
          key,
          sku.sizeLabel || sku.sizeExtraValue || fromOpt?.name || attributeValueDisplayLabel(fromOpt),
        );
      });
    });
    (form.selectedSizeIds || []).forEach((id) => {
      const fromOpt = sizeAttr?.values?.find((v) => String(v.id) === String(id));
      push(id, fromOpt?.name || attributeValueDisplayLabel(fromOpt) || id);
    });
    return labels;
  }, [form.skcList, form.selectedSizeIds, sizeAttr]);
  const modelTryOnSizeOptions = useMemo(() => buildTiktokModelTryOnSizeOptions({
    sizeAttr,
    attrTpl,
    selectedSizeIds: form.selectedSizeIds,
    skcList: form.skcList,
  }), [sizeAttr, attrTpl, form.selectedSizeIds, form.skcList]);
  const colorLabel = attributeDisplayLabel(colorAttr) || t('publishSpec.mainSpec');
  const sizeLabel = attributeDisplayLabel(sizeAttr) || t('publishSpec.otherSpec');
  const completion = useMemo(
    () => calcPublishCompletion(form, attrTpl, { hideTitleEn }),
    [form, attrTpl, hideTitleEn],
  );
  /** 搜索词 / 卖点计数（条数与合计字符，与 Create Product 官方口径一致） */
  const searchTermList = useMemo(() => parseTiktokStringList(form.searchTerms), [form.searchTerms]);
  const keyFeatureList = useMemo(
    () => parseTiktokStringList(form.keyProductFeatures),
    [form.keyProductFeatures],
  );
  /** 预售类型：类目规则允许 ∩ 当前市场支持（US 三种，其余仅 PRE_ORDER） */
  const preSaleTypeOptions = useMemo(() => {
    const allowed = Array.isArray(categoryRules.allowedSpecialProductTypes)
      ? categoryRules.allowedSpecialProductTypes
      : [];
    return resolveTiktokPreSaleTypes(marketMeta.region).filter((type) => allowed.includes(type));
  }, [marketMeta.region, categoryRules.allowedSpecialProductTypes]);
  const preSaleRule = useMemo(
    () => resolveTiktokPreSaleRule(marketMeta.region, form.preSaleType),
    [marketMeta.region, form.preSaleType],
  );

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      if (!prev?.[key] && !prev?.form) return prev;
      const next = { ...prev };
      delete next[key];
      delete next.form;
      return next;
    });
  };

  /**
   * 商品级包裹重量/尺寸：同步到全部 SKU 行，避免旧草稿路径仍读 SKU 字段时不一致。
   * @param {{ weightGrams?: string, pkgLength?: string, pkgWidth?: string, pkgHeight?: string }} patch
   */
  const updateProductPackage = (patch) => {
    const nextPatch = patch && typeof patch === 'object' ? patch : {};
    setForm((prev) => {
      const skuPatch = {};
      if ('weightGrams' in nextPatch) skuPatch.weight = nextPatch.weightGrams;
      if ('pkgLength' in nextPatch) skuPatch.length = nextPatch.pkgLength;
      if ('pkgWidth' in nextPatch) skuPatch.width = nextPatch.pkgWidth;
      if ('pkgHeight' in nextPatch) skuPatch.height = nextPatch.pkgHeight;
      const skcList = Object.keys(skuPatch).length
        ? (prev.skcList || []).map((skc) => ({
          ...skc,
          skuRows: (skc.skuRows || []).map((sku) => ({ ...sku, ...skuPatch })),
        }))
        : prev.skcList;
      return { ...prev, ...nextPatch, skcList };
    });
    setFieldErrors((prev) => {
      if (!prev) return prev;
      const keys = ['weightGrams', 'pkgLength', 'pkgWidth', 'pkgHeight', 'form'];
      if (!keys.some((k) => prev[k])) return prev;
      const next = { ...prev };
      keys.forEach((k) => { delete next[k]; });
      return next;
    });
  };

  /**
   * 预约上架：与 shelfWay 同步（'2' = 预约上架，'1' = 自动/立即上架）。
   * 记录列表与导出按 shelfWay 判断展示「自动上架」还是预约时间，故必须同写。
   */
  const updateScheduledSaleAt = (value) => {
    const next = String(value || '');
    setForm((prev) => ({ ...prev, hopeOnSaleDate: next, shelfWay: next ? '2' : '1' }));
    setFieldErrors((prev) => {
      if (!prev?.hopeOnSaleDate && !prev?.form) return prev;
      const nextErrors = { ...prev };
      delete nextErrors.hopeOnSaleDate;
      delete nextErrors.form;
      return nextErrors;
    });
  };

  /**
   * 上架方式切换（同 SHEIN）：'1' = 自动上架（提交即上架），'2' = 预约上架。
   * 切回自动上架必须清掉已选时间，否则仍会带上 scheduled_sale。
   */
  const updateShelfWay = (value) => {
    const next = String(value || '') === '2' ? '2' : '1';
    setForm((prev) => ({
      ...prev,
      shelfWay: next,
      hopeOnSaleDate: next === '2' ? prev.hopeOnSaleDate : '',
    }));
    setFieldErrors((prev) => {
      if (next === '2' || (!prev?.hopeOnSaleDate && !prev?.form)) return prev;
      const nextErrors = { ...prev };
      delete nextErrors.hopeOnSaleDate;
      delete nextErrors.form;
      return nextErrors;
    });
  };

  /** 官方尺码表面板回写：禁止 loading/skip 空 draft 冲掉已回填数据；内容签名相同则跳过 setForm */
  const onOfficialSizeChartDraftChange = useCallback((draft) => {
    setForm((prev) => {
      const nextDraft = draft && typeof draft === 'object' ? draft : null;
      if (prev.officialSizeChart === nextDraft) return prev;
      if (
        hasOfficialSizeChartContent(prev.officialSizeChart)
        && isEmptyOfficialSizeChartDraft(nextDraft)
      ) {
        return prev;
      }
      if (
        officialSizeChartDraftSignature(prev.officialSizeChart)
        === officialSizeChartDraftSignature(nextDraft)
      ) {
        return prev;
      }
      return { ...prev, officialSizeChart: nextDraft };
    });
    setFieldErrors((prev) => {
      if (!prev?.sizeChart && !prev?.form) return prev;
      const next = { ...prev };
      delete next.sizeChart;
      delete next.form;
      return next;
    });
  }, []);

  const setAttrValue = (attributeId, value) => {
    setForm((prev) => {
      const draft = { ...prev.productAttributes, [attributeId]: value };
      const pool = [
        ...(attrTpl?.productAttrs || []),
        ...(attrTpl?.requiredProduct || []),
        ...(attrTpl?.optionalProduct || []),
      ];
      const seen = new Set();
      const allAttrs = pool.filter((a) => {
        const id = String(a?.attributeId || '');
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
      const pruned = pruneInvisibleProductAttributes(draft, allAttrs);
      return { ...prev, productAttributes: pruned };
    });
  };

  /** 自定义属性值写入本地属性模板，供下拉即时可选 */
  const appendCustomAttrValue = (attributeId, opt) => {
    if (!attributeId || !opt?.id) return;
    setAttrTpl((prev) => {
      if (!prev) return prev;
      const patchList = (list) => (list || []).map((attr) => {
        if (String(attr.attributeId) !== String(attributeId)) return attr;
        if ((attr.values || []).some((v) => String(v.id) === String(opt.id))) return attr;
        return {
          ...attr,
          values: [...(attr.values || []), {
            id: String(opt.id),
            name: opt.name,
            nameCn: opt.nameCn || opt.name,
          }],
        };
      });
      return {
        ...prev,
        productAttrs: patchList(prev.productAttrs),
        requiredProduct: patchList(prev.requiredProduct),
        mainAttrs: patchList(prev.mainAttrs),
        otherAttrs: patchList(prev.otherAttrs),
        optionalProduct: patchList(prev.optionalProduct),
        skcSaleAttrs: patchList(prev.skcSaleAttrs),
        skuSaleAttrs: patchList(prev.skuSaleAttrs),
      };
    });
  };

  const updateSkc = (skcId, patch) => {
    setForm((prev) => ({
      ...prev,
      skcList: prev.skcList.map((skc) => (skc.id === skcId ? { ...skc, ...patch } : skc)),
    }));
  };

  const updatePrintImages = (imgs) => {
    const list = Array.isArray(imgs) ? imgs : [];
    // 印花图仅站内展示 / 侵权检测用，不自动回填货号与商家 SKU，也不提交平台
    setForm((prev) => ({
      ...prev,
      printImages: list,
      ipePrintImageNames: printNamesFromTiktokPrintImages(list),
      ipePrintInfringement: { ...EMPTY_TIKTOK_PRINT_INFRINGEMENT },
    }));
  };

  const updateSku = (skcId, skuId, patch) => {
    setForm((prev) => ({
      ...prev,
      skcList: prev.skcList.map((skc) => {
        if (skc.id !== skcId) return skc;
        return {
          ...skc,
          skuRows: skc.skuRows.map((sku) => (sku.id === skuId ? { ...sku, ...patch } : sku)),
        };
      }),
    }));
  };

  // 运费模板（Create Product shipping_template_id 选填；全托/本土跳过，不预加载）
  useEffect(() => {
    if (!shopId || skipSiteWarehouseFreight) {
      setFreightOptions([]);
      setFreightLoading(false);
      return undefined;
    }
    let cancelled = false;
    setFreightLoading(true);
    tiktokPlatformAPI.listShopFreightTemplates(shopId)
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res?.items)
          ? res.items
          : (Array.isArray(res?.freightTemplates) ? res.freightTemplates : []);
        setFreightOptions(rows.map((t) => {
          const id = String(t.shippingTemplateId || t.templateId || t.freightTemplateId || t.id || '').trim();
          const name = String(t.templateName || t.name || id).trim();
          const suffix = t.isAvailable === false ? fixedUi('（不可用）') : (t.isDefault === true ? fixedUi('（默认）') : '');
          return { value: id, label: `${name || id}${suffix}` };
        }).filter((t) => t.value));
        // 已选模板标记不可用时清空，提交时不传 shipping_template_id
        setForm((prev) => {
          const cur = String(prev.freightTemplateId || '').trim();
          if (!cur) return prev;
          const hit = rows.find((t) => {
            const id = String(t.shippingTemplateId || t.templateId || t.freightTemplateId || t.id || '').trim();
            return id === cur;
          });
          if (hit && hit.isAvailable === false) {
            return { ...prev, freightTemplateId: '' };
          }
          return prev;
        });
      })
      .catch(() => {
        if (!cancelled) setFreightOptions([]);
      })
      .finally(() => {
        if (!cancelled) setFreightLoading(false);
      });
    return () => { cancelled = true; };
  }, [shopId, skipSiteWarehouseFreight]);

  // 商品级自定义配送方式：仅白名单市场（ID/MX/MY/PH/SG/TH/VN）可选，留空继承仓库默认。
  // 后端 supported=false 时隐藏该字段（其他市场 delivery_option_ids 不被处理）。
  useEffect(() => {
    if (!shopId || skipSiteWarehouseFreight) {
      setDeliveryOptionOptions([]);
      setDeliveryOptionsSupported(false);
      setDeliveryOptionsLoading(false);
      return undefined;
    }
    const warehouseId = String(form.warehouseId || '').trim();
    if (!warehouseId) {
      setDeliveryOptionOptions([]);
      setDeliveryOptionsLoading(false);
      return undefined;
    }
    let cancelled = false;
    setDeliveryOptionsLoading(true);
    tiktokPlatformAPI.getPublishDeliveryOptions(shopId, warehouseId)
      .then((res) => {
        if (cancelled) return;
        const supported = Boolean(res?.supported);
        setDeliveryOptionsSupported(supported);
        setDeliveryOptionOptions(
          supported && Array.isArray(res?.items)
            ? res.items.map((row) => ({
              value: String(row.id || '').trim(),
              label: String(row.name || row.id || '').trim(),
            })).filter((o) => o.value)
            : [],
        );
      })
      .catch(() => {
        if (cancelled) return;
        setDeliveryOptionOptions([]);
        setDeliveryOptionsSupported(false);
      })
      .finally(() => {
        if (!cancelled) setDeliveryOptionsLoading(false);
      });
    return () => { cancelled = true; };
  }, [shopId, skipSiteWarehouseFreight, form.warehouseId]);

  // 品牌下拉：始终从「品牌库」本地缓存（/shops/{id}/brands）加载，不依赖类目是否已选；
  // 输入关键字时按品牌名/品牌 ID 后端模糊查询（品牌库可能有上百条，避免一次性全量拉取）。
  useEffect(() => {
    if (!shopId) {
      setBrandSelectOptions([]);
      setBrandLibraryEmpty(false);
      setBrandOptionsLoading(false);
      return undefined;
    }
    const keyword = brandKeyword.trim();
    let cancelled = false;
    const timer = setTimeout(() => {
      setBrandOptionsLoading(true);
      tiktokPlatformAPI.getShopBrands(shopId, {
        pageSize: BRAND_DROPDOWN_PAGE_SIZE,
        brandName: keyword || undefined,
      })
        .then((res) => {
          if (cancelled) return;
          const rows = res?.brands ?? res;
          setBrandSelectOptions(shopBrandsToSelectOptions(rows));
          const total = Number(res?.total ?? res?.total_count);
          setBrandLibraryEmpty(
            !keyword && (Number.isFinite(total) ? total <= 0 : !Array.isArray(rows) || rows.length === 0),
          );
        })
        .catch(() => {
          if (cancelled) return;
          setBrandSelectOptions([]);
          setBrandLibraryEmpty(false);
        })
        .finally(() => {
          if (!cancelled) setBrandOptionsLoading(false);
        });
    }, keyword ? 300 : 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [shopId, brandKeyword]);

  // 编辑回填的品牌可能不在首页结果里：补一条占位选项，避免下拉显示为空
  const brandFieldOptions = useMemo(() => {
    const value = String(form.brandCode || '').trim();
    if (!value || brandSelectOptions.some((opt) => String(opt.value) === value)) {
      return brandSelectOptions;
    }
    return [{ value, label: value }, ...brandSelectOptions];
  }, [brandSelectOptions, form.brandCode]);

  // 包装清单选项（发品下拉：accessories.get；选类目后再拉）
  useEffect(() => {
    if (hideTemuSkuPackageExtras || !shopId || !form.categoryId) {
      setAccessoryOptions([]);
      setAccessoryLoadError('');
      setAccessoryLoading(false);
      return undefined;
    }
    let cancelled = false;
    setAccessoryLoading(true);
    setAccessoryLoadError('');
    tiktokPlatformAPI.listPublishAccessories(shopId, { page: 1, pageSize: 100 })
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res?.data)
          ? res.data
          : (Array.isArray(res?.result?.data) ? res.result.data : []);
        const opts = (rows || []).map((row) => ({
          value: String(row.vid),
          label: String(row.value || row.vid),
          unitCode: Number(row.unitCode || 1),
          unitName: String(row.unitName || '件'),
          thumbUrl: String(row.thumbUrl || ''),
        })).filter((o) => o.value);
        setAccessoryOptions(opts);
        if (!opts.length) {
          setAccessoryLoadError(t('publishPkg.accEmpty'));
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setAccessoryOptions([]);
        const msg = String(e?.message || e?.userMessage || t('publishPkg.accLoadFail')).trim();
        setAccessoryLoadError(msg);
        showToast(msg.includes('PA') || msg.includes('7000016') || msg.includes('网关')
          ? t('publishPkg.accLoadFailWrap', { message: msg })
          : t('publishPkg.accLoadFailWrap', { message: msg }), 'warning');
      })
      .finally(() => {
        if (!cancelled) setAccessoryLoading(false);
      });
    return () => { cancelled = true; };
  }, [shopId, form.categoryId, accessoryReloadKey, hideTemuSkuPackageExtras, t]);

  const batchUpdateSku = (patchOrMapper = {}) => {
    if (typeof patchOrMapper === 'function') {
      setForm((prev) => ({
        ...prev,
        skcList: prev.skcList.map((skc) => ({
          ...skc,
          skuRows: (skc.skuRows || []).map((sku) => {
            const patch = patchOrMapper(skc, sku);
            if (!patch || typeof patch !== 'object') return sku;
            return { ...sku, ...patch };
          }),
        })),
      }));
      showToast(t('templateEditor.skuBatchUpdated'), 'success');
      return;
    }
    const next = {};
    [
      'price',
      'suggestedPrice',
      'suggestedPriceCurrencyType',
      'stock',
      'supplierSku',
      'identifierCode',
      'identifierType',
      'weight',
      'netWeight',
      'length',
      'width',
      'height',
      'quantity',
      'quantityUnit',
      'packageType',
    ].forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(patchOrMapper, key)) next[key] = patchOrMapper[key];
    });
    if (!Object.keys(next).length) return;
    setForm((prev) => ({
      ...prev,
      skcList: prev.skcList.map((skc) => ({
        ...skc,
        skuRows: (skc.skuRows || []).map((sku) => ({ ...sku, ...next })),
      })),
    }));
    showToast(t('publishPkg.appliedAll'), 'success');
  };

  const removeSku = (skcId, skuId) => {
    setForm((prev) => {
      const nextSkcList = prev.skcList.map((skc) => {
        if (skc.id !== skcId) return skc;
        const nextRows = skc.skuRows.filter((sku) => sku.id !== skuId);
        return {
          ...skc,
          skuRows: nextRows.length
            ? nextRows
            : [createSkuRow({ sizeAttributeId: sizeAttr?.attributeId || '' })],
        };
      });
      return {
        ...prev,
        skcList: nextSkcList,
        selectedSizeIds: deriveSelectedSizeIds(nextSkcList),
      };
    });
  };

  const skuDefaultsFromForm = (prev) => ({
    price: prev.supplyPrice || '',
    stock: prev.stockQty || '',
    weight: prev.weightGrams || '',
    netWeight: prev.netWeightGrams || '',
    currency: prev.currency || '',
  });

  const onColorsChange = (colorKeys) => {
    const platformIds = new Set((colorAttr?.values || []).map((v) => String(v.id)));
    // 平台未下发颜色枚举（或标记 is_customizable=true）时允许自定义颜色：
    // 提交体只带 value_name（Create Product 会自动生成值 ID），禁止 value_id=0
    const allowCustomColor = tiktokSaleAttrAllowsCustomValues(colorAttr);
    const requested = [...new Set((colorKeys || []).map(String).filter(Boolean))];
    if (!allowCustomColor && requested.some((key) => key.startsWith('custom:'))) {
      showToast(
        t('publishSpec.builtinOnly', {
          label: attributeDisplayLabel(colorAttr) || t('publishSpec.mainSpec'),
        }),
        'warning',
      );
    }
    const safeKeys = requested.filter((key) => {
      if (key.startsWith('custom:')) return allowCustomColor && Boolean(key.slice('custom:'.length));
      return platformIds.has(key);
    });
    setForm((prev) => {
      const defaults = skuDefaultsFromForm(prev);
      const sizeIds = (prev.selectedSizeIds || [])
        .map(String)
        .filter((id) => (sizeAttr?.values || []).some((v) => String(v.id) === id));
      const nextSkcList = safeKeys.map((colorKey) => {
        const isCustom = colorKey.startsWith('custom:');
        let colorExtraValue = '';
        if (isCustom) {
          try {
            colorExtraValue = decodeURIComponent(colorKey.slice('custom:'.length));
          } catch {
            colorExtraValue = colorKey.slice('custom:'.length);
          }
        }
        const existing = prev.skcList.find((skc) => {
          if (isCustom) {
            return Boolean(skc.colorIsCustom || skc.colorExtraValue)
              && String(skc.colorExtraValue || skc.colorLabel || '') === colorExtraValue;
          }
          return String(skc.colorValueId || '') === colorKey && !skc.colorIsCustom;
        });
        const val = !isCustom
          ? colorAttr?.values?.find((v) => String(v.id) === colorKey)
          : null;
        const colorLabel = isCustom
          ? colorExtraValue
          : (val?.nameCn || val?.name || existing?.colorLabel || '');
        if (existing) {
          return {
            ...existing,
            colorAttributeId: colorAttr?.attributeId || existing.colorAttributeId,
            colorValueId: isCustom ? '' : colorKey,
            colorLabel,
            colorCode: existing.colorCode || '',
            colorIsCustom: isCustom,
            colorExtraValue: isCustom ? colorExtraValue : '',
            skuRows: buildSkuRowsForSizes(existing.skuRows, sizeIds, sizeAttr, defaults),
          };
        }
        return createSkc({
          colorAttributeId: colorAttr?.attributeId || '',
          colorValueId: isCustom ? '' : colorKey,
          colorLabel,
          colorCode: '',
          colorIsCustom: isCustom,
          colorExtraValue: isCustom ? colorExtraValue : '',
          skuRows: buildSkuRowsForSizes([], sizeIds, sizeAttr, defaults),
        });
      });
      return { ...prev, skcList: nextSkcList, selectedSizeIds: sizeIds };
    });
  };

  const onSizesChange = (sizeIds) => {
    const allowed = new Set((sizeAttr?.values || []).map((v) => String(v.id)));
    // 平台未下发内置尺码（或标记 is_customizable=true）时允许自定义尺码：
    // 提交体只带 value_name（Create Product 会自动生成值 ID）
    const allowCustomSize = tiktokSaleAttrAllowsCustomValues(sizeAttr);
    const safeIds = [...new Set((sizeIds || []).map(String).filter((id) => {
      if (!id) return false;
      if (isCustomSpecKey(id)) return allowCustomSize && Boolean(id.slice('custom:'.length));
      return allowed.has(id);
    }))];
    setForm((prev) => {
      const defaults = skuDefaultsFromForm(prev);
      return {
        ...prev,
        selectedSizeIds: safeIds,
        sizeSaleAttributeId: sizeAttr?.attributeId || prev.sizeSaleAttributeId || '',
        skcList: prev.skcList.map((skc) => ({
          ...skc,
          skuRows: buildSkuRowsForSizes(skc.skuRows, safeIds, sizeAttr, defaults),
        })),
      };
    });
  };

  const updateSizeCell = (sizeValueId, attrId, value) => {
    setForm((prev) => ({
      ...prev,
      sizeSaleAttributeId: prev.sizeSaleAttributeId || sizeAttr?.attributeId || '',
      sizeChart: {
        ...prev.sizeChart,
        [sizeValueId]: {
          ...(prev.sizeChart?.[sizeValueId] || {}),
          [attrId]: value,
        },
      },
    }));
  };

  const applySizeChart = (nextChart, options = {}) => {
    const replace = options?.replace === true;
    setForm((prev) => ({
      ...prev,
      sizeSaleAttributeId: prev.sizeSaleAttributeId || sizeAttr?.attributeId || '',
      sizeChart: replace
        ? { ...(nextChart || {}) }
        : {
          ...(prev.sizeChart || {}),
          ...(nextChart || {}),
        },
    }));
  };

  useEffect(() => {
    if (isEdit) {
      setDraftLoading(false);
      setDraftHydrated(true);
      return undefined;
    }

    // 草稿优先
    if (initialDraftId) {
      if (!shopId) {
        setDraftLoading(false);
        setDraftHydrated(true);
        return undefined;
      }
      let cancelled = false;
      setDraftLoading(true);
      setDraftHydrated(false);
      tiktokPlatformAPI.getPublishDraft(shopId, initialDraftId)
        .then((res) => {
          if (cancelled) return;
          const formData = res?.form && typeof res.form === 'object' ? res.form : {};
          setDraftId(String(res?.id || initialDraftId));
          setForm(sanitizeTiktokFormImageSlots(emptyForm(formData)));
          setSelectedAttrTemplateId(String(
            formData.selectedAttrTemplateId || res?.selectedAttrTemplateId || '',
          ).trim());
          setSelectedSizeChartTemplateId(String(
            formData.selectedSizeChartTemplateId || res?.selectedSizeChartTemplateId || '',
          ).trim());
          setSelectedProductTemplateId(String(formData.selectedProductTemplateId || '').trim());
          setDraftHydrated(true);
        })
        .catch((e) => {
          if (cancelled) return;
          const msg = e?.status === 404 || /不存在|not found/i.test(String(e?.message || ''))
            ? t('publishFlow.draftGone')
            : (e?.message || t('publishFlow.draftLoadFail'));
          showToast(msg, 'error');
          setDraftId('');
          setDraftHydrated(true);
        })
        .finally(() => {
          if (!cancelled) setDraftLoading(false);
        });
      return () => { cancelled = true; };
    }

    // 列表「发布」：按产品模板 ID 拉取
    if (initialProductTemplateId) {
      if (!shopId) {
        setDraftLoading(false);
        setDraftHydrated(true);
        return undefined;
      }
      let cancelled = false;
      setDraftLoading(true);
      setDraftHydrated(false);
      tiktokPlatformAPI.getProductTemplate(shopId, initialProductTemplateId)
        .then((res) => {
          if (cancelled) return;
          const item = res?.item || res || {};
          const rawPayload = item.payload && typeof item.payload === 'object' ? item.payload : {};
          const formData = rawPayload.form && typeof rawPayload.form === 'object'
            ? rawPayload.form
            : rawPayload;
          setForm(sanitizeTiktokFormImageSlots(emptyForm({
            ...formData,
            categoryId: formData.categoryId || item.categoryId || '',
            productTypeId: formData.productTypeId || item.productTypeId || '',
            categoryPathLabel: formData.categoryPathLabel || item.categoryPathLabel || '',
          })));
          setSelectedAttrTemplateId(String(
            rawPayload.selectedAttrTemplateId || formData.selectedAttrTemplateId || '',
          ).trim());
          setSelectedSizeChartTemplateId(String(
            rawPayload.selectedSizeChartTemplateId || formData.selectedSizeChartTemplateId || '',
          ).trim());
          setSelectedProductTemplateId(String(item.id || initialProductTemplateId));
          setDraftHydrated(true);
          showToast(t('publishFlow.broughtIn', { name: item.name || initialProductTemplateId }), 'success');
        })
        .catch((e) => {
          if (cancelled) return;
          showToast(e?.message || t('templateEditor.templateLoadFail'), 'error');
          setDraftHydrated(true);
        })
        .finally(() => {
          if (!cancelled) setDraftLoading(false);
        });
      return () => { cancelled = true; };
    }

    // 编辑页「发布」：按 token 读取表单快照（IndexedDB / localStorage）
    if (fromProductTemplate) {
      const handoffToken = String(fromProductTemplate || '').trim();
      if (productTemplateHandoffAppliedRef.current === handoffToken) {
        setDraftLoading(false);
        setDraftHydrated(true);
        return undefined;
      }
      let cancelled = false;
      setDraftLoading(true);
      setDraftHydrated(false);
      readProductTemplatePublishHandoff(handoffToken)
        .then((formData) => {
          if (cancelled) return;
          if (formData) {
            productTemplateHandoffAppliedRef.current = handoffToken;
            setForm(sanitizeTiktokFormImageSlots(emptyForm(formData)));
            setSelectedAttrTemplateId(String(formData.selectedAttrTemplateId || '').trim());
            setSelectedSizeChartTemplateId(String(formData.selectedSizeChartTemplateId || '').trim());
            setSelectedProductTemplateId(String(formData.selectedProductTemplateId || '').trim());
            scheduleClearProductTemplatePublishHandoff(handoffToken);
            showToast(t('publishFlow.broughtContent'), 'success');
          } else {
            showToast(t('publishFlow.templateMissing'), 'warning');
          }
          setDraftHydrated(true);
        })
        .catch(() => {
          if (cancelled) return;
          showToast(t('publishFlow.templateMissing'), 'warning');
          setDraftHydrated(true);
        })
        .finally(() => {
          if (!cancelled) setDraftLoading(false);
        });
      return () => { cancelled = true; };
    }

    setDraftLoading(false);
    setDraftHydrated(true);
    return undefined;
  }, [isEdit, initialDraftId, initialProductTemplateId, fromProductTemplate, shopId, t]);

  useEffect(() => {
    if (!shopId) {
      setMeta({ categories: null, sites: null, warehouses: [] });
      setPublishCurrencies([]);
      return undefined;
    }
    let cancelled = false;
    setMetaLoading(true);
    setPublishCurrencyLoading(true);
    Promise.all([
      tiktokPlatformAPI.getShopCategories(shopId),
      tiktokPlatformAPI.getShopSiteList(shopId),
      tiktokPlatformAPI.getShopWarehouses(shopId, { warehouseType: 'SALES_WAREHOUSE' })
        .catch(() => ({ warehouses: [] })),
      tiktokPlatformAPI.getPublishFillInStandard(shopId, {}).catch(() => null),
    ])
      .then(async ([catRes, siteRes, warehouseRes, fillInRes]) => {
        if (cancelled) return;
        const warehouses = filterTiktokSalesWarehouses(
          Array.isArray(warehouseRes?.warehouses) ? warehouseRes.warehouses : [],
        );
        const loaded = await loadShopPublishCurrencies({
          shopCaps,
          siteList: siteRes?.siteList ?? null,
          fillInStandard: fillInRes,
          refreshSiteList: () => tiktokPlatformAPI.getShopSiteList(shopId, { refresh: true }),
        });
        if (cancelled) return;
        setMeta({
          categories: catRes?.categories ?? null,
          sites: loaded.siteList,
          warehouses,
        });
        setPublishCurrencies(loaded.currencies);
        if (loaded.emptyWarning) {
          // eslint-disable-next-line no-console
          console.warn('[tiktok] publish currency empty', loaded.source, loaded.emptyWarning);
          showToast(loaded.emptyWarning, 'warning');
        }
      })
      .catch((e) => {
        if (!cancelled) showToast(e?.message || t('templateEditor.shopOptionsFail'), 'error');
      })
      .finally(() => {
        if (!cancelled) {
          setMetaLoading(false);
          setPublishCurrencyLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [shopId, shopCaps, t]);

  useEffect(() => {
    if (!shopId || !draftHydrated) return undefined;
    const attrId = String(selectedAttrTemplateId || '').trim();
    if (!attrId) return undefined;
    let cancelled = false;
    tiktokPlatformAPI.getProductAttributeTemplate(shopId, attrId)
      .then((res) => {
        if (cancelled) return;
        setSelectedAttrTemplateVersion((prev) => (
          prev != null ? prev : parseTemplateVersion(res?.item?.version)
        ));
      })
      .catch((e) => {
        if (cancelled || !isMissingTemplateError(e)) return;
        setSelectedAttrTemplateId('');
        setSelectedAttrTemplateVersion(null);
      });
    return () => { cancelled = true; };
  }, [shopId, draftHydrated, selectedAttrTemplateId]);

  useEffect(() => {
    if (!shopId || !draftHydrated) return undefined;
    const sizeId = String(selectedSizeChartTemplateId || '').trim();
    if (!sizeId) return undefined;
    let cancelled = false;
    tiktokPlatformAPI.getSizeChartTemplate(shopId, sizeId)
      .then((res) => {
        if (cancelled) return;
        setSelectedSizeChartTemplateVersion((prev) => (
          prev != null ? prev : parseTemplateVersion(res?.item?.version)
        ));
      })
      .catch((e) => {
        if (cancelled || !isMissingTemplateError(e)) return;
        setSelectedSizeChartTemplateId('');
        setSelectedSizeChartTemplateVersion(null);
      });
    return () => { cancelled = true; };
  }, [shopId, draftHydrated, selectedSizeChartTemplateId]);

  /**
   * 产品模板版本兜底。
   *
   * 从产品模板列表「发布商品」（?productTemplateId=）或模板页本地传递进入时只带模板 ID、
   * 不带 version，发品前乐观锁会把 expectedVersion=null 当成版本不一致，报「产品模板已被更新，
   * 请重新加载对应区域」；用户点重新加载又会用模板内容覆盖刚填好的表单。
   * 这里在进入发品页时补一次版本（等价于以打开时的版本为基准），
   * 之后模板真的被改，仍然会正常报冲突、仍然提醒重新加载。
   */
  useEffect(() => {
    if (!shopId || !draftHydrated) return undefined;
    const id = String(selectedProductTemplateId || '').trim();
    if (!id || selectedProductTemplateVersion != null) return undefined;
    let cancelled = false;
    tiktokPlatformAPI.getProductTemplate(shopId, id)
      .then((res) => {
        if (cancelled) return;
        const version = parseTemplateVersion(res?.item?.version);
        if (version != null) setSelectedProductTemplateVersion(version);
      })
      .catch(() => {
        /* 取不到版本时不强制锁定，交给发品前校验与后端兜底 */
      });
    return () => { cancelled = true; };
  }, [shopId, draftHydrated, selectedProductTemplateId, selectedProductTemplateVersion]);

  useEffect(() => {
    if (!shopId || !form.categoryId) {
      setAttrTpl(null);
      setAttrError('');
      return undefined;
    }
    const leafCatId = form.categoryId;
    const typeKey = form.productTypeId || form.categoryId;
    let cancelled = false;
    setAttrLoading(true);
    setAttrError('');
    // TikTok：按叶子类目 catId 拉 Get Attributes（兼容旧入参 product_type_id_list）
    // 品牌名选项来自店铺品牌缓存，与类目属性并行拉取
    Promise.all([
      tiktokPlatformAPI.getAttributeTemplate(shopId, {
        catId: Number(leafCatId),
        product_type_id_list: [Number(typeKey)],
      }, { forceRefresh: true }),
      tiktokPlatformAPI.getShopBrands(shopId).catch(() => null),
    ])
      .then(([res, brandRes]) => {
        if (cancelled) return;
        const parsed = applyShopBrandsToAttrTemplate(
          // normalize 以 catId 写入 product_type_id；解析须用同一 key，避免 productTypeId 不一致导致销售属性被滤空
          parseAttributeTemplate(res, { productTypeId: leafCatId }),
          brandRes?.brands ?? brandRes,
        );
        setAttrTpl(parsed);
        // 材质/成分等控件要 [{valueId,percent}]；编辑回填常是 string / string[]
        // 切类目时按名称把旧类目相似字段回填到新 schema
        const stash = pendingCategoryPreserveRef.current;
        let preserveToast = '';
        if (stash) {
          pendingCategoryPreserveRef.current = null;
          const remappedAttrs = remapProductAttributesAcrossCategories({
            prevAttributes: stash.productAttributes,
            prevAttrTpl: stash.prevAttrTpl,
            nextAttrTpl: parsed,
          });
          const remappedSize = remapSizeChartAcrossCategories({
            prevSizeChart: stash.sizeChart,
            prevSelectedSizeIds: stash.selectedSizeIds,
            prevAttrTpl: stash.prevAttrTpl,
            nextAttrTpl: parsed,
          });
          const remappedSkc = remapSkcListAcrossCategories({
            prevSkcList: stash.skcList,
            prevAttrTpl: stash.prevAttrTpl,
            nextAttrTpl: parsed,
          });
          const selectedSizeIds = remappedSize.selectedSizeIds.length
            ? remappedSize.selectedSizeIds
            : remappedSkc.selectedSizeIds;
          if (
            remappedAttrs.preservedCount > 0
            || remappedSize.preservedCount > 0
            || remappedSkc.preservedCount > 0
          ) {
            pendingAttrAutoForRef.current = '';
            pendingProductTplAutoForRef.current = '';
            const parts = [];
            if (remappedAttrs.preservedCount > 0) parts.push(t('templateEditor.keptSimilar', { count: remappedAttrs.preservedCount }));
            if (remappedSkc.preservedCount > 0) parts.push(t('templateEditor.keptMainSpec', { count: remappedSkc.preservedCount }));
            if (remappedSize.preservedCount > 0) parts.push(t('templateEditor.sizeChartWord'));
            preserveToast = parts.length ? t('templateEditor.keptPrefix', { detail: parts.join('、') }) : '';
          }
          setForm((prev) => ({
            ...prev,
            productAttributes: alignAttributesToCategoryTemplate(remappedAttrs.attributes, parsed),
            sizeChart: remappedSize.sizeChart,
            selectedSizeIds,
            skcList: remappedSkc.skcList,
            // 切类目 remap 后保留媒体与官方尺码表（onCategoryChange 曾临时清空）
            tiktokVideos: Array.isArray(stash.tiktokVideos) ? stash.tiktokVideos : (prev.tiktokVideos || []),
            materialImage: stash.materialImage || prev.materialImage || null,
            materialImgUrl: stash.materialImgUrl || prev.materialImgUrl || '',
            officialSizeChart: stash.officialSizeChart || prev.officialSizeChart || null,
          }));
        } else {
          setForm((prev) => {
            const aligned = alignAttributesToCategoryTemplate(prev.productAttributes, parsed);
            if (aligned === prev.productAttributes) return prev;
            return { ...prev, productAttributes: aligned };
          });
        }
        if (preserveToast) showToast(preserveToast, 'success');
        const total = (parsed.productAttrs?.length || 0)
          + (parsed.skcSaleAttrs?.length || 0)
          + (parsed.skuSaleAttrs?.length || 0)
          + (parsed.sizeAttrs?.length || 0);
        if (total === 0) {
          setAttrError(t('templateEditor.attrEmptyCat'));
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setAttrTpl(null);
        setAttrError(e?.message || t('templateEditor.attrLoadFail'));
      })
      .finally(() => {
        if (!cancelled) setAttrLoading(false);
      });
    return () => { cancelled = true; };
  }, [shopId, form.categoryId, form.productTypeId, t]);

  useEffect(() => {
    if (!shopId || !form.categoryId) {
      setSizeChartRequired(false);
      setSizeChartClass(null);
      setSizeChartClassError('');
      setSizeChartClassLoading(false);
      return undefined;
    }
    let cancelled = false;
    setSizeChartClassLoading(true);
    setSizeChartClassError('');
    fetchSizeChartClassRequired(shopId, form.categoryId)
      .then((res) => {
        if (cancelled) return;
        if (!res?.ok) {
          setSizeChartRequired(false);
          setSizeChartClass(null);
          setSizeChartClassError(res?.error || t('templateEditor.sizeClassFailShort'));
          showToast(res?.error || t('templateEditor.sizeClassFail'), 'error');
          return;
        }
        setSizeChartRequired(Boolean(res.required));
        setSizeChartClass(res.sizeClass || null);
        setSizeChartClassError('');
        if (!res.required) {
          setForm((prev) => (
            (prev.tiktokSizeTemplateIds || []).length
              ? { ...prev, tiktokSizeTemplateIds: [] }
              : prev
          ));
        }
      })
      .finally(() => {
        if (!cancelled) setSizeChartClassLoading(false);
      });
    return () => { cancelled = true; };
  }, [shopId, form.categoryId, t]);

  // Get Category Rules：认证 / COD / PFAND / 制造商 / 欧盟负责人
  useEffect(() => {
    if (!shopId || !form.categoryId) {
      setCategoryRules(EMPTY_TIKTOK_CATEGORY_RULES);
      setCategoryRulesError('');
      setCategoryRulesLoading(false);
      return undefined;
    }
    let cancelled = false;
    setCategoryRulesLoading(true);
    setCategoryRulesError('');
    tiktokPlatformAPI.getPublishCategoryRules(shopId, form.categoryId)
      .then((res) => {
        if (cancelled) return;
        setCategoryRules(normalizeTiktokCategoryRules(res));
      })
      .catch((e) => {
        if (cancelled) return;
        setCategoryRules(EMPTY_TIKTOK_CATEGORY_RULES);
        setCategoryRulesError(e?.message || t('templateEditor.rulesFail'));
      })
      .finally(() => {
        if (!cancelled) setCategoryRulesLoading(false);
      });
    return () => { cancelled = true; };
  }, [shopId, form.categoryId, t]);

  useEffect(() => {
    if (!shopId || !form.productTypeId) {
      setAttrTemplates([]);
      attrTemplatesLoadedForRef.current = '';
      // 切类目时由 onCategoryChange 清空选中；此处勿清，避免草稿/模板回填被冲掉
      return undefined;
    }
    const productTypeId = String(form.productTypeId);
    let cancelled = false;
    attrTemplatesLoadedForRef.current = '';
    tiktokPlatformAPI.listProductAttributeTemplates(shopId, {
      productTypeId,
      page: 1,
      pageSize: 100,
    })
      .then((res) => {
        if (cancelled) return;
        setAttrTemplates(Array.isArray(res?.items) ? res.items : []);
        attrTemplatesLoadedForRef.current = productTypeId;
      })
      .catch(() => {
        if (!cancelled) {
          setAttrTemplates([]);
          attrTemplatesLoadedForRef.current = productTypeId;
        }
      });
    return () => { cancelled = true; };
  }, [shopId, form.productTypeId]);

  useEffect(() => {
    if (!shopId || !form.categoryId) {
      setCustomAttrPerm({});
      return undefined;
    }
    let cancelled = false;
    tiktokPlatformAPI.getCustomAttributePermission(shopId, {
      category_id_list: [Number(form.categoryId)],
    })
      .then((res) => {
        if (cancelled) return;
        setCustomAttrPerm(parseCustomAttributePermission(res));
      })
      .catch(() => {
        if (!cancelled) setCustomAttrPerm({});
      });
    return () => { cancelled = true; };
  }, [shopId, form.categoryId]);

  // 按末级类目拉取 Tiktok 发品约束（/publish/standard；非 Shein fill-in-standard）
  useEffect(() => {
    if (!shopId || !form.categoryId) {
      setSkuPkgCaps(EMPTY_SKU_PACKAGE_CAPS);
      setSkuPkgCapsLoading(false);
      return undefined;
    }
    const categoryId = String(form.categoryId);
    let cancelled = false;
    setSkuPkgCapsLoading(true);
    tiktokPlatformAPI.getPublishFillInStandard(shopId, {
      catId: Number(categoryId),
      category_id: Number(categoryId),
    })
      .then((res) => {
        if (cancelled) return;
        const nextCaps = resolveSkuPackageCapabilities(res);
        setSkuPkgCaps(nextCaps);
        setMarketMeta({
          region: String(res?.region || '').toUpperCase(),
          titleMaxLength: Number(res?.titleMaxLength) > 0 ? Number(res.titleMaxLength) : 0,
        });
        // 能力关闭时清空残留，避免脏数据进预检 / 误展示
        if (!nextCaps.quantityInfo.show || !nextCaps.packageType.show) {
          setForm((prev) => {
            let changed = false;
            const skcList = (prev.skcList || []).map((skc) => {
              let skcChanged = false;
              const skuRows = (skc.skuRows || []).map((sku) => {
                const patch = {};
                if (!nextCaps.quantityInfo.show) {
                  if (sku.quantity != null && String(sku.quantity).trim() !== '') {
                    patch.quantity = '';
                  }
                  if (sku.quantityUnit != null && String(sku.quantityUnit) !== '1') {
                    patch.quantityUnit = '1';
                  }
                }
                if (!nextCaps.packageType.show) {
                  if (sku.packageType != null && String(sku.packageType) !== '' && String(sku.packageType) !== '0') {
                    patch.packageType = '';
                  }
                }
                if (!Object.keys(patch).length) return sku;
                skcChanged = true;
                changed = true;
                return { ...sku, ...patch };
              });
              return skcChanged ? { ...skc, skuRows } : skc;
            });
            return changed ? { ...prev, skcList } : prev;
          });
        }
      })
      .catch(() => {
        if (cancelled) return;
        setSkuPkgCaps(EMPTY_SKU_PACKAGE_CAPS);
      })
      .finally(() => {
        if (!cancelled) setSkuPkgCapsLoading(false);
      });
    return () => { cancelled = true; };
  }, [shopId, form.categoryId]);

  useEffect(() => {
    if (!isEdit) {
      setLoadingSpu(false);
      return undefined;
    }
    if (!shopId || (!editSpuName && !initialRecordId && !initialDraftId)) {
      setLoadingSpu(false);
      return undefined;
    }
    let cancelled = false;
    setLoadingSpu(true);
    // 审核中商品 searchProduct 往往只有类目；优先本地草稿 form 整表回填
    tiktokPlatformAPI.getProductForEdit(shopId, {
      spuName: editSpuName,
      recordId: initialRecordId,
      draftId: initialDraftId,
    })
      .then((res) => {
        if (cancelled) return;
        const info = res || {};
        if (info.draftId != null && info.draftId !== '') {
          setDraftId(String(info.draftId));
        }
        const skcItems = Array.isArray(info.skcItems)
          ? info.skcItems
          : (Array.isArray(info.form?.skcItems) ? info.form.skcItems : []);
        if (info.form && typeof info.form === 'object') {
          const merged = enrichFormWithPlatformSkcIds({
            ...info.form,
            spuName: info.spuName || info.form.spuName || editSpuName,
            title: info.form.title || info.title || '',
            titleEn: info.form.titleEn || info.titleEn || info.englishTitle || '',
            supplierCode: info.form.supplierCode || info.supplierCode || '',
            brandCode: info.form.brandCode || info.brandCode || '',
            categoryId: String(info.form.categoryId || info.categoryId || ''),
            productTypeId: String(info.form.productTypeId || info.productTypeId || ''),
            categoryPathLabel: info.form.categoryPathLabel || info.categoryPathLabel || '',
            description: info.form.description || info.description || '',
            originCountry: info.form.originCountry || info.originCountry || '',
          }, skcItems);
          const nextForm = sanitizeTiktokFormImageSlots(
            withDefaultWarehouse(emptyForm(merged), warehouseListRef.current),
          );
          editBaselineRef.current = JSON.parse(JSON.stringify(nextForm));
          setForm(nextForm);
          if (info.platformLoadWarning) {
            showToast(String(info.platformLoadWarning), 'warning');
          }
          return;
        }
        const flatForm = sanitizeTiktokFormImageSlots(withDefaultWarehouse(enrichFormWithPlatformSkcIds({
          ...emptyForm({}),
          spuName: info.spuName || editSpuName || '',
          title: info.title || '',
          titleEn: info.titleEn || info.englishTitle || '',
          description: info.description || '',
          supplierCode: info.supplierCode || '',
          brandCode: info.brandCode || '',
          originCountry: info.originCountry || '',
          categoryId: info.categoryId != null ? String(info.categoryId) : '',
          productTypeId: info.productTypeId != null ? String(info.productTypeId) : '',
          categoryPathLabel: info.categoryPathLabel || '',
        }, skcItems), warehouseListRef.current));
        editBaselineRef.current = JSON.parse(JSON.stringify(flatForm));
        setForm(flatForm);
      })
      .catch((e) => {
        if (!cancelled) showToast(e?.message || t('publishFlow.detailLoadFail'), 'error');
      })
      .finally(() => {
        if (!cancelled) setLoadingSpu(false);
      });
    return () => { cancelled = true; };
  }, [isEdit, shopId, editSpuName, initialRecordId, initialDraftId, t]);

  useEffect(() => {
    if (!colorAttr && !sizeAttr) return;
    setForm((prev) => {
      const colorValues = colorAttr?.values || [];
      const sizeValues = sizeAttr?.values || [];
      const colorIds = new Set(colorValues.map((v) => String(v.id)));
      const sizeIds = new Set(sizeValues.map((v) => String(v.id)));
      const defaults = {
        price: prev.supplyPrice || '',
        stock: prev.stockQty || '',
        weight: prev.weightGrams || '',
        netWeight: prev.netWeightGrams || '',
        currency: prev.currency || '',
      };
      // 尺码表纵轴以 SKU 规格为准，禁止合并模板残留的 selectedSizeIds（否则会出现两个 mid-platform）
      const rowSizeIds = deriveSelectedSizeIds(prev.skcList || []).map(String);
      const prevSizeIds = (prev.selectedSizeIds || []).map(String);
      // 自定义尺码（custom:xxx）只存在于 SKU 行里，必须原样带回选择器，
      // 否则「平台无内置尺码 + 手输自定义尺码」时会从主规格区域消失
      const nextSizeIds = rowSizeIds.length
        ? rowSizeIds
        : prevSizeIds.filter((id) => isCustomSpecKey(id) || !sizeValues.length || sizeIds.has(id));
      const nextSkcList = (prev.skcList || []).map((skc) => {
        const knownColor = !skc.colorValueId || colorIds.has(String(skc.colorValueId));
        const asCustom = Boolean(
          skc.colorIsCustom
          || skc.colorExtraValue
          || (colorValues.length > 0 && skc.colorValueId && !knownColor),
        );
        const colorLabel = asCustom
          ? (skc.colorExtraValue || skc.colorLabel || '')
          : (colorAttr?.values?.find((v) => String(v.id) === String(skc.colorValueId))?.name
            || skc.colorLabel);
        const existingRows = skc.skuRows || [];
        // 编辑多 SKU：保留全部行；仅在新建且用户主动勾选尺码时按 sizeIds 重建
        const keepAllRows = isEdit || existingRows.length > 1 || !nextSizeIds.length;
        return {
          ...skc,
          colorAttributeId: skc.colorAttributeId || colorAttr?.attributeId || '',
          colorIsCustom: asCustom,
          colorExtraValue: asCustom
            ? (skc.colorExtraValue || skc.colorLabel || '')
            : (skc.colorExtraValue || ''),
          colorLabel,
          skuRows: keepAllRows
            ? existingRows.map((row) => ({
              ...row,
              sizeAttributeId: row.sizeAttributeId || sizeAttr?.attributeId || '',
            }))
            : buildSkuRowsForSizes(existingRows, nextSizeIds, sizeAttr, defaults),
        };
      }).filter((skc) => (
        skc.colorValueId
        || String(skc.colorExtraValue || '').trim()
        || String(skc.colorLabel || '').trim()
        || (skc.skuRows || []).length > 0
      ));
      const nextState = {
        ...prev,
        sizeSaleAttributeId: prev.sizeSaleAttributeId || sizeAttr?.attributeId || '',
        selectedSizeIds: nextSizeIds.length ? nextSizeIds : prev.selectedSizeIds,
        skcList: nextSkcList,
      };
      const sameSizes = JSON.stringify((prev.selectedSizeIds || []).map(String))
        === JSON.stringify((nextState.selectedSizeIds || []).map(String));
      const sameSaleAttr = String(prev.sizeSaleAttributeId || '') === String(nextState.sizeSaleAttributeId || '');
      const sameSkc = prev.skcList === nextSkcList || (
        (prev.skcList || []).length === nextSkcList.length
        && (prev.skcList || []).every((skc, i) => {
          const n = nextSkcList[i];
          return skc === n
            || (
              String(skc.colorAttributeId || '') === String(n.colorAttributeId || '')
              && Boolean(skc.colorIsCustom) === Boolean(n.colorIsCustom)
              && String(skc.colorExtraValue || '') === String(n.colorExtraValue || '')
              && String(skc.colorLabel || '') === String(n.colorLabel || '')
              && (skc.skuRows || []).length === (n.skuRows || []).length
              && (skc.skuRows || []).every((row, j) => (
                String(row.sizeAttributeId || '') === String(n.skuRows[j]?.sizeAttributeId || '')
              ))
            );
        })
      );
      if (sameSizes && sameSaleAttr && sameSkc) return prev;
      return nextState;
    });
  }, [colorAttr, sizeAttr, isEdit]);

  useEffect(() => {
    if (!form.categoryId || !categoryOptions.length) return;
    const selected = categoryOptions.find((item) => String(item.categoryId) === String(form.categoryId));
    if (!selected) return;
    const needType = !form.productTypeId && selected.productTypeId;
    const needPath = !form.categoryPathLabel && selected.label;
    if (!needType && !needPath) return;
    setForm((prev) => {
      if (String(prev.categoryId) !== String(selected.categoryId)) return prev;
      return {
        ...prev,
        productTypeId: prev.productTypeId || (selected.productTypeId ? String(selected.productTypeId) : ''),
        categoryPathLabel: prev.categoryPathLabel || selected.label || '',
      };
    });
  }, [form.categoryId, form.productTypeId, form.categoryPathLabel, categoryOptions]);

  const applyProductTemplateById = async (templateId, {
    categoryId = '',
    productTypeId = '',
    categoryPathLabel = '',
    silent = false,
  } = {}) => {
    const id = String(templateId || '').trim();
    if (!shopId || !id) return false;
    const res = await tiktokPlatformAPI.getProductTemplate(shopId, id);
    const item = res?.item || res || {};
    const rawPayload = item.payload && typeof item.payload === 'object' ? item.payload : {};
    const formData = rawPayload.form && typeof rawPayload.form === 'object'
      ? rawPayload.form
      : rawPayload;
    setForm(sanitizeTiktokFormImageSlots(emptyForm({
      ...formData,
      categoryId: categoryId || formData.categoryId || item.categoryId || '',
      productTypeId: productTypeId || formData.productTypeId || item.productTypeId || '',
      categoryPathLabel: categoryPathLabel || formData.categoryPathLabel || item.categoryPathLabel || '',
    })));
    setSelectedAttrTemplateId(String(
      rawPayload.selectedAttrTemplateId || formData.selectedAttrTemplateId || '',
    ).trim());
    setSelectedSizeChartTemplateId(String(
      rawPayload.selectedSizeChartTemplateId || formData.selectedSizeChartTemplateId || '',
    ).trim());
    setSelectedProductTemplateId(String(item.id || id));
    setSelectedProductTemplateVersion(parseTemplateVersion(item.version));
    setSelectedAttrTemplateVersion(null);
    setSelectedSizeChartTemplateVersion(null);
    if (!silent) {
      showToast(t('publishFlow.appliedTpl', { name: item.name || id }), 'success');
    }
    return true;
  };

  // 选类目后加载该类目产品模板；用户主动切换类目时自动套用优先级最高的一项
  useEffect(() => {
    if (isEdit || !shopId || !form.productTypeId) {
      if (!form.productTypeId) {
        setProductTemplates([]);
      }
      return undefined;
    }
    const productTypeId = String(form.productTypeId);
    const categoryId = String(form.categoryId || '');
    const categoryPathLabel = String(form.categoryPathLabel || '');
    const shouldAuto = String(pendingProductTplAutoForRef.current || '') === productTypeId;
    pendingProductTplAutoForRef.current = '';
    const seq = categorySelectSeqRef.current + 1;
    categorySelectSeqRef.current = seq;
    setProductTemplateBusy(true);
    let cancelled = false;
    tiktokPlatformAPI.listProductTemplates(shopId, {
      productTypeId,
      page: 1,
      pageSize: 100,
    })
      .then(async (res) => {
        if (cancelled || categorySelectSeqRef.current !== seq) return;
        const sorted = sortTemplatesByPriority(Array.isArray(res?.items) ? res.items : []);
        setProductTemplates(sorted);
        if (!shouldAuto) return;
        const picked = pickHighestPriorityTemplate(sorted);
        if (!picked?.id) return;
        // 产品模板已带类目属性/尺码库选中项，跳过属性库自动套用，避免覆盖
        pendingAttrAutoForRef.current = '';
        try {
          await applyProductTemplateById(picked.id, {
            categoryId,
            productTypeId,
            categoryPathLabel,
          });
        } catch (e) {
          showToast(e?.message || t('publishFlow.autoApplyFail'), 'error');
        }
      })
      .catch(() => {
        if (cancelled || categorySelectSeqRef.current !== seq) return;
        setProductTemplates([]);
      })
      .finally(() => {
        if (!cancelled && categorySelectSeqRef.current === seq) {
          setProductTemplateBusy(false);
        }
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 仅随类目类型变化加载/自动套用
  }, [isEdit, shopId, form.productTypeId, form.categoryId]);

  const onCategoryChange = (categoryId, meta) => {
    const nextCategoryId = categoryId == null || categoryId === '' ? '' : String(categoryId);
    // 再点一次当前类目不算切换：否则会清空模板带入的属性/规格，且自动套用不会重跑
    if (String(form.categoryId || '') === nextCategoryId) {
      return;
    }
    const selected = categoryOptions.find((item) => String(item.categoryId) === nextCategoryId);
    const nextProductTypeId = selected?.productTypeId
      ? String(selected.productTypeId)
      : (meta?.productTypeId ? String(meta.productTypeId) : nextCategoryId);
    const nextCategoryPathLabel = selected?.label || meta?.label || '';
    const nextCatPathIds = Array.isArray(selected?.pathIds)
      ? selected.pathIds.map(String)
      : (Array.isArray(meta?.pathIds) ? meta.pathIds.map(String) : []);
    pendingCategoryPreserveRef.current = {
      productAttributes: pickFilledProductAttributes(form.productAttributes),
      sizeChart: form.sizeChart && typeof form.sizeChart === 'object' ? form.sizeChart : {},
      officialSizeChart: form.officialSizeChart && typeof form.officialSizeChart === 'object'
        ? form.officialSizeChart
        : null,
      selectedSizeIds: Array.isArray(form.selectedSizeIds) ? [...form.selectedSizeIds] : [],
      skcList: Array.isArray(form.skcList) ? form.skcList : [],
      tiktokVideos: Array.isArray(form.tiktokVideos) ? form.tiktokVideos : [],
      materialImage: form.materialImage || null,
      materialImgUrl: form.materialImgUrl || '',
      prevAttrTpl: attrTplRef.current,
    };
    pendingAttrAutoForRef.current = nextProductTypeId;
    pendingProductTplAutoForRef.current = nextProductTypeId;
    setSelectedAttrTemplateId('');
    setSelectedSizeChartTemplateId('');
    setSelectedProductTemplateId('');
    setSelectedProductTemplateVersion(null);
    setSelectedAttrTemplateVersion(null);
    setSelectedSizeChartTemplateVersion(null);
    setVersionConflicts({});
    setProductTemplates([]);
    setForm((prev) => ({
      ...prev,
      categoryId: nextCategoryId,
      productTypeId: nextProductTypeId,
      categoryPathLabel: nextCategoryPathLabel,
      catPathIds: nextCatPathIds,
      productAttributes: {},
      sizeChart: {},
      // 尺码表图片随类目变更失效（各平台的尺码表规则不同），需重新上传
      sizeChartImage: null,
      skcList: [],
      selectedSizeIds: [],
      tiktokSizeTemplateIds: [],
      goodsModels: [],
      // 主图视频 / 素材图 / 官方尺码表由 stash 在属性 remap 后写回，此处勿永久丢弃
    }));
    setAttrsExpanded(false);
  };

  const onProductTemplateSelect = async (templateId) => {
    const id = String(templateId || '').trim();
    setSelectedProductTemplateId(id);
    if (!id) return;
    if (!shopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    setProductTemplateBusy(true);
    try {
      pendingAttrAutoForRef.current = '';
      await applyProductTemplateById(id, {
        categoryId: form.categoryId,
        productTypeId: form.productTypeId,
        categoryPathLabel: form.categoryPathLabel,
      });
    } catch (e) {
      showToast(e?.message || t('publishFlow.applyFail'), 'error');
    } finally {
      setProductTemplateBusy(false);
    }
  };

  const onSitesChange = (abbr) => {
    const next = abbr == null || abbr === '' ? '' : String(abbr);
    const selected = next ? [next] : [];
    const first = isSemiManaged
      ? null
      : siteOptions.find((s) => selected.includes(s.siteAbbr));
    setForm((prev) => ({
      ...prev,
      selectedSites: selected,
      mainSite: first?.mainSiteCode || prev.mainSite || 'tiktok',
    }));
  };

  const onMaterialImagesChange = (next) => {
    // PublishImageUploadZone 单图模式传对象/null；多图模式传数组
    const img = Array.isArray(next) ? (next[0] || null) : (next || null);
    setForm((prev) => ({
      ...prev,
      materialImage: img,
      materialImgUrl: String(img?.tiktokUrl || '').trim(),
    }));
    setFieldErrors((prev) => {
      if (!prev?.materialImgUrl) return prev;
      const nextErrors = { ...prev };
      delete nextErrors.materialImgUrl;
      return nextErrors;
    });
  };

  const onVideosChange = (videos) => {
    setForm((prev) => ({
      ...prev,
      tiktokVideos: Array.isArray(videos) ? videos : [],
    }));
    setFieldErrors((prev) => {
      if (!prev?.tiktokVideos) return prev;
      const next = { ...prev };
      delete next.tiktokVideos;
      return next;
    });
  };

  const onWarehouseChange = (warehouseId) => {
    const selected = warehouseOptions.find((item) => String(item.value) === String(warehouseId));
    setForm((prev) => ({
      ...prev,
      warehouseId: selected?.value || '',
      warehouseName: selected?.name || '',
      // 换仓后原配送方式 id 可能已不属于该仓，清空避免下发无效 id
      deliveryOptionIds: [],
    }));
  };

  const scrollToSection = (id) => {
    setActiveSection(id);
    document.getElementById(`tko-pub-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const scrollToFieldError = (field, section) => {
    if (section) scrollToSection(section);
    const el = document.querySelector(`[data-field-error="${field}"]`);
    if (el) {
      window.setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 80);
    }
  };

  const applyFieldErrors = (errors, fallbackMessage = '') => {
    const list = Array.isArray(errors) ? errors : [];
    const map = fieldErrorsToMap(list);
    setFieldErrors(map);
    const first = list[0];
    if (first?.field) {
      scrollToFieldError(first.field, first.section);
    }
    const tip = first?.message || fallbackMessage;
    if (tip) showToast(tip, 'error', Math.min(12000, Math.max(4000, String(tip).length * 50)));
  };

  const buildPublishFormSnapshot = (formSource = form) => {
    const sizeValueLabels = {};
    (sizeAttr?.values || []).forEach((v) => {
      if (v?.id != null && v?.name) sizeValueLabels[String(v.id)] = String(v.name);
    });
    const skcList = (formSource.skcList || []).map((skc) => ({
      ...skc,
      skuRows: (skc.skuRows || []).map((sku) => ({
        ...sku,
        sizeLabel: sizeAttr?.values?.find((v) => String(v.id) === String(sku.sizeValueId))?.name
          || sku.sizeLabel
          || '',
      })),
    }));
    const selectedSizeIds = (formSource.selectedSizeIds?.length
      ? formSource.selectedSizeIds
      : deriveSelectedSizeIds(skcList)
    ).map((id) => String(id));
    return {
      ...formSource,
      skcList,
      selectedSizeIds,
      sizeValueLabels,
      selectedAttrTemplateId: String(selectedAttrTemplateId || '').trim(),
      selectedSizeChartTemplateId: String(selectedSizeChartTemplateId || '').trim(),
      selectedProductTemplateId: String(selectedProductTemplateId || '').trim(),
      ...(skipSiteWarehouseFreight
        ? {
          selectedSites: [],
          warehouseId: '',
          warehouseName: '',
          freightTemplateId: '',
          deliveryOptionIds: [],
          shipmentLimitSecond: '',
          tiktokSiteIds: [],
          tiktokSiteId: '',
          tiktokFreightTemplateId: '',
        }
        : {}),
    };
  };

  const onSaveDraft = async () => {
    if (!shopId) {
      showToast(t('publishFlow.pickShopDraft'), 'warning');
      return;
    }
    setSavingDraft(true);
    try {
      // 印花图仅站内展示 / 侵权检测，不自动拼接到货号与商家 SKU
      // TTS：不自动裁色块图/素材图（Create Product 无对应字段）
      let enriched = await enrichTiktokFormPrintNames(form);
      if (enriched !== form) setForm(enriched);
      const payload = {
        form: persistTiktokFormImageSlots(buildPublishFormSnapshot(enriched)),
      };
      const res = draftId
        ? await tiktokPlatformAPI.updatePublishDraft(shopId, draftId, payload)
        : await tiktokPlatformAPI.createPublishDraft(shopId, payload);
      if (res?.id) setDraftId(String(res.id));
      showToast(t('publishFlow.draftSavedLocal'), 'success');
      allowUnsavedLeave();
      navigate(buildTiktokPath('products/drafts'));
    } catch (e) {
      showToast(e?.message || t('publishFlow.draftFail'), 'error');
    } finally {
      setSavingDraft(false);
    }
  };

  const openProductTplSaveModal = () => {
    if (!shopId) {
      showToast(t('returnList.pickShop'), 'error');
      return;
    }
    if (!form.categoryId || !form.productTypeId) {
      showToast(t('publishAttr.pickCategory'), 'error');
      return;
    }
    setProductTplSaveModalOpen(true);
  };

  const showSkuQuantity = !skuPkgCapsLoading && skuPkgCaps.quantityInfo.show === true;
  const showSkuPackageType = !skuPkgCapsLoading && skuPkgCaps.packageType.show === true;
  // 包装表已改为 Tiktok SKU 分类；下列能力开关仅留给校验兼容
  void showSkuQuantity;
  void showSkuPackageType;

  const { requiredAttrs, optionalAttrs } = useMemo(
    () => partitionProductAttrsForDisplay(attrTpl, null),
    [attrTpl],
  );
  const allProductAttrs = useMemo(
    () => [...requiredAttrs, ...optionalAttrs],
    [requiredAttrs, optionalAttrs],
  );
  const visibleRequiredAttrs = useMemo(
    () => requiredAttrs.filter((attr) => !isComplianceProductAttr(attr) && isEditorAttrVisible(
      attr,
      form.productAttributes || {},
      allProductAttrs,
    )),
    [requiredAttrs, allProductAttrs, form.productAttributes],
  );
  const visibleOptionalAttrs = useMemo(
    () => optionalAttrs.filter((attr) => !isComplianceProductAttr(attr) && isEditorAttrVisible(
      attr,
      form.productAttributes || {},
      allProductAttrs,
    )),
    [optionalAttrs, allProductAttrs, form.productAttributes],
  );
  // 合规类属性（生产批次号 / 安全警告等）单独交给「商品合规」区块渲染
  const visibleComplianceAttrs = useMemo(
    () => splitComplianceProductAttrs(allProductAttrs).compliance.filter((attr) => isEditorAttrVisible(
      attr,
      form.productAttributes || {},
      allProductAttrs,
    )),
    [allProductAttrs, form.productAttributes],
  );

  const syncTitleEnFromZh = async (zhTitle, { silent = false, force = false } = {}) => {
    const zh = String(zhTitle || '').trim();
    if (!zh) {
      setForm((prev) => (prev.titleEn ? { ...prev, titleEn: '' } : prev));
      lastAutoTitleEnRef.current = '';
      return '';
    }
    const seq = ++titleTranslateSeqRef.current;
    setTitleTranslateBusy(true);
    try {
      const titleEn = await translateTiktokTitleToEnglish(zh, titleMaxChars);
      if (seq !== titleTranslateSeqRef.current) return titleEn;
      setForm((prev) => {
        const currentEn = String(prev.titleEn || '').trim();
        const lastAuto = String(lastAutoTitleEnRef.current || '').trim();
        // 用户已手改英文名：非强制时不覆盖
        if (!force && currentEn && currentEn !== lastAuto) {
          return prev;
        }
        lastAutoTitleEnRef.current = titleEn;
        return { ...prev, titleEn };
      });
      return titleEn;
    } catch (error) {
      if (seq !== titleTranslateSeqRef.current) return '';
      if (!silent) {
        showToast(error?.message || t('templateEditor.translateFail'), 'warning');
      }
      throw error;
    } finally {
      if (seq === titleTranslateSeqRef.current) {
        setTitleTranslateBusy(false);
      }
    }
  };

  useEffect(() => {
    if (hideTitleEn) return undefined;
    const zh = String(form.title || '').trim();
    if (!zh) {
      if (form.titleEn) {
        setForm((prev) => ({ ...prev, titleEn: '' }));
      }
      lastAutoTitleEnRef.current = '';
      return undefined;
    }
    const timer = window.setTimeout(() => {
      void syncTitleEnFromZh(zh, { silent: true, force: false }).catch(() => { });
    }, 700);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.title, hideTitleEn]);

  const handleAiTitle = async () => {
    if (aiTitleBusy || aiDescBusy || submitting) return;
    const confirmed = await confirmIpeCreditAction({
      navId: 'aiCopywriting',
      imageCount: 1,
      title: t('templateEditor.aiTitleDialog'),
      confirmLabel: t('templateEditor.confirmGen'),
    });
    if (!confirmed) return;

    setAiTitleBusy(true);
    showToast(t('templateEditor.aiTitleBusy'), 'info');
    try {
      const title = await generateTiktokPublishAiTitle({
        skcList: form.skcList,
        categoryPathLabel: form.categoryPathLabel,
        productAttributes: form.productAttributes,
        attrDefs: allProductAttrs,
        region: marketMeta.region,
        titleMaxLength: marketMeta.titleMaxLength,
        maxChars: titleMaxChars,
      });
      const en = title.slice(0, titleMaxChars);
      titleTranslateSeqRef.current += 1;
      lastAutoTitleEnRef.current = en;
      setForm((prev) => ({
        ...prev,
        title: en,
        ...(hideTitleEn ? {} : { titleEn: en }),
      }));
      showToast(t('templateEditor.aiTitleDone'), 'success');
    } catch (error) {
      showToast(error?.message || t('templateEditor.aiTitleFail'), 'error');
    } finally {
      setAiTitleBusy(false);
    }
  };

  const handleAiDescription = async () => {
    if (aiDescBusy || aiTitleBusy || submitting) return;
    const mainUrls = resolveAllSkcMainImageUrls(form.skcList);
    if (!mainUrls.length) {
      showToast(t('templateEditor.needMainImage'), 'warning');
      return;
    }
    const confirmed = await confirmIpeCreditAction({
      navId: 'aiCopywriting',
      imageCount: 1,
      title: t('templateEditor.aiDescTitle'),
      confirmLabel: t('templateEditor.confirmGen'),
    });
    if (!confirmed) return;

    const brandLabel = brandFieldOptions.find(
      (opt) => String(opt.value) === String(form.brandCode || '').trim(),
    )?.label || String(form.brandCode || '').trim();

    setAiDescBusy(true);
    showToast(t('templateEditor.aiDescBusy', { count: mainUrls.length }), 'info');
    try {
      const html = await generateTiktokPublishAiDescription({
        skcList: form.skcList,
        categoryPathLabel: form.categoryPathLabel,
        productAttributes: form.productAttributes,
        attrDefs: allProductAttrs,
        brandLabel,
        productTitle: form.title,
        maxChars: TIKTOK_DESCRIPTION_MAX_CHARS,
      });
      setForm((prev) => ({ ...prev, description: html }));
      showToast(t('templateEditor.aiDescDone'), 'success');
    } catch (error) {
      showToast(error?.message || t('templateEditor.aiDescFail'), 'error');
    } finally {
      setAiDescBusy(false);
    }
  };

  const handleAssessPrintInfringement = async () => {
    if (printInfringementBusy || form.ipePrintInfringement?.assessing) return;
    const imageUrl = resolveFirstPrintImageUrl(form.printImages);
    if (!imageUrl) {
      showToast(t('publishFlow.needPrint'), 'warning');
      return;
    }
    const confirmed = await confirmIpeCreditAction({
      navId: 'infringementDetection',
      imageCount: 1,
      title: t('publishSpec.infringement'),
      confirmLabel: t('publishFlow.confirmDetect'),
    });
    if (!confirmed) return;

    setPrintInfringementBusy(true);
    setForm((prev) => ({
      ...prev,
      ipePrintInfringement: {
        ...normalizeTiktokPrintInfringement(prev.ipePrintInfringement),
        assessing: true,
      },
    }));
    showToast(t('publishFlow.printBusy'), 'info');
    try {
      const result = await assessTiktokPublishPrintInfringement(imageUrl);
      setForm((prev) => ({
        ...prev,
        ipePrintInfringement: normalizeTiktokPrintInfringement({
          risk: result.risk,
          assessing: false,
          riskAdvice: result.riskAdvice,
          assessedAt: result.assessedAt,
        }),
      }));
      showToast(t('publishFlow.printDone'), 'success');
    } catch (error) {
      setForm((prev) => ({
        ...prev,
        ipePrintInfringement: normalizeTiktokPrintInfringement({
          risk: 'pending',
          assessing: false,
          riskAdvice: error?.message || t('publishFlow.printFail'),
          assessedAt: new Date().toISOString(),
        }),
      }));
      showToast(error?.message || t('publishFlow.printFail'), 'error');
    } finally {
      setPrintInfringementBusy(false);
    }
  };

  const validate = (targetForm = form) => {
    if (sizeChartClassError) {
      return {
        ok: false,
        message: sizeChartClassError,
        section: 'spec',
        field: 'sizeChart',
      };
    }
    return validateTiktokPublishForm(targetForm, {
      isEdit,
      shopId,
      shopType: activeShop?.shopType,
      attrTpl,
      colorAttr,
      sizeAttr,
      sizeMeasureAttrs,
      sizeChartRequired,
      warehouseOptions: meta.warehouses || [],
      skuPkgCaps,
      categoryRules,
      region: marketMeta.region,
      titleMaxLength: marketMeta.titleMaxLength,
    });
  };

  const clearVersionConflict = (type) => {
    const key = conflictFieldKey(type);
    setVersionConflicts((prev) => {
      if (!prev?.[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setChangePreviews((prev) => {
      if (!prev?.[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const buildAttrChangePreview = async () => {
    const id = String(selectedAttrTemplateId || '').trim();
    if (!shopId || !id) return { title: t('templateEditor.attrUpdated'), changes: [] };
    const res = await tiktokPlatformAPI.getProductAttributeTemplate(shopId, id);
    const item = res?.item || {};
    const { patch } = applyProductAttributeTemplatePayload(
      item.payload || {},
      (attrTplRef.current?.requiredProduct || [])
        .concat(attrTplRef.current?.mainAttrs || [])
        .concat(attrTplRef.current?.otherAttrs || [])
        .map((attr) => String(attr.attributeId || ''))
        .filter(Boolean),
    );
    const nextAttrs = alignAttributesToCategoryTemplate({ ...patch }, attrTplRef.current || attrTpl);
    const attrDefs = [
      ...(attrTplRef.current?.requiredProduct || attrTpl?.requiredProduct || []),
      ...(attrTplRef.current?.mainAttrs || attrTpl?.mainAttrs || []),
      ...(attrTplRef.current?.otherAttrs || attrTpl?.otherAttrs || []),
    ];
    const { changes, count } = diffProductAttributeChanges({
      current: form.productAttributes || {},
      next: nextAttrs,
      attrDefs,
    });
    return {
      title: t('templateEditor.attrUpdated'),
      intro: t('templateEditor.attrDiffIntro', { name: item.name || id, count }),
      changes,
      count,
    };
  };

  const buildSizeChartChangePreview = async () => {
    const id = String(selectedSizeChartTemplateId || '').trim();
    if (!shopId || !id) return { title: t('templateEditor.sizeUpdated'), changes: [] };
    const res = await tiktokPlatformAPI.getSizeChartTemplate(shopId, id);
    const item = res?.item || {};
    const patch = extractSizeChartTemplatePatch(item);
    const { changes, count } = diffSizeChartChanges({
      current: form.sizeChart || {},
      next: patch.sizeChart || {},
      measureAttrs: sizeMeasureAttrs,
    });
    return {
      title: t('templateEditor.sizeUpdated'),
      intro: t('templateEditor.sizeDiffIntro', { name: item.name || id, count }),
      changes,
      count,
    };
  };

  const reloadConflictingTemplateRegion = async (type) => {
    if (!shopId) return;
    setReloadingConflictType(type);
    try {
      if (type === TEMPLATE_VERSION_TYPES.product) {
        const id = String(selectedProductTemplateId || '').trim();
        if (!id) return;
        const res = await tiktokPlatformAPI.getProductTemplate(shopId, id);
        const item = res?.item || res || {};
        const merged = mergeProductTemplateIntoForm(form, item);
        setForm((prev) => mergeProductTemplateIntoForm(prev, item).form);
        if (merged.selectedAttrTemplateId) {
          setSelectedAttrTemplateId(merged.selectedAttrTemplateId);
        }
        if (merged.selectedSizeChartTemplateId) {
          setSelectedSizeChartTemplateId(merged.selectedSizeChartTemplateId);
        }
        setSelectedProductTemplateVersion(merged.version);
        clearVersionConflict(type);
        showToast(t('publishFlow.reloadedTpl'), 'success');
        return;
      }
      if (type === TEMPLATE_VERSION_TYPES.attribute) {
        const id = String(selectedAttrTemplateId || '').trim();
        if (!id) return;
        const res = await tiktokPlatformAPI.getProductAttributeTemplate(shopId, id);
        const patch = extractAttrTemplatePatch(res?.item || res || {});
        setForm((prev) => {
          const aligned = alignAttributesToCategoryTemplate(
            { ...(patch.productAttributes || {}) },
            attrTplRef.current || attrTpl,
          );
          return { ...prev, productAttributes: aligned };
        });
        setSelectedAttrTemplateVersion(patch.version);
        clearVersionConflict(type);
        showToast(t('publishFlow.reloadedAttrs'), 'success');
        return;
      }
      if (type === TEMPLATE_VERSION_TYPES.sizeChart) {
        const id = String(selectedSizeChartTemplateId || '').trim();
        if (!id) return;
        const res = await tiktokPlatformAPI.getSizeChartTemplate(shopId, id);
        const patch = extractSizeChartTemplatePatch(res?.item || res || {});
        if (patch.kind === 'tiktok-official' || patch.officialSizeChart) {
          setForm((prev) => ({
            ...prev,
            officialSizeChart: patch.officialSizeChart,
            tiktokSizeTemplateIds: (patch.platformBusinessIds || []).map(String),
          }));
        } else {
          applySizeChart(patch.sizeChart || {}, { replace: true });
        }
        setSelectedSizeChartTemplateVersion(patch.version);
        clearVersionConflict(type);
        showToast(t('publishFlow.reloadedSize'), 'success');
      }
    } catch (e) {
      if (isMissingTemplateError(e)
        && (type === TEMPLATE_VERSION_TYPES.attribute || type === TEMPLATE_VERSION_TYPES.sizeChart)) {
        if (type === TEMPLATE_VERSION_TYPES.attribute) {
          setSelectedAttrTemplateId('');
          setSelectedAttrTemplateVersion(null);
        } else {
          setSelectedSizeChartTemplateId('');
          setSelectedSizeChartTemplateVersion(null);
        }
        clearVersionConflict(type);
        showToast(
          type === TEMPLATE_VERSION_TYPES.sizeChart
            ? t('publishFlow.refSizeOut')
            : t('publishFlow.refAttrOut'),
          'warning',
        );
        return;
      }
      showToast(e?.message || t('templateEditor.reloadFail'), 'error');
    } finally {
      setReloadingConflictType('');
    }
  };

  const onSubmit = async () => {
    // 印花图仅站内展示 / 侵权检测，不自动拼接到货号与商家 SKU
    // TTS：不自动裁色块图/素材图
    let formWithPrintNames = await enrichTiktokFormPrintNames(form);
    if (formWithPrintNames !== form) {
      setForm(formWithPrintNames);
    }

    const zhTitle = String(formWithPrintNames.title || '').trim();
    if (!hideTitleEn && zhTitle && !String(formWithPrintNames.titleEn || '').trim()) {
      setSubmitting(true);
      setSubmitHint(t('templateEditor.translatingEnToast'));
      try {
        const titleEn = await syncTitleEnFromZh(zhTitle, { silent: true, force: true });
        formWithPrintNames = { ...formWithPrintNames, titleEn };
      } catch (error) {
        applyFieldErrors([{
          field: 'title',
          section: 'basic',
          message: error?.message || t('templateEditor.translateRetry'),
        }]);
        setSubmitting(false);
        setSubmitHint('');
        return;
      }
      setSubmitting(false);
      setSubmitHint('');
    }

    const result = validate(formWithPrintNames);
    if (!result.ok) {
      applyFieldErrors([{
        field: result.field || 'form',
        section: result.section || 'basic',
        message: result.message,
      }]);
      return;
    }
    if (sizeChartRequired) {
      const sizeCheck = officialSizeChartRef.current?.validate?.();
      if (sizeCheck && !sizeCheck.ok) {
        applyFieldErrors([{
          field: 'sizeChart',
          section: 'spec',
          message: sizeCheck.message,
        }]);
        return;
      }
    }
    setFieldErrors({});
    setSubmitting(true);
    try {
      setSubmitHint(isEdit
        ? t('publishFlow.checkingSkuTt')
        : t('publishFlow.checkingSkuTt'));
      const preflight = await runTiktokPublishPreflight({
        shopId,
        form: formWithPrintNames,
        isEdit,
        attrTpl,
        shopType: activeShop?.shopType,
        shopCaps,
        siteList: meta.sites,
      });
      if (!preflight.ok) {
        applyFieldErrors([{
          field: preflight.field || 'form',
          section: preflight.section || 'basic',
          message: preflight.message,
        }]);
        return;
      }

      let resolvedSizeTemplateIds = formWithPrintNames.tiktokSizeTemplateIds || [];
      if (sizeChartRequired && !isEdit) {
        setSubmitHint(t('publishFlow.creatingSizeTt'));
        try {
          if (!officialSizeChartRef.current?.resolveForSubmit) {
            throw new Error(t('publishFlow.sizePanelNotReady'));
          }
          resolvedSizeTemplateIds = await officialSizeChartRef.current.resolveForSubmit();
          formWithPrintNames = {
            ...formWithPrintNames,
            tiktokSizeTemplateIds: resolvedSizeTemplateIds.map(String),
          };
          setForm((prev) => ({
            ...prev,
            tiktokSizeTemplateIds: resolvedSizeTemplateIds.map(String),
          }));
        } catch (sizeErr) {
          applyFieldErrors([{
            field: 'sizeChart',
            section: 'spec',
            message: sizeErr?.message || t('publishFlow.sizeHandleFail'),
          }]);
          showToast(sizeErr?.message || t('publishFlow.sizeHandleFail'), 'error');
          return;
        }
      }

      // 自定义颜色 / 自定义尺码：按 TikTok Create Product 契约只带 value_name 提交
      // （文档：sales_attributes 可给自定义 value_name，平台自动生成值 ID；禁止 value_id=0）
      // 不再预创建平台规格：TikTok 侧 /publish/specs 后端未实现，只会 404 阻断发品。

      const templateRefs = collectAppliedTemplateRefs({
        productTemplateId: selectedProductTemplateId,
        productTemplateVersion: selectedProductTemplateVersion,
        attrTemplateId: selectedAttrTemplateId,
        attrTemplateVersion: selectedAttrTemplateVersion,
        sizeChartTemplateId: selectedSizeChartTemplateId,
        sizeChartTemplateVersion: selectedSizeChartTemplateVersion,
      });
      if (templateRefs.length > 0) {
        setSubmitHint(t('publishFlow.checkingTemplates'));
        const versionCheck = await checkTemplateVersions(tiktokPlatformAPI, shopId, templateRefs);
        if (!versionCheck.ok) {
          const conflicts = versionCheck.conflicts || [];
          const { missingLibraryRefs, blocking } = partitionTemplateVersionConflicts(conflicts);
          if (missingLibraryRefs.length) {
            missingLibraryRefs.forEach((c) => {
              if (c.type === TEMPLATE_VERSION_TYPES.attribute) {
                setSelectedAttrTemplateId('');
                setSelectedAttrTemplateVersion(null);
              } else if (c.type === TEMPLATE_VERSION_TYPES.sizeChart) {
                setSelectedSizeChartTemplateId('');
                setSelectedSizeChartTemplateVersion(null);
              }
            });
          }
          if (blocking.length) {
            const nextMarks = {};
            blocking.forEach((c) => {
              const key = conflictFieldKey(c.type);
              if (key) nextMarks[key] = true;
            });
            setVersionConflicts(nextMarks);
            const nextPreviews = {};
            await Promise.all(blocking.map(async (c) => {
              try {
                if (c.type === TEMPLATE_VERSION_TYPES.attribute) {
                  nextPreviews.productAttributes = await buildAttrChangePreview();
                } else if (c.type === TEMPLATE_VERSION_TYPES.sizeChart) {
                  nextPreviews.sizeChart = await buildSizeChartChangePreview();
                }
              } catch {
                /* ignore */
              }
            }));
            setChangePreviews(nextPreviews);
            const attrCount = nextPreviews.productAttributes?.count;
            const sizeCount = nextPreviews.sizeChart?.count;
            const detailBits = [];
            if (attrCount != null) detailBits.push(t('templateEditor.attrCount', { count: attrCount }));
            if (sizeCount != null) detailBits.push(t('templateEditor.sizeCount', { count: sizeCount }));
            showToast(
              detailBits.length
                ? `${conflictToastMessage(blocking)}${t('templateEditor.conflictDetail', { detail: detailBits.join('，') })}`
                : conflictToastMessage(blocking),
              'warning',
              3000,
            );
            const firstField = conflictFieldKey(blocking[0]?.type) || 'form';
            const el = document.querySelector(`[data-field-error="${firstField}"]`);
            if (el?.scrollIntoView) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
          }
        }
        setVersionConflicts({});
        setChangePreviews({});
      }

      // 提交币种：半托管/全托管对齐发布规范；自运营对齐站点列表（已写入 publishCurrencies）
      const alignedCurrency = pickPublishCurrency(
        publishCurrencies,
        formWithPrintNames.currency || platformCurrency,
      );
      if (!alignedCurrency) {
        applyFieldErrors([{
          field: 'currency',
          section: 'sales',
          message: shopCaps.usesPriceInfo
            ? t('publishFlow.needSiteCurrency')
            : t('publishFlow.needRuleCurrency'),
        }]);
        return;
      }
      const sizeValueLabels = { ...(formWithPrintNames.sizeValueLabels || {}) };
      (sizeAttr?.values || []).forEach((v) => {
        if (v?.id != null && v?.name) sizeValueLabels[String(v.id)] = String(v.name);
      });
      const resolvedSizeSaleAttrId = formWithPrintNames.sizeSaleAttributeId || sizeAttr?.attributeId || '';
      // 提交纵轴必须与供应表 SKU 一致，勿用可能含重复行的 selectedSizeIds
      const fromSkcSizes = deriveSelectedSizeIds(formWithPrintNames.skcList).map((id) => String(id));
      const resolvedSelectedSizeIds = fromSkcSizes.length
        ? fromSkcSizes
        : (formWithPrintNames.selectedSizeIds || []).map((id) => String(id));
      const submitFormBase = {
        ...formWithPrintNames,
        sizeValueLabels,
        // 尺码表关联次销售：缺 id 时平台会报「尺寸表填写数值不能为空 / 单选多值」
        sizeSaleAttributeId: resolvedSizeSaleAttrId,
        selectedSizeIds: resolvedSelectedSizeIds,
        // 从已发布商品另存草稿再发：按新建品处理，勿带平台 SPU
        spuName: isEdit ? formWithPrintNames.spuName : '',
        currency: alignedCurrency || formWithPrintNames.currency,
        skcList: (formWithPrintNames.skcList || []).map((skc) => {
          const resolved = resolveSkcColorAgainstTemplate({
            ...skc,
            colorAttributeId: skc.colorAttributeId || colorAttr?.attributeId || '',
          }, colorAttr);
          return {
            ...resolved,
            tiktokSpecId: skc.tiktokSpecId || resolved.tiktokSpecId,
            // 确保主规格属性 ID 齐全，否则 sale_attribute 组不出来
            colorLabel: resolved.colorIsCustom || resolved.colorExtraValue
              ? (resolved.colorExtraValue || resolved.colorLabel)
              : (colorAttr?.values?.find((v) => String(v.id) === String(resolved.colorValueId))?.name
                || resolved.colorLabel),
            skcName: isEdit ? skc.skcName : '',
            skuRows: (skc.skuRows || []).map((sku) => ({
              ...sku,
              sizeAttributeId: sku.sizeAttributeId || sizeAttr?.attributeId || resolvedSizeSaleAttrId || '',
              sizeLabel: sizeAttr?.values?.find((v) => String(v.id) === String(sku.sizeValueId))?.name
                || sku.sizeLabel
                || sizeValueLabels[String(sku.sizeValueId || '')]
                || '',
              currency: alignedCurrency || sku.currency || formWithPrintNames.currency,
              skuCode: isEdit ? sku.skuCode : '',
            })),
          };
        }),
      };
      // 丢掉模板残留规格行；mode=3（如周长）收敛为单值，避免平台报「单选多值」
      const submitForm = {
        ...submitFormBase,
        sizeChart: pruneSizeChartForSubmit(submitFormBase, sizeMeasureAttrs),
        shopType: activeShop?.shopType,
        siteList: meta.sites,
        ...(skipSiteWarehouseFreight
          ? {
            selectedSites: [],
            warehouseId: '',
            warehouseName: '',
            freightTemplateId: '',
            deliveryOptionIds: [],
            shipmentLimitSecond: '',
            tiktokSiteIds: [],
            tiktokSiteId: '',
            tiktokFreightTemplateId: '',
          }
          : {}),
      };
      if (isEdit) {
        const idCheck = validateEditPlatformIds(submitForm, editBaselineRef.current);
        if (!idCheck.ok) {
          applyFieldErrors([{
            field: 'skcList',
            section: 'spec',
            message: idCheck.message,
          }]);
          return;
        }
        const priceChanges = collectSkuPriceChanges(submitForm, editBaselineRef.current, shopCaps);
        if (!priceChanges.empty && priceChanges.mode === 'none') {
          applyFieldErrors([{
            field: 'supply',
            section: 'spec',
            message: t('publishFlow.priceUnsupported'),
          }]);
          return;
        }
        // 成本价任意变更、售价涨价：先确认涨价原因
        if (!priceChanges.empty && (priceChanges.mode === 'cost' || priceChanges.hasIncrease)) {
          setPriceConfirm({ submitForm, changes: priceChanges });
          return;
        }
        await finishEditSubmit(submitForm, priceChanges, {});
        return;
      }

      let payload;
      try {
        payload = buildTiktokPublishPayload(submitForm, {
          isEdit: false,
          sizeAttrs: sizeMeasureAttrs,
          skuPkgCaps,
          shopType: activeShop?.shopType,
          siteList: meta.sites,
          attrTpl,
          categories: meta.categories,
          categoryRules,
          region: marketMeta.region,
          titleMaxLength: marketMeta.titleMaxLength,
        });
      } catch (buildErr) {
        applyFieldErrors([{
          field: 'form',
          section: 'basic',
          message: buildErr?.message || t('publishFlow.buildFail'),
        }]);
        return;
      }

      // TTS：提交前调用平台 Check Listing Prerequisites + Check Product Listing
      if (!isEdit && ttsPublishMode) {
        setSubmitHint(t('publishFlow.checkingListing'));
        const listingCheck = await runTiktokListingPlatformCheck({
          shopId,
          ttsProduct: payload?.ttsProduct || null,
          payload,
        });
        if (!listingCheck.ok) {
          applyFieldErrors([{
            field: listingCheck.field || 'form',
            section: listingCheck.section || 'basic',
            message: listingCheck.message,
          }]);
          showToast(listingCheck.message, 'error');
          return;
        }
        if (listingCheck.warning) {
          showToast(listingCheck.warning, 'warning');
        }
      }

      // 幂等键（idempotency_key）：仅真实提交时生成；listing_check 未收录该字段，故不放进核验体
      if (payload?.ttsProduct) {
        payload.ttsProduct.idempotency_key = createTiktokIdempotencyKey();
      }

      const sampleCurrency = samplePublishPayloadCurrency(payload);
      if (!sampleCurrency) {
        applyFieldErrors([{
          field: 'supply',
          section: 'sales',
          message: shopCaps.usesPriceInfo
            ? t('publishFlow.needSaleCurrency')
            : t('publishFlow.needSupplyCurrency'),
        }]);
        return;
      }
      // 提交前落一份完整草稿：发布记录可关联；编辑页回填不依赖审核中不全的平台详情
      let submitDraftId = draftId;
      try {
        setSubmitHint(t('publishFlow.savingDraft'));
        const draftPayload = {
          form: {
            ...submitForm,
            selectedSizeIds: (submitForm.selectedSizeIds?.length
              ? submitForm.selectedSizeIds
              : deriveSelectedSizeIds(submitForm.skcList)
            ).map((id) => String(id)),
          },
        };
        const draftRes = submitDraftId
          ? await tiktokPlatformAPI.updatePublishDraft(shopId, submitDraftId, draftPayload)
          : await tiktokPlatformAPI.createPublishDraft(shopId, draftPayload);
        if (draftRes?.id != null) {
          submitDraftId = String(draftRes.id);
          setDraftId(submitDraftId);
        }
      } catch (draftErr) {
        showToast(draftErr?.message || t('publishFlow.draftFailContinue'), 'warning');
      }
      const params = submitDraftId ? { draftId: Number(submitDraftId) || submitDraftId } : {};
      setSubmitHint(t('publishFlow.submittingJob'));
      let job = await tiktokPlatformAPI.publishOrEditProduct(shopId, payload, params);
      const jobId = job?.jobId;
      let platformRes = job?.response;
      if (jobId && !job?.done) {
        setSubmitHint(job?.message || t('publishFlow.queueWait'));
        const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
        let latest = job;
        while (latest && !latest.done) {
          await sleep(700);
          latest = await tiktokPlatformAPI.getGoodsSubmitJob(shopId, jobId);
          if (!latest) break;
          setSubmitHint(latest.message || t('publishFlow.publishing'));
          job = latest;
        }
        platformRes = latest?.response;
        if (!platformRes && latest?.status === 'failed') {
          throw new Error(latest?.error || latest?.message || t('publishExtra.submitFail'));
        }
      } else if (!platformRes && job && (job.code != null || job.info != null)) {
        platformRes = job;
      }
      const res = platformRes || job?.response || job;
      const ok = isTiktokPublishSuccess(res)
        || job?.ok === true
        || String(job?.status || '').toLowerCase() === 'success';
      if (!ok) {
        const fieldErrs = extractTiktokPublishFieldErrors(res, extractTiktokPublishFailMessage(res) || job?.error);
        applyFieldErrors(fieldErrs, extractTiktokPublishFailMessage(res) || job?.message || job?.error);
        return;
      }
      setFieldErrors({});
      const publishedSpu = extractTiktokPublishedSpu(res, payload, form.spuName)
        || job?.publishedSpu
        || '';
      // TTS Create Product 默认 activate=true：无审核周期则直接上架
      showToast(publishedSpu ? t('publishFlow.publishedLive') : t('publishFlow.published'), 'success');
      if (shopId) {
        try {
          await tiktokPlatformAPI.createPublishRecord(shopId, {
            form: submitForm,
            platformRes: res,
            publishedSpu,
            draftId: submitDraftId,
            action: 'publish',
            listed: true,
          });
        } catch {
          /* 本地记录失败不阻断跳转 */
        }
      }
      // 已发布成功：清理本地草稿，避免草稿箱残留
      if (shopId && submitDraftId) {
        try {
          await tiktokPlatformAPI.deletePublishDraft(shopId, submitDraftId);
          setDraftId('');
        } catch {
          /* 清理失败不阻断跳转 */
        }
      }
      if (!publishedSpu) {
        showToast(t('publishFlow.noProductId'), 'warning');
      }
      if (shopId && publishedSpu) {
        tiktokPlatformAPI.syncCertificates(shopId, { spuName: publishedSpu }).catch(() => { });
        showToast(t('publishFlow.certSynced'), 'info');
      }
      allowUnsavedLeave();
      navigate(buildTiktokPath('products/records'), {
        replace: true,
        state: { refreshPublishRecords: true, at: Date.now() },
      });
    } catch (e) {
      const msg = e?.message || t('publishExtra.submitFail');
      const fieldErrs = extractTiktokPublishFieldErrors(
        { message: msg, msg, errorMsg: msg },
        msg,
      );
      applyFieldErrors(
        fieldErrs.length ? fieldErrs : [{ field: 'form', section: 'basic', message: msg }],
        msg,
      );
    } finally {
      setSubmitting(false);
      setSubmitHint('');
    }
  };

  /** 编辑提交：partialEdit（非价字段）+ 按店铺类型专用改价接口 */
  const finishEditSubmit = async (submitForm, priceChanges, priceMeta = {}) => {
    setSubmitting(true);
    setPriceConfirm(null);
    try {
      // 编辑提交前：店铺发品前置条件（仓/税/运费模板/权限）。
      // 编辑不走 Check Product Listing——那是 Create Product 的同载荷校验，会对已存在商品误报重复。
      if (ttsPublishMode && shopId) {
        setSubmitHint(t('publishFlow.checkingPrereq'));
        const prereq = await runTiktokEditPrerequisitesCheck({ shopId });
        if (!prereq.ok) {
          applyFieldErrors([{
            field: prereq.field || 'form',
            section: prereq.section || 'basic',
            message: prereq.message,
          }]);
          showToast(prereq.message, 'error');
          return;
        }
      }

      // 涨价前查平台活动锁（TTS Promotion `activity_commands=IMMUTABLE` 时为价格锁定）
      if (!priceChanges.empty && priceChanges.hasIncrease && shopId && submitForm.spuName) {
        setSubmitHint(t('publishFlow.checkingActivityLock'));
        try {
          const lockRes = await tiktokPlatformAPI.getPublishPriceLocks(shopId, {
            productId: submitForm.spuName,
          });
          const lockCheck = validateTiktokPriceChangesAgainstLocks(priceChanges, lockRes);
          if (!lockCheck.ok) {
            const lockErr = new Error(lockCheck.message);
            lockErr.priceLockBlocked = true;
            throw lockErr;
          }
          if (lockCheck.warning) {
            showToast(lockCheck.warning, 'warning');
          }
        } catch (lockErr) {
          // 查询失败不阻断降价；锁定命中必须拦截
          if (lockErr?.priceLockBlocked) {
            throw lockErr;
          }
        }
      }

      let payload = null;
      let skippedPartial = false;
      try {
        payload = buildTiktokEditPayload(submitForm, {
          baselineForm: editBaselineRef.current,
          sizeAttrs: sizeMeasureAttrs,
          skuPkgCaps,
          attrTpl,
          categoryRules,
        });
        const ttsWrapped = buildTiktokPublishPayload(submitForm, {
          isEdit: true,
          sizeAttrs: sizeMeasureAttrs,
          skuPkgCaps,
          shopType: activeShop?.shopType,
          siteList: meta.sites,
          attrTpl,
          categories: meta.categories,
          categoryRules,
          region: marketMeta.region,
          titleMaxLength: marketMeta.titleMaxLength,
        });
        payload = {
          ...payload,
          productId: submitForm.spuName,
          ttsProduct: ttsWrapped?.ttsProduct || null,
        };
      } catch (buildErr) {
        if (!priceChanges.empty && isEmptyPartialEditError(buildErr)) {
          skippedPartial = true;
        } else {
          throw buildErr;
        }
      }

      let submitDraftId = draftId;
      try {
        setSubmitHint(t('publishFlow.savingDraft'));
        const draftPayload = {
          form: {
            ...submitForm,
            selectedSizeIds: (submitForm.selectedSizeIds?.length
              ? submitForm.selectedSizeIds
              : deriveSelectedSizeIds(submitForm.skcList)
            ).map((id) => String(id)),
          },
        };
        const draftRes = submitDraftId
          ? await tiktokPlatformAPI.updatePublishDraft(shopId, submitDraftId, draftPayload)
          : await tiktokPlatformAPI.createPublishDraft(shopId, draftPayload);
        if (draftRes?.id != null) {
          submitDraftId = String(draftRes.id);
          setDraftId(submitDraftId);
        }
      } catch (draftErr) {
        showToast(draftErr?.message || t('publishFlow.draftFailContinue'), 'warning');
      }

      let partialOk = skippedPartial;
      let publishedSpu = submitForm.spuName || '';
      if (!skippedPartial && payload) {
        const params = submitDraftId ? { draftId: Number(submitDraftId) || submitDraftId } : {};
        setSubmitHint(t('publishFlow.submittingEdit'));
        let job = await tiktokPlatformAPI.partialEditProduct(shopId, payload, params);
        const jobId = job?.jobId;
        let platformRes = job?.response;
        if (jobId && !job?.done) {
          setSubmitHint(job?.message || t('publishFlow.queueWait'));
          const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
          let latest = job;
          while (latest && !latest.done) {
            await sleep(700);
            latest = await tiktokPlatformAPI.getGoodsSubmitJob(shopId, jobId);
            if (!latest) break;
            setSubmitHint(latest.message || t('publishFlow.editing'));
            job = latest;
          }
          platformRes = latest?.response;
          if (!platformRes && latest?.status === 'failed') {
            throw new Error(latest?.error || latest?.message || t('publishFlow.editSubmitFail'));
          }
        } else if (!platformRes && job && (job.code != null || job.info != null)) {
          platformRes = job;
        }
        const res = platformRes || job?.response || job;
        const ok = isTiktokPublishSuccess(res)
          || job?.ok === true
          || String(job?.status || '').toLowerCase() === 'success';
        if (!ok) {
          const fieldErrs = extractTiktokPublishFieldErrors(res, extractTiktokPublishFailMessage(res) || job?.error);
          applyFieldErrors(fieldErrs, extractTiktokPublishFailMessage(res) || job?.message || job?.error);
          return;
        }
        partialOk = true;
        publishedSpu = extractTiktokPublishedSpu(res, payload, submitForm.spuName)
          || job?.publishedSpu
          || publishedSpu;
      }

      let priceMsg = '';
      if (!priceChanges.empty) {
        setSubmitHint(priceChanges.mode === 'sale' ? t('publishFlow.updatingSale') : t('publishFlow.updatingCost'));
        if (priceChanges.mode === 'cost') {
          const costBody = buildUpdateCostPayload(priceChanges, {
            reasonBySkc: priceMeta.reasonBySkc || {},
            remarkBySkc: priceMeta.remarkBySkc || {},
            fileBySkc: priceMeta.fileBySkc || {},
          });
          const chunks = chunkUpdateCostPayload(costBody);
          for (let i = 0; i < chunks.length; i += 1) {
            if (chunks.length > 1) {
              setSubmitHint(t('publishFlow.updatingCostChunk', { current: i + 1, total: chunks.length }));
            }
            const costRes = await tiktokPlatformAPI.updateProductCost(shopId, chunks[i]);
            const interpreted = interpretPriceUpdateResponse(costRes, 'cost');
            if (!interpreted.ok) {
              throw new Error(interpreted.message);
            }
            priceMsg = interpreted.message;
          }
        } else if (priceChanges.mode === 'sale') {
          const productId = String(
            submitForm.productId || submitForm.spuName || priceChanges.items?.[0]?.productId || '',
          ).trim();
          const saleBody = buildTiktokUpdatePricePayload(productId, priceChanges);
          const saleRes = await tiktokPlatformAPI.saveProductSalePrice(shopId, saleBody);
          const interpreted = interpretPriceUpdateResponse(saleRes, 'sale');
          if (!interpreted.ok) {
            throw new Error(interpreted.message);
          }
          priceMsg = interpreted.message;
          if (interpreted.pending) {
            showToast(interpreted.message, 'warning');
          }
        }
      }

      if (!partialOk && priceChanges.empty) {
        throw new Error(t('publishFlow.noChanges'));
      }

      setFieldErrors({});
      if (partialOk && !priceChanges.empty) {
        showToast(priceMsg ? t('publishFlow.editSubmittedWith', { detail: priceMsg }) : t('publishFlow.editPriceUpdated'), 'success');
      } else if (!priceChanges.empty) {
        showToast(priceMsg || t('publishFlow.priceUpdated'), 'success');
      } else {
        showToast(t('publishFlow.editSubmitted'), 'success');
      }
      if (shopId && (partialOk || !priceChanges.empty)) {
        try {
          await tiktokPlatformAPI.createPublishRecord(shopId, {
            form: submitForm,
            platformRes: null,
            publishedSpu,
            draftId: submitDraftId,
            action: 'edit',
          });
        } catch {
          /* ignore */
        }
      }
      if (!publishedSpu) {
        showToast(t('publishFlow.noProductId'), 'warning');
      }
      allowUnsavedLeave();
      navigate(buildTiktokPath('products/records'), {
        replace: true,
        state: { refreshPublishRecords: true, at: Date.now() },
      });
      return { ok: true };
    } catch (e) {
      const msg = e?.message || t('publishExtra.submitFail');
      const fieldErrs = extractTiktokPublishFieldErrors(
        { message: msg, msg, errorMsg: msg },
        msg,
      );
      applyFieldErrors(
        fieldErrs.length ? fieldErrs : [{ field: 'form', section: 'basic', message: msg }],
        msg,
      );
      return { ok: false, message: msg };
    } finally {
      setSubmitting(false);
      setSubmitHint('');
    }
  };

  const allowedAttrIds = useMemo(
    () => allProductAttrs.map((attr) => String(attr.attributeId || '')).filter(Boolean),
    [allProductAttrs],
  );
  attrTplRef.current = attrTpl;

  const resolvedAttrStyleNo = useMemo(
    () => {
      const fromForm = String(form.supplierCode || '').trim();
      const fromSkc = (form.skcList || [])
        .map((skc) => String(skc?.supplierCode || '').trim())
        .find(Boolean);
      return styleNoFromSupplierCode(
        fromForm || fromSkc,
        attrTemplates.map((item) => item?.styleNo).filter(Boolean),
      );
    },
    [form.skcList, form.supplierCode, attrTemplates],
  );

  const primarySupplierCode = useMemo(() => {
    const fromForm = String(form.supplierCode || '').trim();
    if (fromForm) return fromForm;
    // 旧草稿：色块货号回退
    return (form.skcList || [])
      .map((skc) => String(skc?.supplierCode || '').trim())
      .find(Boolean) || '';
  }, [form.skcList, form.supplierCode]);

  useEffect(() => {
    const target = String(form.productTypeId || '');
    if (!target || attrLoading || !allowedAttrIds.length) return undefined;
    if (attrTemplatesLoadedForRef.current !== target) return undefined;

    const styleKey = String(resolvedAttrStyleNo || '').trim().toLowerCase();
    const pendingCategory = String(pendingAttrAutoForRef.current || '') === target;
    // 首次进入编辑/回填：锁定当前款号，避免立刻重套覆盖已有属性
    if (lastAttrAutoStyleKeyRef.current === null) {
      lastAttrAutoStyleKeyRef.current = styleKey;
      if (!pendingCategory) return undefined;
    }
    const styleChanged = styleKey !== lastAttrAutoStyleKeyRef.current;
    if (!pendingCategory && !styleChanged) return undefined;

    const picked = pickAutoRuleTemplate(attrTemplates, { styleNo: resolvedAttrStyleNo });
    pendingAttrAutoForRef.current = '';
    lastAttrAutoStyleKeyRef.current = styleKey;
    if (!picked?.id) return undefined;

    let cancelled = false;
    setSelectedAttrTemplateId(String(picked.id));
    setAttrTemplateBusy(true);
    tiktokPlatformAPI.getProductAttributeTemplate(shopId, picked.id)
      .then((res) => {
        if (cancelled) return;
        const payload = res?.item?.payload || {};
        const { patch, filled } = applyProductAttributeTemplatePayload(payload, allowedAttrIds);
        setForm((prev) => {
          if (String(prev.productTypeId) !== target) return prev;
          // 整表替换：空模板则清空类目属性
          const aligned = alignAttributesToCategoryTemplate({ ...patch }, attrTplRef.current);
          return { ...prev, productAttributes: aligned };
        });
        const styleHint = resolvedAttrStyleNo ? t('templateEditor.styleHint', { styleNo: resolvedAttrStyleNo }) : '';
        showToast(
          filled
            ? t('templateEditor.autoNamed', { name: picked.name || picked.id, hint: styleHint })
            : t('templateEditor.autoEmpty'),
          'success',
        );
      })
      .catch(() => {
        /* 自动套用失败不打断发品 */
      })
      .finally(() => {
        if (!cancelled) setAttrTemplateBusy(false);
      });
    return () => { cancelled = true; };
  }, [
    shopId,
    form.productTypeId,
    resolvedAttrStyleNo,
    attrLoading,
    allowedAttrIds,
    attrTemplates,
    t,
  ]);

  const applyAttrTemplate = async (templateId) => {
    const id = String(templateId || '').trim();
    if (!shopId || !id) return;
    if (!allowedAttrIds.length) {
      showToast(t('templateEditor.attrsNotLoaded'), 'error');
      return;
    }
    setAttrTemplateBusy(true);
    try {
      const res = await tiktokPlatformAPI.getProductAttributeTemplate(shopId, id);
      const item = res?.item || {};
      const payload = item.payload || {};
      const { patch, filled } = applyProductAttributeTemplatePayload(payload, allowedAttrIds);
      setForm((prev) => {
        // 整表替换：空模板则清空类目属性
        const aligned = alignAttributesToCategoryTemplate({ ...patch }, attrTplRef.current || attrTpl);
        return { ...prev, productAttributes: aligned };
      });
      setSelectedAttrTemplateVersion(parseTemplateVersion(item.version));
      showToast(filled ? t('templateEditor.attrApplied', { count: filled }) : t('templateEditor.attrEmptyCleared'), 'success');
    } catch (e) {
      showToast(e?.message || t('templateEditor.attrApplyFail'), 'error');
    } finally {
      setAttrTemplateBusy(false);
    }
  };

  const onAttrTemplateSelect = (templateId) => {
    const id = String(templateId || '').trim();
    setSelectedAttrTemplateId(id);
    if (id) {
      applyAttrTemplate(id);
      return;
    }
    setSelectedAttrTemplateVersion(null);
    // 下拉选空：直接清除类目属性
    setForm((prev) => ({ ...prev, productAttributes: {} }));
  };

  const openAttrSaveModal = () => {
    if (!shopId) {
      showToast(t('returnList.pickShop'), 'error');
      return;
    }
    if (!form.categoryId || !form.productTypeId) {
      showToast(t('publishAttr.pickCategory'), 'error');
      return;
    }
    if (!countFilledProductAttributes(form.productAttributes)) {
      showToast(t('templateEditor.fillAttrsFirst'), 'error');
      return;
    }
    setAttrSaveModalOpen(true);
  };

  const refreshAttrTemplates = async () => {
    if (!shopId || !form.productTypeId) return;
    try {
      const listRes = await tiktokPlatformAPI.listProductAttributeTemplates(shopId, {
        productTypeId: form.productTypeId,
        page: 1,
        pageSize: 50,
      });
      setAttrTemplates(Array.isArray(listRes?.items) ? listRes.items : []);
    } catch {
      /* ignore */
    }
  };
  const visibleOptional = useMemo(() => {
    if (attrsExpanded) return visibleOptionalAttrs;
    const hintIds = new Set(Object.keys(indexAttributeChangeHints(
      changePreviews.productAttributes,
      !!versionConflicts.productAttributes,
    )));
    const head = visibleOptionalAttrs.slice(0, 9);
    const headIds = new Set(head.map((a) => String(a.attributeId)));
    const extraTail = visibleOptionalAttrs
      .slice(9)
      .filter((a) => hintIds.has(String(a.attributeId)))
      .filter((a) => !headIds.has(String(a.attributeId)));
    return extraTail.length ? [...head, ...extraTail] : head;
  }, [
    attrsExpanded,
    visibleOptionalAttrs,
    changePreviews.productAttributes,
    versionConflicts.productAttributes,
  ]);

  const attrVersionHintById = useMemo(
    () => indexAttributeChangeHints(
      changePreviews.productAttributes,
      !!versionConflicts.productAttributes,
    ),
    [changePreviews.productAttributes, versionConflicts.productAttributes],
  );

  const detailBootLoading = Boolean(isEdit && loadingSpu);

  return (
    <div className={`tko-wizard tko-publish${submitting ? ' is-publishing' : ''}${detailBootLoading ? ' is-loading-detail' : ''}`}>
      {submitting ? (
        <div className="tko-publish-loading" role="status" aria-live="polite" aria-busy="true">
          <div className="tko-publish-loading__card">
            <i className="tko-publish-loading__spinner" aria-hidden />
            <strong>
              {isEdit ? t('tiktokPublish.submittingEdit') : t('tiktokPublish.publishing')}
            </strong>
            <span>
              {submitHint || t('tiktokPublish.submitWait')}
            </span>
          </div>
        </div>
      ) : null}
      {!submitting && detailBootLoading ? (
        <div className="tko-publish-loading tko-publish-loading--detail" role="status" aria-live="polite" aria-busy="true">
          <div className="tko-publish-loading__card">
            <i className="tko-publish-loading__spinner" aria-hidden />
            <strong>{t('tiktokPublish.loadingDetail')}</strong>
            <span>{t('tiktokPublish.loadingDetailHint')}</span>
          </div>
        </div>
      ) : null}
      <aside className="tko-wizard__steps" aria-label={t('tiktokPublish.sectionsAria')}>
        <div className="tko-wizard__steps-label">
          {isEdit ? t('tiktokPublish.modeEdit') : t('tiktokPublish.modePublish')}
        </div>
        {SECTIONS.map((item, idx) => (
          <button
            key={item.id}
            type="button"
            className={`tko-wizard__step${activeSection === item.id ? ' tko-wizard__step--active' : ''}`}
            onClick={() => scrollToSection(item.id)}
          >
            <span className="tko-wizard__step-index">{idx + 1}</span>
            <span className="tko-wizard__step-title">{t(`tiktokPublish.${item.titleKey}`)}</span>
          </button>
        ))}
        <div className="tko-publish__progress">
          <div className="tko-publish__progress-label">{t('tiktokPublish.progress', { percent: completion })}</div>
          <div className="tko-publish__progress-bar">
            <i style={{ width: `${completion}%` }} />
          </div>
        </div>
      </aside>

      <section className="tko-wizard__panel">
        <div className="tko-wizard__panel-scroll">
          {draftLoading ? (
            <p className="tko-wizard__inline-hint">
              {initialProductTemplateId || fromProductTemplate ? t('tiktokPublish.loadingTemplate') : t('tiktokPublish.loadingDraft')}
            </p>
          ) : null}
          {fieldErrors.form ? (
            <div className="tko-publish-form-error" role="alert" data-field-error="form">
              {fieldErrors.form}
            </div>
          ) : null}

          {/* —— 基础信息 —— */}
          <div id="tko-pub-basic" className="tko-wizard__block" onFocusCapture={() => setActiveSection('basic')}>
            <h3>{t('tiktokPublish.sectionBasic')}</h3>

            <div className="tko-wizard__form">
              <label className={`tko-field tko-field--full${fieldErrors.categoryId ? ' tko-field--invalid' : ''}`} data-field-error="categoryId">
                <span className="tko-field__label">{t('publishBasic.category')} <RequiredMark /></span>
                <CategoryCascaderSelect
                  shopId={shopId}
                  categories={meta.categories}
                  value={form.categoryId}
                  label={form.categoryPathLabel}
                  disabled={!shopId || metaLoading || isEdit}
                  placeholder={!shopId ? t('returnList.pickShop') : t('publishBasic.pickCategory')}
                  onChange={onCategoryChange}
                />
                {!isEdit && !metaLoading && shopId && !(Array.isArray(meta.categories) ? meta.categories.length : 0) ? (
                  <p className="tko-wizard__inline-hint">{t('publishBasic.noCategoryCache')}</p>
                ) : null}

                {isEdit ? (
                  <p className="tko-wizard__inline-hint">{t('publishBasic.categoryLocked')}</p>
                ) : null}
                <FieldErrorText message={fieldErrors.categoryId} />
              </label>

              {!isEdit && form.categoryId ? (
                <label
                  className={`tko-field tko-field--full${versionConflicts.productTemplate ? ' tko-field--version-conflict' : ''}`}
                  data-field-error="productTemplate"
                >
                  <span className="tko-field__label">
                    {t('publishBasic.productTemplate')}
                    <TemplateVersionConflictMark
                      active={!!versionConflicts.productTemplate}
                      reloading={reloadingConflictType === TEMPLATE_VERSION_TYPES.product}
                      onReload={() => reloadConflictingTemplateRegion(TEMPLATE_VERSION_TYPES.product)}
                    />
                  </span>
                  <SearchableSelect
                    value={selectedProductTemplateId}
                    disabled={!shopId || productTemplateBusy || !form.productTypeId}
                    placeholder={
                      productTemplateBusy
                        ? t('publishBasic.loadingTemplates')
                        : (!form.productTypeId
                          ? t('publishBasic.noProductType')
                          : (productTemplates.length ? t('publishBasic.pickTemplate') : t('publishBasic.noTemplateForCat')))
                    }
                    emptyText={t('publishBasic.noMatchTemplate')}
                    onChange={onProductTemplateSelect}
                    options={productTemplates.map((item) => ({
                      value: String(item.id),
                      label: item.sortOrder != null
                        ? t('publishBasic.priorityLabel', { name: item.name || item.id, order: item.sortOrder })
                        : (item.name || String(item.id)),
                    }))}
                  />
                  <p className="tko-wizard__inline-hint">
                    {productTemplates.length > 1
                      ? t('publishBasic.defaultHighest')
                      : (productTemplates.length === 1
                        ? t('publishBasic.autoApplied')
                        : t('publishBasic.createThenPick'))}
                  </p>
                </label>
              ) : (!isEdit && selectedProductTemplateId && versionConflicts.productTemplate) ? (
                <div className="tko-field tko-field--full tko-field--version-conflict" data-field-error="productTemplate">
                  <TemplateVersionConflictMark
                    className="tko-version-conflict-mark--block"
                    active
                    label={t('publishBasic.templateUpdated')}
                    reloading={reloadingConflictType === TEMPLATE_VERSION_TYPES.product}
                    onReload={() => reloadConflictingTemplateRegion(TEMPLATE_VERSION_TYPES.product)}
                  />
                </div>
              ) : null}

              <label className={`tko-field tko-field--full${fieldErrors.title ? ' tko-field--invalid' : ''}`} data-field-error="title">
                <span className="tko-field__label tko-field__label-row">
                  <span>{t('publishBasic.productName')} <RequiredMark /></span>
                  <button
                    type="button"
                    className="tko-btn tko-btn--danger tko-btn--sm"
                    disabled={aiTitleBusy || submitting || attrLoading}
                    title={buildCreditActionHint('aiCopywriting', 1) || undefined}
                    onClick={() => { void handleAiTitle(); }}
                  >
                    {aiTitleBusy ? t('publishBasic.generating') : t('publishBasic.aiTitle')}
                  </button>
                </span>
                <TitleFieldLoadingShell
                  loading={aiTitleBusy}
                  label={t('publishBasic.aiTitleLoadingTt')}
                >
                  <textarea
                    className="tko-textarea tko-textarea--title"
                    value={form.title}
                    maxLength={titleMaxChars}
                    rows={2}
                    readOnly={aiTitleBusy}
                    onChange={(e) => update('title', e.target.value)}
                    placeholder={t('publishBasic.titlePhTt', { max: titleMaxChars })}
                  />
                </TitleFieldLoadingShell>
                <span className="tko-publish__counter">{String(form.title || '').length}/{titleMaxChars}</span>
                <FieldErrorText message={fieldErrors.title} />
              </label>

              {!hideTitleEn ? (
                <label className={`tko-field tko-field--full${fieldErrors.titleEn ? ' tko-field--invalid' : ''}`} data-field-error="titleEn">
                  <span className="tko-field__label">
                    {t('publishBasic.titleEn')}
                    {titleTranslateBusy ? (
                      <span className="tko-wizard__inline-hint" style={{ marginLeft: 8 }}>{t('publishBasic.translating')}</span>
                    ) : null}
                  </span>
                  <textarea
                    className="tko-textarea tko-textarea--title"
                    value={form.titleEn}
                    maxLength={titleMaxChars}
                    rows={2}
                    onChange={(e) => update('titleEn', e.target.value)}
                    placeholder={t('publishBasic.titleEnPh')}
                  />
                  <span className="tko-publish__counter">{String(form.titleEn || '').length}/{titleMaxChars}</span>

                  <FieldErrorText message={fieldErrors.titleEn} />
                </label>
              ) : null}

              <div className={`tko-field tko-field--full${fieldErrors.description ? ' tko-field--invalid' : ''}`} data-field-error="description">
                <span className="tko-field__label tko-field__label-row">
                  <span>{t('publishBasic.desc')} <RequiredMark /></span>
                  <button
                    type="button"
                    className="tko-btn tko-btn--danger tko-btn--sm"
                    disabled={aiDescBusy || aiTitleBusy || submitting || attrLoading}
                    title={buildCreditActionHint('aiCopywriting', 1) || undefined}
                    onClick={() => { void handleAiDescription(); }}
                  >
                    {aiDescBusy ? t('publishBasic.generating') : t('publishBasic.aiDesc')}
                  </button>
                </span>
                <TitleFieldLoadingShell
                  loading={aiDescBusy}
                  label={t('publishBasic.aiDescLoading')}
                >
                  <TiktokDescriptionRichEditor
                    value={form.description}
                    shopId={shopId}
                    disabled={submitting || aiDescBusy}
                    maxLength={TIKTOK_DESCRIPTION_MAX_CHARS}
                    placeholder={t('publishBasic.descPhTt')}
                    onChange={(html) => update('description', html)}
                  />
                </TitleFieldLoadingShell>
                <FieldErrorText message={fieldErrors.description} />
              </div>

              <div className={`tko-field tko-field--full${fieldErrors.searchTerms ? ' tko-field--invalid' : ''}`} data-field-error="searchTerms">
                <span className="tko-field__label tko-field__label-with-help">
                  {t('publishBasic.searchTerms')}
                  <FieldHelpMark text={t('publishBasic.searchHelp')} />
                </span>
                <textarea
                  className="tko-textarea"
                  rows={3}
                  value={form.searchTerms || ''}
                  onChange={(e) => update('searchTerms', e.target.value)}
                  placeholder={t('publishBasic.searchPh')}
                />
                <span className="tko-publish__counter">
                  {searchTermList.length}/{TIKTOK_SEARCH_TERMS_MAX}
                  {' · '}
                  {tiktokStringListChars(searchTermList)}/{TIKTOK_SEARCH_TERMS_MAX_CHARS}
                </span>
                <FieldErrorText message={fieldErrors.searchTerms} />
              </div>

              <div className={`tko-field tko-field--full${fieldErrors.keyProductFeatures ? ' tko-field--invalid' : ''}`} data-field-error="keyProductFeatures">
                <span className="tko-field__label tko-field__label-with-help">
                  {t('publishBasic.features')}
                  <FieldHelpMark text={t('publishBasic.featuresHelp')} />
                </span>
                <textarea
                  className="tko-textarea"
                  rows={3}
                  value={form.keyProductFeatures || ''}
                  onChange={(e) => update('keyProductFeatures', e.target.value)}
                  placeholder={t('publishBasic.featuresPh')}
                />
                <span className="tko-publish__counter">
                  {keyFeatureList.length}/{TIKTOK_KEY_FEATURES_MAX}
                  {' · '}
                  {tiktokStringListChars(keyFeatureList)}/{TIKTOK_KEY_FEATURES_MAX_CHARS}
                </span>
                <FieldErrorText message={fieldErrors.keyProductFeatures} />
              </div>

              <label className={`tko-field${fieldErrors.supplierCode ? ' tko-field--invalid' : ''}`} data-field-error="supplierCode">
                <span className="tko-field__label tko-field__label-with-help">
                  {t('tiktokCatalog.supplier')}
                  <FieldHelpMark text={t('publishBasic.supplierHelp')} />
                </span>
                <input
                  className="tko-input"
                  value={form.supplierCode || ''}
                  maxLength={TIKTOK_EXTERNAL_PRODUCT_ID_MAX}
                  onChange={(e) => update('supplierCode', e.target.value)}
                  placeholder={t('publishBasic.supplierPh')}
                />
                <FieldErrorText message={fieldErrors.supplierCode} />
              </label>

              <label className={`tko-field${fieldErrors.brandCode ? ' tko-field--invalid' : ''}`} data-field-error="brandCode">
                <span className="tko-field__label">{t('publishBasic.brand')}</span>
                <SearchableSelect
                  value={form.brandCode || ''}
                  placeholder={t('publishBasic.brandPh')}
                  allowClear
                  filterLocal={false}
                  loading={brandOptionsLoading}
                  emptyText={brandLibraryEmpty ? t('publishBasic.brandEmpty') : t('publishBasic.brandNone')}
                  onSearch={setBrandKeyword}
                  onChange={(v) => update('brandCode', v || '')}
                  options={brandFieldOptions}
                />
                {brandLibraryEmpty ? (
                  <span className="tko-wizard__inline-hint">
                    {t('publishBasic.brandEmptyHint')}
                  </span>
                ) : null}
                <FieldErrorText message={fieldErrors.brandCode} />
              </label>

              {!hideProductOrigin ? (
                <>
                  <label className={`tko-field${fieldErrors.originRegion ? ' tko-field--invalid' : ''}`} data-field-error="originRegion">
                    <span className="tko-field__label">{t('publishBasic.origin')} <RequiredMark /></span>
                    <SearchableSelect
                      value={form.originRegion || 'CN'}
                      placeholder={t('publishBasic.pickOrigin')}
                      allowClear={false}
                      onChange={(v) => {
                        setForm((prev) => ({
                          ...prev,
                          originRegion: v || 'CN',
                          originCountry: v || 'CN',
                          originRegion2Id: v === 'CN' ? prev.originRegion2Id : '',
                        }));
                      }}
                      options={localizeRegionOptions(ORIGIN_REGION_OPTIONS)}
                    />
                    <FieldErrorText message={fieldErrors.originRegion} />
                  </label>
                  {String(form.originRegion || 'CN') === 'CN' ? (
                    <label className={`tko-field${fieldErrors.originRegion2Id ? ' tko-field--invalid' : ''}`} data-field-error="originRegion2Id">
                      <span className="tko-field__label">{t('publishBasic.originProvince')} <RequiredMark /></span>
                      <SearchableSelect
                        value={form.originRegion2Id}
                        placeholder={t('publishBasic.pickProvince')}
                        onChange={(v) => update('originRegion2Id', v)}
                        options={localizeCnProvinceOptions(CN_REGION2_OPTIONS)}
                      />
                      <FieldErrorText message={fieldErrors.originRegion2Id} />
                    </label>
                  ) : (
                    <div className={`tko-field tko-field--full${fieldErrors.originCertFiles ? ' tko-field--invalid' : ''}`} data-field-error="originCertFiles">
                      <span className="tko-field__label">
                        {t('publishBasic.originCert')}
                        {isFullManaged ? <RequiredMark /> : null}
                      </span>
                      <p className="tko-wizard__inline-hint">
                        {t('publishBasic.originCertHint')}
                      </p>
                      <input
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                        disabled={originCertBusy || !shopId}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          e.target.value = '';
                          if (!file || !shopId) return;
                          setOriginCertBusy(true);
                          try {
                            const res = await tiktokPlatformAPI.uploadPublishGoodsFile(shopId, file);
                            const url = String(res?.url || res?.fileUrl || '').trim();
                            if (!url) throw new Error(t('publishBasic.uploadNoUrl'));
                            update('originCertFiles', [{
                              fileName: String(res?.fileName || file.name),
                              fileUrl: url,
                            }]);
                            showToast(t('publishBasic.originUploaded'), 'success');
                          } catch (err) {
                            showToast(err?.message || t('publishBasic.originUploadFail'), 'error');
                          } finally {
                            setOriginCertBusy(false);
                          }
                        }}
                      />
                      {(form.originCertFiles || []).map((f, idx) => (
                        <div key={`${f.fileUrl}-${idx}`} className="tko-guide-file__list" style={{ marginTop: 8 }}>
                          <span>{f.fileName || f.fileUrl}</span>
                          {' '}
                          <button
                            type="button"
                            className="tko-btn tko-btn--ghost tko-btn--sm"
                            onClick={() => update('originCertFiles', [])}
                          >
                            {t('shop.delete')}
                          </button>
                        </div>
                      ))}
                      <FieldErrorText message={fieldErrors.originCertFiles} />
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </div>

          {/* —— 属性（关键属性 = 平台 is_requried 必填，其余为选项） —— */}
          <div
            id="tko-pub-attrs"
            className="tko-wizard__block"
            onFocusCapture={() => setActiveSection('attrs')}
            data-field-error="productAttributes"
          >
            <div className="tko-publish__block-head">
              <h3>{t('tiktokPublish.sectionAttrs')}</h3>
            </div>
            <FieldErrorText message={fieldErrors.productAttributes} />
            {!form.categoryId ? (
              <p className="tko-wizard__inline-hint">{t('publishAttr.pickCategory')}</p>
            ) : attrLoading ? (
              <div className="tko-attr-loading" role="status" aria-live="polite" aria-busy="true">
                <div className="tko-attr-loading__banner">
                  <span className="tko-attr-loading__spinner" aria-hidden />
                  <div className="tko-attr-loading__copy">
                    <strong>{t('publishAttr.loadingTitle')}</strong>
                    <span>{t('publishAttr.loadingHint')}</span>
                  </div>
                </div>
                <div className="tko-wizard__form tko-wizard__form--attrs tko-attr-loading__skeleton">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div key={index} className="tko-attr-loading__card">
                      <span className="tko-attr-loading__line tko-attr-loading__line--label" />
                      <span className="tko-attr-loading__line tko-attr-loading__line--field" />
                    </div>
                  ))}
                </div>
              </div>
            ) : attrError && !(visibleRequiredAttrs.length || visibleOptionalAttrs.length || visibleComplianceAttrs.length) ? (
              <p className="tko-wizard__inline-error" role="alert">{attrError}</p>
            ) : (
              <>
                {attrError ? <p className="tko-wizard__inline-hint">{attrError}</p> : null}
                {allProductAttrs.length > 0 ? (
                  <div className={`tko-size-chart-template-bar tko-attr-template-bar${versionConflicts.productAttributes ? ' tko-attr-template-bar--version-conflict' : ''}`}>
                    <TemplateVersionConflictMark
                      active={!!versionConflicts.productAttributes}
                      label={
                        changePreviews.productAttributes?.count != null
                          ? t('publishAttr.updatedCount', { count: changePreviews.productAttributes.count })
                          : t('photoLib.updatedMark')
                      }
                      reloading={reloadingConflictType === TEMPLATE_VERSION_TYPES.attribute}
                      resolveChangePreview={async () => (
                        changePreviews.productAttributes || buildAttrChangePreview()
                      )}
                      onReload={() => reloadConflictingTemplateRegion(TEMPLATE_VERSION_TYPES.attribute)}
                    />
                    <select
                      className="tko-select"
                      value={selectedAttrTemplateId}
                      disabled={attrTemplateBusy || !shopId}
                      onChange={(e) => onAttrTemplateSelect(e.target.value)}
                    >
                      <option value="">{t('publishAttr.pickLib')}</option>
                      {attrTemplates.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                          {item.filledAttrCount != null ? t('publishAttr.filledCount', { count: item.filledAttrCount }) : ''}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="tko-btn tko-btn--primary tko-btn--sm"
                      disabled={attrTemplateBusy || !shopId || !form.categoryId}
                      onClick={openAttrSaveModal}
                    >
                      {t('shop.save')}
                    </button>
                  </div>
                ) : null}
                {visibleRequiredAttrs.length > 0 ? (
                  <>
                    <h4 className="tko-publish__subhead">{t('publishAttr.keyAttrs')}</h4>
                    <p className="tko-wizard__inline-hint">
                      {t('publishAttr.keyHint')}
                    </p>
                    <div className="tko-wizard__form tko-wizard__form--attrs">
                      {layoutAttributeFields(visibleRequiredAttrs).map(({ attr, fullWidth }) => (
                        <AttributeField
                          key={attr.attributeId}
                          attr={attr}
                          required={isEditorAttrRequiredMark(
                            attr,
                            form.productAttributes || {},
                            allProductAttrs,
                          )}
                          fullWidth={fullWidth}
                          shopId={shopId}
                          categoryId={form.categoryId}
                          allowCustomValue={!!customAttrPerm[String(attr.attributeId)] && attr.isCustomizable === true}
                          value={form.productAttributes[attr.attributeId]}
                          onChange={(v) => setAttrValue(attr.attributeId, v)}
                          onCustomValueAdded={appendCustomAttrValue}
                          versionUpdateHint={attrVersionHintById[String(attr.attributeId)] || null}
                        />
                      ))}
                    </div>
                  </>
                ) : null}

                {visibleOptionalAttrs.length > 0 ? (
                  <>
                    <h4 className="tko-publish__subhead">{t('publishAttr.options')}</h4>
                    <div className="tko-wizard__form tko-wizard__form--attrs">
                      {layoutAttributeFields(visibleOptional).map(({ attr, fullWidth }) => (
                        <AttributeField
                          key={attr.attributeId}
                          attr={attr}
                          fullWidth={fullWidth}
                          shopId={shopId}
                          categoryId={form.categoryId}
                          allowCustomValue={!!customAttrPerm[String(attr.attributeId)] && attr.isCustomizable === true}
                          value={form.productAttributes[attr.attributeId]}
                          onChange={(v) => setAttrValue(attr.attributeId, v)}
                          onCustomValueAdded={appendCustomAttrValue}
                          versionUpdateHint={attrVersionHintById[String(attr.attributeId)] || null}
                        />
                      ))}
                    </div>
                    {visibleOptionalAttrs.length > 9 ? (
                      <div className="tko-publish__expand">
                        <button type="button" className="tko-btn tko-btn--ghost tko-btn--sm" onClick={() => setAttrsExpanded((v) => !v)}>
                          {attrsExpanded ? t('returnDetail.collapse') : t('publishAttr.expand', { count: visibleOptionalAttrs.length - 9 })}
                        </button>
                      </div>
                    ) : null}
                  </>
                ) : null}

                {!visibleRequiredAttrs.length && !visibleOptionalAttrs.length && !visibleComplianceAttrs.length ? (
                  <p className="tko-wizard__inline-hint">{t('publishAttr.none')}</p>
                ) : null}
              </>
            )}
          </div>

          {/* —— 商品合规（Get Category Rules + 合规类类目属性） —— */}
          <TiktokComplianceRulePanel
            shopId={shopId}
            region={marketMeta.region}
            rules={categoryRules}
            loading={categoryRulesLoading}
            error={categoryRulesError}
            value={form}
            onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
            disabled={submitting}
            onActivate={() => setActiveSection('compliance')}
            extraGroups={visibleComplianceAttrs.length > 0 ? (
              <div className="tko-compliance__group" data-field-error="productAttributes">
                <h4 className="tko-publish__subhead">{t('tiktokPublish.sectionCompliance')}</h4>
                <div className="tko-wizard__form tko-wizard__form--attrs">
                  {layoutAttributeFields(visibleComplianceAttrs).map(({ attr, fullWidth }) => (
                    <AttributeField
                      key={attr.attributeId}
                      attr={attr}
                      required={isEditorAttrRequiredMark(
                        attr,
                        form.productAttributes || {},
                        allProductAttrs,
                      )}
                      fullWidth={fullWidth}
                      shopId={shopId}
                      categoryId={form.categoryId}
                      allowCustomValue={!!customAttrPerm[String(attr.attributeId)] && attr.isCustomizable === true}
                      value={form.productAttributes[attr.attributeId]}
                      onChange={(v) => setAttrValue(attr.attributeId, v)}
                      onCustomValueAdded={appendCustomAttrValue}
                      versionUpdateHint={attrVersionHintById[String(attr.attributeId)] || null}
                    />
                  ))}
                </div>
              </div>
            ) : null}
          />

          {/* —— 规格供应 —— */}
          <div id="tko-pub-spec" className="tko-wizard__block" onFocusCapture={() => setActiveSection('spec')} data-field-error="skcList">
            <div className="tko-publish__block-head">
              <h3>{t('tiktokPublish.sectionSpecTitle')}</h3>
            </div>
            <FieldErrorText message={fieldErrors.skcList || fieldErrors.supplierSku || fieldErrors.supply} />
            <SpecSupplySection
              colorAttr={colorAttr}
              sizeAttr={sizeAttr}
              skcList={form.skcList}
              selectedSizeIds={form.selectedSizeIds || []}
              shopId={shopId}
              categoryId={form.categoryId}
              customAttrPerm={customAttrPerm}
              onCustomValueAdded={appendCustomAttrValue}
              currency={platformCurrency}
              currencyPending={publishCurrencyLoading || !platformCurrency}
              attrLoading={attrLoading}
              isEdit={isEdit}
              priceMode={priceMode}
              priceEditable={!isEdit || Boolean(priceMode)}
              priceLockedHint={
                isEdit && !priceMode
                  ? t('publishSpec.priceLocked')
                  : ''
              }
              defaultSupplierSkuSpu={primarySupplierCode}
              onProductSupplierCodeChange={(code) => update('supplierCode', code || '')}
              mainImage={form.mainImage}
              detailImages={form.detailImages || []}
              onMainImageChange={(img) => update('mainImage', img || null)}
              onDetailImagesChange={(imgs) => update(
                'detailImages',
                Array.isArray(imgs) ? imgs : [],
              )}
              printImages={form.printImages || []}
              onPrintImagesChange={updatePrintImages}
              printInfringement={form.ipePrintInfringement}
              printInfringementBusy={printInfringementBusy}
              onAssessPrintInfringement={handleAssessPrintInfringement}
              infringementCreditHint={buildCreditActionHint('infringementDetection', 1) || ''}
              materialImage={form.materialImage}
              materialImgError={fieldErrors.materialImgUrl}
              onMaterialImagesChange={onMaterialImagesChange}
              tiktokVideos={form.tiktokVideos || []}
              videoError={fieldErrors.tiktokVideos}
              onVideosChange={onVideosChange}
              onColorsChange={onColorsChange}
              onSizesChange={onSizesChange}
              onUpdateSkc={updateSkc}
              onUpdateSku={updateSku}
              onRemoveSku={removeSku}
              onBatchUpdateSku={batchUpdateSku}
              afterMaterialContent={(sizeChartRequired && sizeChartClass?.classId && !sizeChartClassLoading) ? (
                <>
                  {!hideGoodsModels ? (
                    <TiktokModelSelectBar
                      shopId={shopId}
                      visible
                      value={form.goodsModels || []}
                      onChange={(next) => update('goodsModels', next)}
                      errorMessage={fieldErrors.goodsModels}
                      sizeOptions={modelTryOnSizeOptions}
                    />
                  ) : null}
                  <TiktokOfficialSizeChartPanel
                    key={`official-size-${shopId}-${form.categoryId}-${sizeChartClass?.classId || ''}-${selectedProductTemplateId || 'none'}`}
                    ref={officialSizeChartRef}
                    shopId={shopId}
                    categoryId={form.categoryId}
                    productTypeId={form.productTypeId}
                    categoryPathLabel={form.categoryPathLabel}
                    selectedSizeIds={form.selectedSizeIds}
                    selectedSizeLabels={selectedSizeLabels}
                    defaultStyleNo={resolvedAttrStyleNo}
                    required={sizeChartRequired}
                    sizeClass={sizeChartClass}
                    loading={sizeChartClassLoading}
                    errorMessage={fieldErrors.sizeChart}
                    initialDraft={form.officialSizeChart}
                    onDraftChange={onOfficialSizeChartDraftChange}
                    sizeChartImage={form.sizeChartImage || null}
                    onSizeChartImageChange={(img) => update('sizeChartImage', img || null)}
                  />
                </>
              ) : (sizeChartRequired || sizeChartClassLoading || sizeChartClassError) ? (
                <>
                  {!hideGoodsModels ? (
                    <TiktokModelSelectBar
                      shopId={shopId}
                      visible
                      value={form.goodsModels || []}
                      onChange={(next) => update('goodsModels', next)}
                      errorMessage={fieldErrors.goodsModels}
                      sizeOptions={modelTryOnSizeOptions}
                    />
                  ) : null}
                  {sizeChartClassError ? (
                    <p className="tko-wizard__inline-error" role="alert">{sizeChartClassError}</p>
                  ) : null}
                  {sizeChartClassLoading ? (
                    <div
                      className="tko-size-chart tko-official-size-chart tko-size-chart--loading"
                      role="status"
                      aria-live="polite"
                      aria-busy="true"
                    >
                      <div className="tko-size-chart__head">
                        <div className="tko-size-chart__title-row">
                          <h4 className="tko-publish__subhead">{fixedUi("尺寸信息")}</h4>
                        </div>
                      </div>
                      <div className="tko-attr-loading">
                        <div className="tko-attr-loading__banner">
                          <span className="tko-attr-loading__spinner" aria-hidden />
                          <div className="tko-attr-loading__copy">
                            <strong>{fixedUi("正在加载尺码表")}</strong>
                            <span>{fixedUi("正在获取类目尺码表规则…")}</span>
                          </div>
                        </div>
                        <div className="tko-size-chart-loading__skeleton" aria-hidden>
                          <div className="tko-attr-loading__line tko-attr-loading__line--field" />
                          <div className="tko-size-chart-loading__table">
                            {[0, 1, 2, 3].map((row) => (
                              <div key={row} className="tko-size-chart-loading__row">
                                <i className="tko-attr-loading__line" />
                                <i className="tko-attr-loading__line" />
                                <i className="tko-attr-loading__line" />
                                <i className="tko-attr-loading__line" />
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </>
              ) : null}
            />
          </div>

          {!hideGuideFiles ? (
            <div className="tko-wizard__block" onFocusCapture={() => setActiveSection('spec')}>
              <TiktokGuideFilePanel
                shopId={shopId}
                files={form.guideFiles || []}
                onChange={(next) => update('guideFiles', next)}
                errorMessage={fieldErrors.guideFiles}
              />
            </div>
          ) : null}

          {/* —— 包装信息 —— */}
          <div
            id="tko-pub-size"
            className="tko-wizard__block"
            onFocusCapture={() => setActiveSection('size')}
          >
            <h3>{t('tiktokPublish.sectionSize')}</h3>

            {ttsPublishMode ? (
              <div
                className={`tko-product-package-row${fieldErrors.weightGrams || fieldErrors.pkgLength ? ' is-invalid' : ''}`}
                data-field-error={fieldErrors.weightGrams ? 'weightGrams' : 'pkgLength'}
              >
                <label className="tko-product-package-row__item">
                  <span className="tko-field__label tko-field__label-with-help">
                    {t('publishPkg.pkgWeight')} <RequiredMark />
                    <FieldHelpMark text={t('publishPkg.pkgWeightHelp')} />
                  </span>
                  <span className="tko-pkg-inline tko-pkg-inline--weight">
                    <input
                      className="tko-input tko-pkg-inline__input"
                      value={form.weightGrams || ''}
                      inputMode="decimal"
                      onChange={(e) => updateProductPackage({
                        weightGrams: e.target.value.replace(/[^\d.]/g, ''),
                      })}
                      placeholder={t('publishPkg.input')}
                    />
                    <span className="tko-pkg-inline__unit">{TIKTOK_WEIGHT_UNIT}</span>
                  </span>
                </label>
                <label className="tko-product-package-row__item">
                  <span className="tko-field__label">{t('publishPkg.len')} <RequiredMark /></span>
                  <span className="tko-pkg-inline">
                    <input
                      className="tko-input tko-pkg-inline__input"
                      value={form.pkgLength ?? ''}
                      inputMode="numeric"
                      onChange={(e) => updateProductPackage({
                        pkgLength: e.target.value.replace(/[^\d]/g, ''),
                      })}
                      placeholder={t('publishPkg.input')}
                    />
                    <span className="tko-pkg-inline__unit">{TIKTOK_DIM_UNIT}</span>
                  </span>
                </label>
                <label className="tko-product-package-row__item">
                  <span className="tko-field__label">{t('publishPkg.wid')} <RequiredMark /></span>
                  <span className="tko-pkg-inline">
                    <input
                      className="tko-input tko-pkg-inline__input"
                      value={form.pkgWidth ?? ''}
                      inputMode="numeric"
                      onChange={(e) => updateProductPackage({
                        pkgWidth: e.target.value.replace(/[^\d]/g, ''),
                      })}
                      placeholder={t('publishPkg.input')}
                    />
                    <span className="tko-pkg-inline__unit">{TIKTOK_DIM_UNIT}</span>
                  </span>
                </label>
                <label className="tko-product-package-row__item">
                  <span className="tko-field__label">{t('publishPkg.hei')} <RequiredMark /></span>
                  <span className="tko-pkg-inline">
                    <input
                      className="tko-input tko-pkg-inline__input"
                      value={form.pkgHeight ?? ''}
                      inputMode="numeric"
                      onChange={(e) => updateProductPackage({
                        pkgHeight: e.target.value.replace(/[^\d]/g, ''),
                      })}
                      placeholder={t('publishPkg.input')}
                    />
                    <span className="tko-pkg-inline__unit">{TIKTOK_DIM_UNIT}</span>
                  </span>
                </label>
                <FieldErrorText message={fieldErrors.weightGrams || fieldErrors.pkgLength} />
              </div>
            ) : null}

            {!ttsPublishMode ? (
              <>
                {!hideTemuSkuPackageExtras && accessoryLoading ? (
                  <p className="tko-wizard__inline-hint">{t('publishPkg.loadingAcc')}</p>
                ) : null}
                <div className="tko-publish__table-wrap tko-publish__table-wrap--scroll">
                  <table className="tko-publish__table tko-pkg-table">
                    <colgroup>
                      <col className="tko-pkg-col tko-pkg-col--color" />
                      <col className="tko-pkg-col tko-pkg-col--size" />
                      {!hideTemuSkuPackageExtras ? <col className="tko-pkg-col tko-pkg-col--sensitive" /> : null}
                      <col className="tko-pkg-col tko-pkg-col--dims" />
                      <col className="tko-pkg-col tko-pkg-col--weight" />
                      {!hideTemuSkuPackageExtras ? <col className="tko-pkg-col tko-pkg-col--net-weight" /> : null}
                      {!hideTemuSkuPackageExtras ? <col className="tko-pkg-col tko-pkg-col--sku-class" /> : null}
                      {!hideTemuSkuPackageExtras ? <col className="tko-pkg-col tko-pkg-col--accessories" /> : null}
                    </colgroup>
                    <thead>
                      <tr>
                        <th>{colorLabel}</th>
                        <th>{sizeLabel}</th>
                        {!hideTemuSkuPackageExtras ? (
                          <th className="tko-pkg-table__sensitive">
                            <span className="tko-supply-th">
                              <span className="tko-th-with-help">
                                {t('publishPkg.sensitive')} <RequiredMark />
                              </span>
                              <button
                                type="button"
                                className="tko-supply-th__batch"
                                onClick={() => setPkgBatchMode('sensitive')}
                              >
                                {t('publishSpec.batch')}
                              </button>
                            </span>
                          </th>
                        ) : null}
                        <th className="tko-pkg-table__dims">
                          <span className="tko-supply-th">
                            <span className="tko-th-with-help">
                              {t('publishPkg.volume', { unit: TIKTOK_DIM_UNIT })} <RequiredMark />
                              <FieldHelpMark text={t('publishPkg.volumeHelpTt')} />
                            </span>
                            <button
                              type="button"
                              className="tko-supply-th__batch"
                              onClick={() => setPkgBatchMode('dims')}
                            >
                              {t('publishSpec.batch')}
                            </button>
                          </span>
                        </th>
                        <th className="tko-pkg-table__weight">
                          <span className="tko-supply-th">
                            <span>{t('publishPkg.weight')} <RequiredMark /></span>
                            <button
                              type="button"
                              className="tko-supply-th__batch"
                              onClick={() => setPkgBatchMode('weight')}
                            >
                              {t('publishSpec.batch')}
                            </button>
                          </span>
                        </th>
                        {!hideTemuSkuPackageExtras ? (
                          <>
                            <th className="tko-pkg-table__net-weight">
                              <span className="tko-supply-th">
                                <span className="tko-th-with-help">
                                  {t('publishPkg.netWeight')}
                                  <FieldHelpMark text={t('publishPkg.netHelpTt')} />
                                </span>
                                <button
                                  type="button"
                                  className="tko-supply-th__batch"
                                  onClick={() => setPkgBatchMode('weight')}
                                >
                                  {t('publishSpec.batch')}
                                </button>
                              </span>
                            </th>
                            <th className="tko-pkg-table__sku-class">
                              <span className="tko-supply-th">
                                <span className="tko-th-with-help">
                                  {t('publishPkg.skuClass')}
                                  <FieldHelpMark text={t('publishPkg.skuClassHelpTt')} />
                                </span>
                                <button
                                  type="button"
                                  className="tko-supply-th__batch"
                                  onClick={() => setPkgBatchMode('quantity')}
                                >
                                  {t('publishSpec.batch')}
                                </button>
                              </span>
                            </th>
                            <th className="tko-pkg-table__accessories">
                              <span className="tko-supply-th">
                                <span className="tko-th-with-help">
                                  {t('publishPkg.accessories')}
                                  <FieldHelpMark text={t('publishPkg.accHelp')} />
                                </span>
                                <button
                                  type="button"
                                  className="tko-supply-th__batch"
                                  onClick={() => setPkgBatchMode('accessories')}
                                  disabled={accessoryLoading || !accessoryOptions.length}
                                >
                                  {t('publishSpec.batch')}
                                </button>
                              </span>
                              {accessoryLoadError ? (
                                <div className="tko-wizard__inline-hint" role="status">
                                  {accessoryLoading ? t('publishPkg.loadingAcc') : accessoryLoadError}
                                  {!accessoryLoading && accessoryLoadError ? (
                                    <button
                                      type="button"
                                      className="tko-supply-th__batch"
                                      style={{ marginLeft: 8 }}
                                      onClick={() => setAccessoryReloadKey((k) => k + 1)}
                                    >
                                      {t('commerce.retry')}
                                    </button>
                                  ) : null}
                                </div>
                              ) : null}
                            </th>
                          </>
                        ) : null}
                      </tr>
                    </thead>
                    <tbody>
                      {form.skcList.flatMap((skc) => {
                        const rows = skc.skuRows?.length ? skc.skuRows : [];
                        return rows.map((sku, rowIndex) => {
                          const sizeName = sku.sizeIsCustom || sku.sizeExtraValue
                            ? (attributeValueDisplayLabel(sku.sizeLabel || sku.sizeExtraValue) || '—')
                            : (
                              attributeValueDisplayLabel(
                                sizeAttr?.values?.find((v) => String(v.id) === String(sku.sizeValueId))
                                || sku.sizeLabel,
                              ) || '—'
                            );
                          const skuClass = String(sku.skuClassification || '1');
                          const isMulti = skuClass === '2' || skuClass === '3';
                          const sensitiveOn = Number(sku.isSensitive || 0) === 1;
                          const sensitiveSlots = sensitiveSlotsFromList(sku.sensitiveList, 3);
                          const pieceUnit = String(sku.pieceUnitCode || sku.quantityUnit || '1');
                          const pieceCount = sku.numberOfPieces ?? sku.quantity ?? '1';
                          const accessories = Array.isArray(sku.accessories) && sku.accessories.length
                            ? sku.accessories
                            : [createEmptyAccessoryRow()];
                          const filledAccessories = accessories.filter(
                            (row) => Number(row?.vid) > 0 && Number(row?.num) > 0,
                          );
                          const needAccessories = skuClass === '3';
                          return (
                            <tr key={`${skc.id}-${sku.id}`}>
                              {rowIndex === 0 ? (
                                <td rowSpan={rows.length} className="tko-pkg-table__color">
                                  {skc.colorLabel || '—'}
                                </td>
                              ) : null}
                              <td className="tko-pkg-table__size">{sizeName}</td>
                              {!hideTemuSkuPackageExtras ? (
                                <td className="tko-pkg-table__sensitive">
                                  <div className="tko-pkg-sensitive">
                                    <SearchableSelect
                                      value={String(sku.isSensitive ?? 0)}
                                      allowClear={false}
                                      onChange={(v) => updateSku(skc.id, sku.id, {
                                        isSensitive: Number(v) === 1 ? 1 : 0,
                                        ...(Number(v) !== 1 ? { sensitiveList: [] } : {}),
                                      })}
                                      options={localizeOptions(TIKTOK_SENSITIVE_YES_NO_OPTIONS, YES_NO_KEYS)}
                                    />
                                    {sensitiveOn ? (
                                      <div className="tko-pkg-sensitive__types">
                                        {sensitiveSlots.map((slotVal, slotIdx) => (
                                          <SearchableSelect
                                            key={`sens-${sku.id}-${slotIdx}`}
                                            value={slotVal}
                                            allowClear
                                            placeholder={t('shop.pleaseSelect')}
                                            onChange={(v) => {
                                              const nextSlots = [...sensitiveSlots];
                                              nextSlots[slotIdx] = v == null ? '' : String(v);
                                              updateSku(skc.id, sku.id, {
                                                sensitiveList: sensitiveListFromSlots(nextSlots),
                                              });
                                            }}
                                            options={localizeOptions(TIKTOK_SENSITIVE_LIST_OPTIONS, SENSITIVE_KEYS)}
                                          />
                                        ))}
                                      </div>
                                    ) : null}
                                  </div>
                                </td>
                              ) : null}
                              <td className="tko-pkg-table__dims">
                                <div className="tko-pkg-volume">
                                  <label className="tko-pkg-volume__item">
                                    <span>{t('publishPkg.len')}</span>
                                    <input
                                      className="tko-input"
                                      value={sku.length ?? ''}
                                      inputMode="numeric"
                                      onChange={(e) => updateSku(skc.id, sku.id, { length: e.target.value })}
                                      placeholder={t('publishPkg.input')}
                                    />
                                    <em>{TIKTOK_DIM_UNIT}</em>
                                  </label>
                                  <label className="tko-pkg-volume__item">
                                    <span>{t('publishPkg.wid')}</span>
                                    <input
                                      className="tko-input"
                                      value={sku.width ?? ''}
                                      inputMode="numeric"
                                      onChange={(e) => updateSku(skc.id, sku.id, { width: e.target.value })}
                                      placeholder={t('publishPkg.input')}
                                    />
                                    <em>{TIKTOK_DIM_UNIT}</em>
                                  </label>
                                  <label className="tko-pkg-volume__item">
                                    <span>{t('publishPkg.hei')}</span>
                                    <input
                                      className="tko-input"
                                      value={sku.height ?? ''}
                                      inputMode="numeric"
                                      onChange={(e) => updateSku(skc.id, sku.id, { height: e.target.value })}
                                      placeholder={t('publishPkg.input')}
                                    />
                                    <em>{TIKTOK_DIM_UNIT}</em>
                                  </label>
                                </div>
                              </td>
                              <td className="tko-pkg-table__weight">
                                <div className="tko-pkg-inline tko-pkg-inline--weight">
                                  <input
                                    className="tko-input tko-pkg-inline__input"
                                    value={sku.weight ?? ''}
                                    inputMode="decimal"
                                    onChange={(e) => updateSku(skc.id, sku.id, { weight: e.target.value })}
                                    placeholder={t('publishPkg.input')}
                                  />
                                  <span className="tko-pkg-inline__unit">{TIKTOK_WEIGHT_UNIT}</span>
                                </div>
                              </td>
                              {!hideTemuSkuPackageExtras ? (
                                <>
                                  <td className="tko-pkg-table__net-weight">
                                    <div className="tko-pkg-inline tko-pkg-inline--weight">
                                      <input
                                        className="tko-input tko-pkg-inline__input"
                                        value={sku.netWeight ?? ''}
                                        inputMode="decimal"
                                        onChange={(e) => updateSku(skc.id, sku.id, { netWeight: e.target.value })}
                                        placeholder={t('publishPkg.optionalPh')}
                                      />
                                      <span className="tko-pkg-inline__unit">{TIKTOK_WEIGHT_UNIT}</span>
                                    </div>
                                  </td>
                                  <td className="tko-pkg-table__sku-class">
                                    <div className="tko-pkg-sku-class">
                                      <SearchableSelect
                                        value={skuClass}
                                        allowClear={false}
                                        onChange={(v) => updateSku(skc.id, sku.id, {
                                          skuClassification: String(v || '1'),
                                          ...(String(v || '1') === '1'
                                            ? { individuallyPacked: '', numberOfPieces: pieceCount || '1', quantity: pieceCount || '1' }
                                            : {}),
                                        })}
                                        options={localizeOptions(TIKTOK_SKU_CLASSIFICATION_OPTIONS, SKU_CLASS_KEYS)}
                                      />
                                      <div className="tko-pkg-sku-class__row">
                                        <span>{t('publishPkg.pieceQty')}</span>
                                        <input
                                          className="tko-input tko-pkg-inline__input--qty"
                                          value={pieceCount}
                                          inputMode="numeric"
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            updateSku(skc.id, sku.id, {
                                              numberOfPieces: val,
                                              quantity: val,
                                            });
                                          }}
                                          placeholder={t('publishPkg.input')}
                                        />
                                        <SearchableSelect
                                          className="tko-select--unit"
                                          value={pieceUnit}
                                          allowClear={false}
                                          onChange={(v) => updateSku(skc.id, sku.id, {
                                            pieceUnitCode: String(v || '1'),
                                            quantityUnit: String(v || '1'),
                                          })}
                                          options={localizeOptions(TIKTOK_PIECE_UNIT_OPTIONS, PIECE_UNIT_KEYS)}
                                        />
                                      </div>
                                      <div className="tko-pkg-sku-class__row">
                                        <span>{t('publishPkg.totalContains')}</span>
                                        <input
                                          className="tko-input tko-pkg-inline__input--qty"
                                          value={sku.totalContains ?? ''}
                                          inputMode="numeric"
                                          onChange={(e) => updateSku(skc.id, sku.id, {
                                            totalContains: e.target.value,
                                          })}
                                          placeholder={t('publishPkg.input')}
                                        />
                                        <span className="tko-pkg-inline__unit">
                                          {optionLabel(TIKTOK_PIECE_UNIT_OPTIONS, PIECE_UNIT_KEYS, pieceUnit, t('publishPkg.piece'))}
                                        </span>
                                      </div>
                                      {isMulti ? (
                                        <SearchableSelect
                                          value={sku.individuallyPacked === 0 || sku.individuallyPacked === '0'
                                            ? '0'
                                            : (sku.individuallyPacked === 1 || sku.individuallyPacked === '1' ? '1' : '')}
                                          allowClear
                                          placeholder={t('publishPkg.indivPh')}
                                          onChange={(v) => updateSku(skc.id, sku.id, {
                                            individuallyPacked: v == null || v === '' ? '' : String(v),
                                          })}
                                          options={localizeOptions(TIKTOK_INDIVIDUALLY_PACKED_OPTIONS, PACKED_KEYS)}
                                        />
                                      ) : null}
                                    </div>
                                  </td>
                                  <td className="tko-pkg-table__accessories">
                                    <div className="tko-pkg-accessories">
                                      {accessories.map((item, idx) => (
                                        <div key={`${sku.id}-acc-${idx}`} className="tko-pkg-accessories__row">
                                          <SearchableSelect
                                            value={item.vid ? String(item.vid) : ''}
                                            allowClear
                                            placeholder={
                                              accessoryLoading
                                                ? t('publishPkg.accLoading')
                                                : (accessoryOptions.length ? t('publishPkg.accSearch') : t('publishPkg.accNone'))
                                            }
                                            disabled={accessoryLoading || !accessoryOptions.length}
                                            onChange={(v) => {
                                              const opt = accessoryOptions.find((o) => o.value === String(v));
                                              const next = accessories.map((row, i) => (
                                                i === idx
                                                  ? {
                                                    ...row,
                                                    vid: v ? Number(v) : '',
                                                    label: opt?.label || '',
                                                    unitCode: opt?.unitCode || row.unitCode || 1,
                                                    unitName: opt?.unitName || row.unitName || '件',
                                                    thumbUrl: opt?.thumbUrl || row.thumbUrl || '',
                                                  }
                                                  : row
                                              ));
                                              updateSku(skc.id, sku.id, { accessories: next });
                                            }}
                                            options={accessoryOptions}
                                          />
                                          <input
                                            className="tko-input tko-pkg-inline__input--qty"
                                            value={item.num ?? ''}
                                            inputMode="numeric"
                                            placeholder={t('publishPkg.qty')}
                                            onChange={(e) => {
                                              const next = accessories.map((row, i) => (
                                                i === idx ? { ...row, num: e.target.value } : row
                                              ));
                                              updateSku(skc.id, sku.id, { accessories: next });
                                            }}
                                          />
                                          <span className="tko-pkg-inline__unit">
                                            {fixedUi(
                                              item.unitName
                                              || accessoryOptions.find((o) => o.value === String(item.vid))?.unitName
                                              || '件',
                                            )}
                                          </span>
                                          <button
                                            type="button"
                                            className="tko-pkg-accessories__del"
                                            title={t('publishPkg.deleteItem')}
                                            aria-label={t('publishPkg.deleteItem')}
                                            onClick={() => {
                                              const next = accessories.filter((_, i) => i !== idx);
                                              updateSku(skc.id, sku.id, {
                                                accessories: next.length ? next : [createEmptyAccessoryRow()],
                                              });
                                            }}
                                          >
                                            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
                                              <path
                                                fill="currentColor"
                                                d="M9 3h6l1 2h4v2H4V5h4l1-2zm1 6h2v10h-2V9zm4 0h2v10h-2V9zM7 9h2v10H7V9zm-1 13h12a1 1 0 0 0 1-1V7H5v14a1 1 0 0 0 1 1z"
                                              />
                                            </svg>
                                          </button>
                                        </div>
                                      ))}
                                      <button
                                        type="button"
                                        className="tko-pkg-accessories__add"
                                        disabled={accessories.length >= 20 || accessoryLoading}
                                        onClick={() => {
                                          updateSku(skc.id, sku.id, {
                                            accessories: [
                                              ...accessories,
                                              createEmptyAccessoryRow(),
                                            ],
                                          });
                                        }}
                                      >
                                        {t('publishPkg.add')}
                                      </button>
                                      {needAccessories && !filledAccessories.length ? (
                                        <span className="tko-wizard__inline-hint">{t('publishPkg.mixNeed')}</span>
                                      ) : null}
                                    </div>
                                  </td>
                                </>
                              ) : null}
                            </tr>
                          );
                        });
                      })}
                    </tbody>
                  </table>
                </div>

                {!hideOuterPackage ? (
                  <>
                    <h4 className="tko-publish__subhead">{t('publishPkg.productPkg')}</h4>

                    <div className="tko-wizard__form tko-wizard__form--2">
                      <label className={`tko-field${fieldErrors.packageShape ? ' tko-field--invalid' : ''}`} data-field-error="packageShape">
                        <span className="tko-field__label">{t('publishPkg.outerShape')} <RequiredMark /></span>
                        <SearchableSelect
                          value={form.packageShape || ''}
                          allowClear
                          placeholder={t('shop.pleaseSelect')}
                          onChange={(v) => update('packageShape', v == null ? '' : String(v))}
                          options={localizeOptions(TIKTOK_OUTER_PACKAGE_SHAPE_OPTIONS, OUTER_SHAPE_KEYS)}
                        />
                        {fieldErrors.packageShape ? (
                          <span className="tko-field__error" role="alert">{fieldErrors.packageShape}</span>
                        ) : null}
                      </label>
                      <label className={`tko-field${fieldErrors.packageTypeOuter ? ' tko-field--invalid' : ''}`} data-field-error="packageTypeOuter">
                        <span className="tko-field__label">{t('publishPkg.outerType')} <RequiredMark /></span>
                        <SearchableSelect
                          value={form.packageTypeOuter || ''}
                          allowClear
                          placeholder={t('shop.pleaseSelect')}
                          onChange={(v) => update('packageTypeOuter', v == null ? '' : String(v))}
                          options={localizeOptions(TIKTOK_OUTER_PACKAGE_TYPE_OPTIONS, OUTER_TYPE_KEYS)}
                        />
                        {fieldErrors.packageTypeOuter ? (
                          <span className="tko-field__error" role="alert">{fieldErrors.packageTypeOuter}</span>
                        ) : null}
                      </label>
                    </div>

                    <div
                      className={`tko-field tko-outer-pkg-images${fieldErrors.outerPackageImages ? ' tko-field--invalid' : ''}`}
                      data-field-error="outerPackageImages"
                    >
                      <span className="tko-field__label tko-field__label-with-help">
                        {t('publishPkg.outerImages')} <RequiredMark />
                        <FieldHelpMark text={t('publishPkg.outerHelpTt')} />
                      </span>
                      <p className="tko-outer-pkg-images__hint">
                        {t('publishPkg.outerHint')}
                      </p>
                      <PublishImageUploadZone
                        compact
                        labelInside
                        label={t('publishPkg.outerImg')}
                        imageType={1}
                        imageBizType={1}
                        multiple
                        maxCount={6}
                        enforceTiktokRules={false}
                        value={Array.isArray(form.outerPackageImages) ? form.outerPackageImages : []}
                        shopId={shopId}
                        onChange={(next) => {
                          update('outerPackageImages', Array.isArray(next) ? next : (next ? [next] : []));
                        }}
                      />
                      {fieldErrors.outerPackageImages ? (
                        <span className="tko-field__error" role="alert">{fieldErrors.outerPackageImages}</span>
                      ) : null}
                    </div>
                  </>
                ) : null}

                {pkgBatchMode ? (
                  <PackageBatchModal
                    mode={pkgBatchMode}
                    skcList={form.skcList}
                    sizeAttr={sizeAttr}
                    accessoryOptions={accessoryOptions}
                    weightUnit={TIKTOK_WEIGHT_UNIT}
                    dimUnit={TIKTOK_DIM_UNIT}
                    onClose={() => setPkgBatchMode(null)}
                    onConfirm={(result) => {
                      if (!result?.applyAll && !result?.bySize) {
                        setPkgBatchMode(null);
                        return;
                      }
                      setForm((prev) => ({
                        ...prev,
                        weightUnit: TIKTOK_WEIGHT_UNIT,
                        dimUnit: TIKTOK_DIM_UNIT,
                        skcList: result.applyAll
                          ? applyPackageBatchToAllSkus(prev.skcList, result.patch)
                          : applyPackageBatchBySize(prev.skcList, result.bySize),
                      }));
                      showToast(
                        result.applyAll
                          ? t('publishPkg.appliedAll')
                          : t('publishPkg.appliedBySizeTt'),
                        'success',
                      );
                      setPkgBatchMode(null);
                    }}
                  />
                ) : null}
              </>
            ) : null}
          </div>

          {/* —— 销售发货：半托管填站点/仓/运费；全托管与本土跳过 —— */}
          <div id="tko-pub-sales" className="tko-wizard__block" onFocusCapture={() => setActiveSection('sales')}>
            <h3>{t('tiktokPublish.salesTitle')}</h3>

            {skipSiteWarehouseFreight ? (
              <p className="tko-wizard__inline-hint">
                {t('tiktokPublish.salesSkip')}
              </p>
            ) : (
              <div className="tko-wizard__form">
                {!hideListingSites && (isSemiManaged || shopCaps.usesPublishSiteList) ? (
                  <div className={`tko-field tko-field--full${fieldErrors.selectedSites ? ' tko-field--invalid' : ''}`} data-field-error="selectedSites">
                    <span className="tko-field__label">{t('tiktokPublish.listingSites')}{isEdit ? null : <RequiredMark />}</span>
                    {isEdit ? (
                      <>
                        <div className="tko-readonly-sites" aria-label={t('tiktokPublish.listedSites')}>
                          {(form.selectedSites || []).length ? (
                            (form.selectedSites || []).map((abbr) => {
                              const site = isSemiManaged
                                ? siteSelectOptions.find((s) => String(s.value) === String(abbr))
                                : siteOptions.find((s) => String(s.siteAbbr) === String(abbr));
                              return (
                                <span key={abbr} className="tko-tag tko-tag--ok">
                                  {site?.label || site?.siteName || abbr}
                                </span>
                              );
                            })
                          ) : (
                            <span className="tko-wizard__inline-hint">{t('tiktokPublish.noSiteInfo')}</span>
                          )}
                        </div>
                        <p className="tko-wizard__inline-hint">{t('tiktokPublish.sitesLocked')}</p>
                      </>
                    ) : (
                      <SearchableSelect
                        value={(form.selectedSites || [])[0] || ''}
                        onChange={onSitesChange}
                        placeholder={
                          isSemiManaged
                            ? t('tiktokPublish.pickTiktok')
                            : (metaLoading ? t('tiktokPublish.loading') : t('tiktokPublish.pickOrSearch'))
                        }
                        disabled={!isSemiManaged && (metaLoading || siteSelectOptions.length === 0)}
                        emptyText={isSemiManaged ? t('tiktokPublish.noSites') : (metaLoading ? t('tiktokPublish.loading') : t('tiktokPublish.noSitesYet'))}
                        options={siteSelectOptions}
                      />
                    )}
                    <FieldErrorText message={fieldErrors.selectedSites} />
                  </div>
                ) : (!hideListingSites ? (
                  <div className="tko-field tko-field--full">
                    <span className="tko-field__label">{t('tiktokPublish.listingSites')}</span>
                    <p className="tko-wizard__inline-hint">{t('tiktokPublish.sitesNotNeeded')}</p>
                  </div>
                ) : null)}

                <div className={`tko-field tko-field--full${fieldErrors.warehouseId ? ' tko-field--invalid' : ''}`} data-field-error="warehouseId">
                  <span className="tko-field__label">
                    {t('tiktokPublish.warehouse')}{multiWarehouse || isSemiManaged ? <RequiredMark /> : null}
                  </span>
                  {metaLoading ? (
                    <p className="tko-wizard__inline-hint">{t('tiktokPublish.loadingWarehouses')}</p>
                  ) : warehouseOptions.length === 0 ? (
                    <p className="tko-wizard__inline-hint">{t('tiktokPublish.noWarehouses')}</p>
                  ) : (
                    <SearchableSelect
                      value={form.warehouseId}
                      onChange={onWarehouseChange}
                      allowClear={false}
                      placeholder={t('tiktokPublish.pickWarehouse')}
                      emptyText={t('tiktokPublish.noWarehouseOption')}
                      options={warehouseOptions}
                    />
                  )}
                  <FieldErrorText message={fieldErrors.warehouseId} />
                </div>

                <div className={`tko-field tko-field--full${fieldErrors.freightTemplateId ? ' tko-field--invalid' : ''}`} data-field-error="freightTemplateId">
                  <span className="tko-field__label">
                    {ttsPublishMode ? t('tiktokPublish.freightLogistics') : t('tiktokPublish.freight')}
                    <span className="tko-field__optional">{t('tiktokPublish.optional')}</span>
                  </span>
                  {freightLoading ? (
                    <p className="tko-wizard__inline-hint">{t('tiktokPublish.loadingFreight')}</p>
                  ) : freightOptions.length === 0 ? (
                    <p className="tko-wizard__inline-hint">
                      {t('tiktokPublish.noFreight')}
                      <button
                        type="button"
                        className="tko-btn tko-btn--sm"
                        onClick={() => openTiktokPathInNewTab('templates/freight')}
                      >
                        {t('tiktokPublish.goSyncFreight')}
                      </button>
                    </p>
                  ) : (
                    <SearchableSelect
                      value={form.freightTemplateId}
                      onChange={(v) => update('freightTemplateId', v)}
                      allowClear
                      placeholder={t('tiktokPublish.pickFreight')}
                      options={freightOptions}
                      placement="top"
                    />
                  )}
                  <FieldErrorText message={fieldErrors.freightTemplateId} />
                </div>

                {deliveryOptionsSupported ? (
                  <div className="tko-field tko-field--full">
                    <span className="tko-field__label">{t('tiktokPublish.delivery')}</span>
                    {deliveryOptionsLoading ? (
                      <p className="tko-wizard__inline-hint">{t('tiktokPublish.loadingDelivery')}</p>
                    ) : deliveryOptionOptions.length === 0 ? (
                      <p className="tko-wizard__inline-hint">{t('tiktokPublish.noDelivery')}</p>
                    ) : (
                      <div className="tko-delivery-options">
                        {deliveryOptionOptions.map((opt) => {
                          const selectedIds = Array.isArray(form.deliveryOptionIds) ? form.deliveryOptionIds : [];
                          const checked = selectedIds.includes(opt.value);
                          return (
                            <label key={opt.value} className="tko-delivery-options__item">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  const next = e.target.checked
                                    ? [...selectedIds, opt.value]
                                    : selectedIds.filter((id) => id !== opt.value);
                                  update('deliveryOptionIds', next);
                                }}
                              />
                              <span>{opt.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                    <p className="tko-wizard__inline-hint">{t('tiktokPublish.deliveryHint')}</p>
                  </div>
                ) : null}

                {!hideShipmentLimit ? (
                  <div className="tko-field tko-field--full">
                    <span className="tko-field__label">
                      {t('tiktokPublish.shipLimit')}{isSemiManaged ? <RequiredMark /> : null}
                    </span>
                    <SearchableSelect
                      value={String(form.shipmentLimitSecond || '172800')}
                      allowClear={false}
                      onChange={(v) => update('shipmentLimitSecond', v)}
                      options={SHIPMENT_LIMIT_OPTIONS.map((row) => ({
                        value: row.value,
                        label: t(`tiktokPublish.${row.labelKey}`),
                      }))}
                      placement="top"
                    />

                  </div>
                ) : null}
              </div>
            )}
          </div>
          {/* 上架方式 / 预约上架 / 限购 / 预售：与仓·运费模板无关，故不放进 skipSiteWarehouseFreight 门控内 */}
          {!isEdit ? (
            <div className="tko-wizard__form" onFocusCapture={() => setActiveSection('sales')}>
              <label className="tko-field" data-field-error="shelfWay">
                <span className="tko-field__label tko-field__label-with-help">
                  {t('tiktokPublish.shelfWay')}
                  <FieldHelpMark text={t('tiktokPublish.shelfWayHelp')} />
                </span>
                <SearchableSelect
                  value={String(form.shelfWay || '1') === '2' ? '2' : '1'}
                  allowClear={false}
                  onChange={updateShelfWay}
                  options={[
                    { value: '1', label: t('tiktokPublish.shelfNow') },
                    { value: '2', label: t('tiktokPublish.shelfSchedule') },
                  ]}
                />
              </label>

              {String(form.shelfWay || '1') === '2' ? (
                <div className={`tko-field${fieldErrors.hopeOnSaleDate ? ' tko-field--invalid' : ''}`} data-field-error="hopeOnSaleDate">
                  <span className="tko-field__label tko-field__label-with-help">
                    {t('tiktokPublish.scheduleAt')}
                    <FieldHelpMark text={t('tiktokPublish.scheduleHelp')} />
                  </span>
                  <TiktokDateTimePicker
                    value={form.hopeOnSaleDate || ''}
                    onChange={updateScheduledSaleAt}
                    placeholder={t('tiktokPublish.pickSchedule')}
                    ariaLabel={t('tiktokPublish.scheduleAria')}
                    disablePast
                  />
                  <FieldErrorText message={fieldErrors.hopeOnSaleDate} />
                </div>
              ) : null}

              <label className={`tko-field${fieldErrors.maxOrderQuantity ? ' tko-field--invalid' : ''}`} data-field-error="maxOrderQuantity">
                <span className="tko-field__label">{t('tiktokPublish.maxOrder')}</span>
                <input
                  className="tko-input"
                  inputMode="numeric"
                  value={form.maxOrderQuantity || ''}
                  onChange={(e) => update('maxOrderQuantity', e.target.value.replace(/[^\d]/g, ''))}
                  placeholder={t('tiktokPublish.unlimited')}
                />
                <FieldErrorText message={fieldErrors.maxOrderQuantity} />
              </label>

              <label className={`tko-field${fieldErrors.cumulativeOrderQuantity ? ' tko-field--invalid' : ''}`} data-field-error="cumulativeOrderQuantity">
                <span className="tko-field__label">{t('tiktokPublish.cumulative')}</span>
                <input
                  className="tko-input"
                  inputMode="numeric"
                  value={form.cumulativeOrderQuantity || ''}
                  onChange={(e) => update('cumulativeOrderQuantity', e.target.value.replace(/[^\d]/g, ''))}
                  placeholder={t('tiktokPublish.unlimited')}
                />
                <FieldErrorText message={fieldErrors.cumulativeOrderQuantity} />
              </label>

              <p className="tko-wizard__inline-hint">
                {t('tiktokPublish.limitHint')}
              </p>

              {preSaleTypeOptions.length ? (
                <div className={`tko-field tko-field--full${fieldErrors.preSaleType ? ' tko-field--invalid' : ''}`} data-field-error="preSaleType">
                  <span className="tko-field__label tko-field__label-with-help">
                    {t('tiktokPublish.preSale')}
                    <FieldHelpMark text={t('tiktokPublish.preSaleHelp')} />
                  </span>
                  <SearchableSelect
                    value={form.preSaleType || ''}
                    allowClear
                    onChange={(v) => update('preSaleType', v || '')}
                    options={preSaleTypeOptions.map((type) => ({
                      value: type,
                      label: TIKTOK_PRESALE_TYPE_KEYS[type]
                        ? t(`tiktokPublish.${TIKTOK_PRESALE_TYPE_KEYS[type]}`)
                        : type,
                    }))}
                    placeholder={t('tiktokPublish.preSaleNone')}
                    placement="top"
                  />

                  {preSaleRule?.fulfillment === 'release_date' ? (
                    <label className={`tko-field tko-field--full${fieldErrors.preSaleReleaseDate ? ' tko-field--invalid' : ''}`} data-field-error="preSaleReleaseDate">
                      <span className="tko-field__label">{t('tiktokPublish.preSaleAt')}</span>
                      <input
                        type="datetime-local"
                        className="tko-input"
                        value={form.preSaleReleaseDate || ''}
                        onChange={(e) => update('preSaleReleaseDate', e.target.value)}
                      />
                      <FieldErrorText message={fieldErrors.preSaleReleaseDate} />
                    </label>
                  ) : null}

                  {preSaleRule?.fulfillment === 'handling_duration_days' ? (
                    <label className={`tko-field tko-field--full${fieldErrors.preSaleHandlingDays ? ' tko-field--invalid' : ''}`} data-field-error="preSaleHandlingDays">
                      <span className="tko-field__label">{t('tiktokPublish.preSaleDays')}</span>
                      <input
                        className="tko-input"
                        inputMode="numeric"
                        value={form.preSaleHandlingDays || ''}
                        onChange={(e) => update('preSaleHandlingDays', e.target.value.replace(/[^\d]/g, ''))}
                        placeholder={`${preSaleRule.minDays}–${preSaleRule.maxDays}`}
                      />
                      <FieldErrorText message={fieldErrors.preSaleHandlingDays} />
                    </label>
                  ) : null}

                  {preSaleRule ? (
                    <p className="tko-wizard__inline-hint">
                      {preSaleRule.fulfillment === 'release_date'
                        ? t('tiktokPublish.preSaleReleaseHint', { type: form.preSaleType, min: preSaleRule.minDays, max: preSaleRule.maxDays })
                        : t(
                          preSaleRule.unit === 'BUSINESS_DAY' ? 'tiktokPublish.preSaleHandlingBiz' : 'tiktokPublish.preSaleHandlingCal',
                          { type: form.preSaleType, min: preSaleRule.minDays, max: preSaleRule.maxDays },
                        )}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="tko-wizard__footer">
          <button
            type="button"
            className="tko-btn tko-btn--ghost"
            onClick={onSaveDraft}
            disabled={submitting || savingDraft || draftLoading || isEdit}
          >
            {savingDraft ? t('tiktokPublish.saving') : t('tiktokPublish.saveDraft')}
          </button>
          <div className="tko-wizard__footer-right">
            <button
              type="button"
              className="tko-btn tko-btn--secondary"
              onClick={openProductTplSaveModal}
              disabled={submitting || loadingSpu || draftLoading || !draftHydrated || !shopId}
            >
              {t('tiktokPublish.saveTemplate')}
            </button>
            <button
              type="button"
              className="tko-btn tko-btn--primary"
              onClick={onSubmit}
              disabled={submitting || loadingSpu || draftLoading || !draftHydrated}
            >
              {submitting ? t('tiktokPublish.submitting') : (isEdit ? t('tiktokPublish.submitEdit') : t('tiktokPublish.publishProduct'))}
            </button>
          </div>
        </div>
        <PublishScrollJumpFab prefix="tko" />
      </section>

      <SaveAttributeTemplateMetaModal
        open={attrSaveModalOpen}
        shopId={shopId}
        categoryId={form.categoryId}
        productTypeId={form.productTypeId}
        categoryPathLabel={form.categoryPathLabel}
        productAttributes={form.productAttributes}
        attrDefs={allProductAttrs}
        runWithoutUnloadPrompt={runWithoutUnloadPrompt}
        onClose={() => setAttrSaveModalOpen(false)}
        onSaved={refreshAttrTemplates}
      />
      <SaveProductTemplateMetaModal
        open={productTplSaveModalOpen}
        shopId={shopId}
        categoryId={form.categoryId}
        productTypeId={form.productTypeId}
        categoryPathLabel={form.categoryPathLabel}
        getFormSnapshot={() => persistTiktokFormImageSlots(
          buildPublishFormSnapshot(form),
        )}
        runWithoutUnloadPrompt={runWithoutUnloadPrompt}
        onClose={() => setProductTplSaveModalOpen(false)}
      />
      <PriceUpdateConfirmModal
        open={Boolean(priceConfirm)}
        mode={priceConfirm?.changes?.mode || priceMode || 'cost'}
        shopId={shopId}
        changes={priceConfirm?.changes}
        onCancel={() => {
          if (!submitting) setPriceConfirm(null);
        }}
        onConfirm={async (meta) => {
          const result = await finishEditSubmit(
            priceConfirm.submitForm,
            priceConfirm.changes,
            meta,
          );
          if (!result?.ok) {
            throw new Error(result?.message || t('publishFlow.priceSubmitFail'));
          }
        }}
      />
    </div>
  );
}
